/**
 * Widget Preset Expansion Utility (Story 12.2)
 *
 * Pre-configured combinations of existing widget types with integration-specific
 * metric/dimension bindings. Presets expand into standard WidgetConfig[] at add-time.
 *
 * Key design: preset functions return widgets WITHOUT positions. Position assignment
 * happens inside the onWidgetsChange(prev => ...) callback where canvas state is available.
 */

import type { PresetType } from "../components/reports/WidgetPalette";
import type { WidgetConfig } from "./templateUtils";
import {
  MOZ_DEMO_SNAPSHOT_SCOPE,
  MOZ_DEMO_SNAPSHOT_TARGET,
  // Story 20.9 — cross-SEO snapshot demo constants (single source of truth
  // shared between widget renderer filter and demo-seed INSERT per
  // Story 20.8 Pass 2 BH2-8 cross-app fix).
  AHREFS_DEMO_SNAPSHOT_TARGET,
  SEMRUSH_DEMO_SNAPSHOT_TARGET,
  SEO_CROSS_TOOL_CALIBRATION_FOOTNOTE,
  SEO_TOOL_DISPLAY_NAMES,
  SEO_TOOL_BRAND_COLORS,
  // Story 23.7 — Reviews Cross-Platform calibration constants. Mirrors the
  // SEO Cross-Tool imports above. All 3 review platforms share a 0-5
  // decimal rating scale so the footnote is lightweight (no normalization
  // disclaimer).
  REVIEWS_CROSS_PLATFORM_CALIBRATION_FOOTNOTE,
  REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES,
  REVIEWS_CROSS_PLATFORM_BRAND_COLORS,
  REVIEWS_CROSS_PLATFORM_SENTIMENT_COLORS,
  // Story 23.8 — Reviews Cross-Platform Response-Tracking constants.
  // Response-time band thresholds + Tailwind v4 semantic-color palette
  // (emerald/blue/amber/red) for the Avg Response Time Bar widget. NOT
  // brand colors — the bar widget uses severity-encoded coloring rather
  // than platform-encoded coloring.
  REVIEWS_CROSS_PLATFORM_RESPONSE_TIME_THRESHOLDS_HOURS,
  REVIEWS_CROSS_PLATFORM_RESPONSE_TIME_BAND_COLORS,
  // Story 23.8 Pass 1 Code Review M4 fix — empty-state color token
  REVIEWS_CROSS_PLATFORM_RESPONSE_TIME_NO_DATA_COLOR,
} from "@provenleap/integrations";

/** Partial widget config without position (assigned later by CanvasEditor) */
export type PresetWidget = Omit<WidgetConfig, "position"> & {
  /** Default colSpan for grid placement */
  colSpan: number;
  /** Default rowSpan for grid placement */
  rowSpan: number;
};

/**
 * Expand the Email Overview KPI preset into 5 KPI widgets.
 *
 * Uses actual Mailchimp adapter metric keys:
 * - total_subscribers, emails_sent, average_open_rate, average_click_rate, unsubscribe_rate
 */
export function expandEmailOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string; showPrivacyDisclaimer?: boolean }[] = [
    { metricKey: "total_subscribers", title: "Total Subscribers" },
    { metricKey: "emails_sent", title: "Emails Sent" },
    { metricKey: "average_open_rate", title: "Open Rate", showPrivacyDisclaimer: true },
    { metricKey: "average_click_rate", title: "Click Rate" },
    { metricKey: "unsubscribe_rate", title: "Unsubscribe Rate" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
      ...(kpi.showPrivacyDisclaimer ? { showPrivacyDisclaimer: true } : {}),
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

// ── Combined Budget/Goal dashboard (Story 18.4 AC #9) ─────────────────

/** Minimal saved-budget shape the dashboard preset needs (from useClientBudgets). */
export interface DashboardBudgetInput {
  id: string;
  integrationType: string;
}

/** Minimal saved-goal shape the dashboard preset needs (from useClientGoals). */
export interface DashboardGoalInput {
  id: string;
  name: string;
}

/** Max bound widgets per group on the one-click dashboard (AC #9 — capped). */
export const DASHBOARD_PRESET_GROUP_CAP = 4;

/**
 * Expand the one-click "Budgets & Goals" dashboard (Story 18.4 AC #9):
 *   1. a `heading` widget titled "Budgets & Goals"
 *   2. one BOUND goal widget per saved budget (capped at 4) — `savedBudgetId`
 *   3. one BOUND goal widget per saved goal (capped at 4) — `savedGoalId`
 *
 * Position-less PresetWidget[] (the house pattern — see expandEmailOverviewPreset):
 * the route's `planPresetWidgetsAdd` assigns positions in ONE atomic pass. The
 * binding key is the ONLY data key written on each goal widget; everything else
 * (target / metric / period / thresholds) hydrates server-side at populate time.
 * Each widget seeds a `title` from the saved record so the canvas placeholder is
 * never blank before the first populate round-trip lands.
 *
 * Pure + deterministic: no Date.now(), no fetch. Returns `[]` when both inputs
 * are empty (the panel gates the button on ≥1 budget AND ≥1 goal anyway).
 */
export function expandBudgetGoalDashboardPreset(
  budgets: DashboardBudgetInput[],
  goals: DashboardGoalInput[],
): PresetWidget[] {
  const widgets: PresetWidget[] = [];

  widgets.push({
    id: crypto.randomUUID(),
    type: "heading",
    title: "Budgets & Goals",
    config: { content: "Budgets & Goals", size: "h2", align: "left" },
    colSpan: 12,
    rowSpan: 1,
  });

  for (const budget of budgets.slice(0, DASHBOARD_PRESET_GROUP_CAP)) {
    const scope =
      budget.integrationType === "all"
        ? "All platforms"
        : budget.integrationType;
    widgets.push({
      id: crypto.randomUUID(),
      type: "goal",
      title: `${scope} budget`,
      // goalType seeds the placeholder + keeps the (now-optional-when-bound)
      // schema's union happy; the real target/metric hydrate server-side.
      config: { savedBudgetId: budget.id, goalType: "budget" },
      colSpan: 6,
      rowSpan: 3,
    });
  }

  for (const goal of goals.slice(0, DASHBOARD_PRESET_GROUP_CAP)) {
    widgets.push({
      id: crypto.randomUUID(),
      type: "goal",
      title: goal.name,
      config: { savedGoalId: goal.id },
      colSpan: 6,
      rowSpan: 3,
    });
  }

  return widgets;
}

/** Input for the Cross-Platform Summary preset (Story 16.5) — one cross-platform total. */
export interface CrossPlatformSummaryInput {
  label: string;
  outputFormat: "number" | "currency" | "percentage";
  members: Array<{ label: string; metricKey: string; platform: string }>;
}

/**
 * Expand the one-click "Cross-Platform Summary" (Story 16.5): a `heading` +
 * one `custom-metric` KPI per cross-platform TOTAL. Each KPI is an ORDINARY
 * custom-metric widget whose variables each carry their own `platform` (the
 * Story 16.4 union shape), summed by a `m0 + m1 + …` formula — the 16.2 blender
 * renders it. NO new widget type (the type-vs-preset decision). Position-less
 * PresetWidget[] (house pattern); the route assigns positions atomically.
 * Pure (no fetch); returns just the heading when `totals` is empty.
 */
export function expandCrossPlatformSummaryPreset(
  totals: CrossPlatformSummaryInput[],
): PresetWidget[] {
  const widgets: PresetWidget[] = [
    {
      id: crypto.randomUUID(),
      type: "heading",
      title: "Cross-Platform Summary",
      config: { content: "Cross-Platform Summary", size: "h2", align: "left" },
      colSpan: 12,
      rowSpan: 1,
    },
  ];
  for (const total of totals) {
    widgets.push({
      id: crypto.randomUUID(),
      type: "custom-metric",
      title: total.label,
      config: {
        formulaExpression: total.members.map((_, i) => `m${i}`).join(" + "),
        variables: total.members.map((m, i) => ({
          id: `m${i}`,
          label: m.label,
          metricKey: m.metricKey,
          platform: m.platform,
        })),
        outputFormat: total.outputFormat,
      },
      colSpan: 3,
      rowSpan: 2,
    });
  }
  return widgets;
}

/**
 * Expand the Campaign Performance Table preset into 1 data-table widget.
 *
 * Uses actual Mailchimp adapter metric/dimension keys.
 * Note: "recipients" is not an adapter metric — using emails_sent instead.
 */
export function expandCampaignTablePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Campaign Performance",
      config: {
        metricKeys: ["emails_sent", "average_open_rate", "average_click_rate", "bounce_rate"],
        dimensionKey: "campaign_name",
        sortBy: "date",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

/**
 * Expand the Subscriber Growth Chart preset into 1 line-chart widget.
 */
export function expandSubscriberGrowthPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Subscriber Growth",
      config: {
        metrics: [{ key: "total_subscribers", label: "Total Subscribers" }],
        xAxis: "date",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Top Campaigns preset into 1 data-table widget.
 *
 * Returns a data-table filtered to campaign records, sorted by open rate descending,
 * limited to top 5 by default. Excludes automation/rss campaigns.
 */
export function expandTopCampaignsPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Top Campaigns",
      config: {
        metricKeys: ["average_open_rate", "average_click_rate", "recipients_count", "click_to_open_rate"],
        dimensionKey: "campaign_name",
        sortBy: "average_open_rate",
        sortDirection: "desc",
        limit: 5,
        campaignTypeFilter: ["regular", "variate"],
        showPrivacyDisclaimer: true,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

/**
 * Expand the E-commerce Sales Overview KPI preset into 5 KPI widgets.
 *
 * Uses actual Shopify adapter metric keys:
 * - total_sales, orders, average_order_value, conversion_rate, customers
 */
export function expandEcommerceSalesOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string; showConversionRateDisclaimer?: boolean }[] = [
    { metricKey: "total_sales", title: "Total Revenue (Gross)" },
    { metricKey: "orders", title: "Orders" },
    { metricKey: "average_order_value", title: "Avg Order Value" },
    { metricKey: "conversion_rate", title: "Store Conversion Rate", showConversionRateDisclaimer: true },
    { metricKey: "customers", title: "Customers" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
      ...(kpi.showConversionRateDisclaimer ? { showConversionRateDisclaimer: true } : {}),
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Expand the E-commerce Revenue Trend preset into 1 line-chart widget.
 */
export function expandEcommerceRevenueTrendPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Revenue Trend",
      config: {
        metrics: [{ key: "total_sales", label: "Total Revenue" }],
        xAxis: "date",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the E-commerce Top Products preset into 1 data-table widget.
 */
export function expandEcommerceTopProductsPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Top Products",
      config: {
        metricKeys: ["product_revenue", "product_units_sold", "percent_of_total"],
        dimensionKey: "product_title",
        sortBy: "product_revenue",
        sortDirection: "desc",
        limit: 10,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

/**
 * Expand the E-commerce Orders & Conversion preset into 1 bar-chart widget.
 */
export function expandEcommerceOrdersConversionPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Orders & Conversion",
      config: {
        metrics: [{ key: "orders", label: "Orders" }],
        xAxis: "date",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the E-commerce Product Performance preset into 1 data-table widget.
 */
export function expandProductPerformancePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Product Performance",
      config: {
        metricKeys: ["product_sku", "product_revenue", "product_units_sold", "average_selling_price", "percent_of_total"],
        dimensionKey: "product_title",
        sortBy: "product_revenue",
        sortDirection: "desc",
        limit: 10,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
      autoSize: true,
    },
  ];
}

// ============================================================================
// Shopify Expanded Presets (IMC-16)
// ============================================================================

/**
 * Expand the Shopify Profit Preset into 3 KPI widgets.
 * Metrics: gross_profit, gross_margin, cost_of_goods_sold
 */
export function expandShopifyProfitPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "gross_profit", title: "Gross Profit" },
    { metricKey: "gross_margin", title: "Gross Margin" },
    { metricKey: "cost_of_goods_sold", title: "Cost of Goods Sold" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 4,
    rowSpan: 1,
  }));
}

/**
 * Expand the Shopify Customer Preset.
 * 4 KPIs: new_customers, returning_customers, customer_ltv, customer_order_count
 */
export function expandShopifyCustomerPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "new_customers", title: "New Customers" },
    { metricKey: "returning_customers", title: "Returning Customers" },
    { metricKey: "customer_ltv", title: "Customer Lifetime Value" },
    { metricKey: "customer_order_count", title: "Avg Orders per Customer" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

/**
 * Expand the Shopify Cart Abandonment Preset.
 * 3 KPIs: cart_abandonment_rate, abandoned_checkout_count, abandoned_checkout_value
 */
export function expandShopifyCartAbandonmentPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "cart_abandonment_rate", title: "Cart Abandonment Rate" },
    { metricKey: "abandoned_checkout_count", title: "Abandoned Checkouts" },
    { metricKey: "abandoned_checkout_value", title: "Abandoned Value" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 4,
    rowSpan: 1,
  }));
}

/**
 * Expand the Shopify Geo Preset.
 * Bar chart: total_sales by billing_country + data-table: country × orders, revenue
 */
export function expandShopifyGeoPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Sales by Country",
      config: {
        metrics: ["total_sales"],
        groupBy: "billing_country",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Revenue by Country",
      config: {
        metricKeys: ["orders", "total_sales", "average_order_value"],
        dimensionKey: "billing_country",
        sortBy: "total_sales",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

// ============================================================================
// Instagram Presets (IMC-6)
// ============================================================================

/**
 * Expand the Instagram Overview KPI preset into 5 KPI widgets.
 *
 * Uses actual Instagram Business adapter metric keys:
 * - followers_count, reach, views, engagement_rate, profile_links_taps
 */
export function expandInstagramOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "followers_count", title: "Followers" },
    { metricKey: "reach", title: "Reach" },
    { metricKey: "views", title: "Views" },
    { metricKey: "engagement_rate", title: "Engagement Rate" },
    { metricKey: "profile_links_taps", title: "Profile Link Taps" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Expand the Instagram Reels Performance preset into 1 data-table widget.
 *
 * Displays per-reel metrics: views, likes, comments, shares, saves, avg_watch_time, skip_rate.
 */
export function expandInstagramReelsPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Reels Performance",
      config: {
        metricKeys: ["views", "likes", "comments", "shares", "saves", "reel_avg_watch_time", "reel_skip_rate"],
        dimensionKey: "media_type",
        mediaTypeFilter: ["REEL"],
        sortBy: "views",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

/**
 * Expand the Instagram Demographics preset into 1 demographic widget.
 *
 * Displays follower age, gender, and country breakdown.
 */
export function expandInstagramDemographicsPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "demographic",
      title: "Follower Demographics",
      config: {
        metricKeys: ["follower_demographics_age", "follower_demographics_gender", "follower_demographics_country"],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 2,
    },
  ];
}

// ============================================================================
// TikTok Ads Presets (IMC-8)
// ============================================================================

/**
 * Expand the TikTok Ads Overview KPI preset into 5 KPI widgets.
 *
 * Uses TikTok Marketing API metric keys:
 * - spend, impressions, clicks, ctr, conversions
 */
export function expandTikTokAdsOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "spend", title: "Ad Spend" },
    { metricKey: "impressions", title: "Impressions" },
    { metricKey: "clicks", title: "Clicks" },
    { metricKey: "ctr", title: "CTR" },
    { metricKey: "conversions", title: "Conversions" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Expand the TikTok Ads Video Funnel preset into 1 funnel widget.
 *
 * Displays: video_play_actions → video_watched_6s → video_views_p50 → video_views_p100
 */
export function expandTikTokAdsVideoFunnelPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "funnel",
      title: "Video View Funnel",
      config: {
        metrics: [
          { key: "video_play_actions", label: "Plays" },
          { key: "video_watched_6s", label: "6s Focused Views" },
          { key: "video_views_p50", label: "50% Watched" },
          { key: "video_views_p100", label: "100% Watched" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the TikTok Ads Campaign Table preset into 1 data-table widget.
 *
 * Per-campaign breakdown: campaign_name × spend, clicks, conversions, cost_per_conversion, purchase_roas
 */
export function expandTikTokAdsCampaignTablePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Campaign Performance",
      config: {
        metricKeys: ["spend", "clicks", "conversions", "cost_per_conversion", "purchase_roas"],
        dimensionKey: "campaign_name",
        sortBy: "spend",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

// ============================================================================
// TikTok Organic Presets (Story IMC-17)
// ============================================================================

/**
 * Expand the TikTok Organic Overview preset into 5 KPI widgets.
 *
 * Uses organic metric keys:
 * - follower_count, view_count, total_engagement, engagement_rate, avg_views_per_video
 */
export function expandTikTokOrganicOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "follower_count", title: "Followers" },
    { metricKey: "view_count", title: "Video Views" },
    { metricKey: "total_engagement", title: "Total Engagement" },
    { metricKey: "engagement_rate", title: "Engagement Rate" },
    { metricKey: "avg_views_per_video", title: "Avg Views Per Video" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Expand the TikTok Video Performance Table preset into 1 data-table widget.
 *
 * Per-video breakdown: video_title × views, likes, comments, shares, saves, avg_watch_time
 */
export function expandTikTokVideoTablePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Video Performance",
      config: {
        metricKeys: ["view_count", "like_count", "comment_count", "share_count", "save_count", "average_time_watched"],
        dimensionKey: "video_title",
        sortBy: "view_count",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

/** Preset label map for user-facing toast messages */
// ============================================================================
// Facebook Pages Presets (Story IMC-7)
// ============================================================================

/**
 * Expand the Facebook Pages Overview preset into 5 KPI widgets.
 * Uses media_view metrics (v25.0+) — NOT deprecated page_posts_impressions.
 */
export function expandFacebookPagesOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "page_follows", title: "Followers" },
    { metricKey: "page_media_view", title: "Media Views" },
    { metricKey: "page_total_media_view_unique", title: "Unique Viewers" },
    { metricKey: "page_post_engagements", title: "Engagements" },
    { metricKey: "engagement_rate", title: "Engagement Rate" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Expand the Facebook Pages Video preset into 2 widgets:
 * - Line chart: page_video_views over time
 * - Bar chart: paid vs organic split
 */
export function expandFacebookPagesVideoPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Video Views Trend",
      config: {
        metricKeys: ["page_video_views"],
        dimensionKey: "date",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Video Views: Paid vs Organic",
      config: {
        metricKeys: ["page_video_views_paid", "page_video_views_organic"],
        dimensionKey: "date",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Facebook Pages Reactions preset into a bar chart
 * showing the breakdown of reaction types.
 */
export function expandFacebookPagesReactionsPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Reactions Breakdown",
      config: {
        metricKeys: [
          "page_actions_post_reactions_like_total",
          "page_actions_post_reactions_love_total",
          "page_actions_post_reactions_wow_total",
          "page_actions_post_reactions_haha_total",
          "page_actions_post_reactions_sorry_total",
          "page_actions_post_reactions_anger_total",
        ],
        dimensionKey: "date",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

// ============================================================================
// Google Ads Presets (IMC-9)
// ============================================================================

/** 5 KPIs: cost, clicks, conversions, ROAS, CTR */
export function expandGoogleAdsOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis = [
    { metricKey: "cost", title: "Ad Spend" },
    { metricKey: "clicks", title: "Clicks" },
    { metricKey: "conversions", title: "Conversions" },
    { metricKey: "roas", title: "ROAS" },
    { metricKey: "ctr", title: "CTR" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: { metricKey: kpi.metricKey, comparisonEnabled: true, integrationPlatform: platform, integrationId },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/** KPI: search IS + bar chart: lost IS budget vs rank */
export function expandGoogleAdsImpressionSharePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "kpi",
      title: "Search Impression Share",
      config: { metricKey: "search_impression_share", comparisonEnabled: true, integrationPlatform: platform, integrationId },
      colSpan: 6,
      rowSpan: 1,
    },
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Lost Impression Share",
      config: {
        metrics: [
          { key: "search_budget_lost_is", label: "Lost IS (Budget)" },
          { key: "search_rank_lost_is", label: "Lost IS (Rank)" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/** Keyword table: keyword × impressions, clicks, ctr, quality_score, cost, conversions */
export function expandGoogleAdsKeywordTablePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Keyword Performance",
      config: {
        dimensionKey: "ad_group_criterion.keyword.text",
        metricKeys: ["impressions", "clicks", "ctr", "quality_score", "cost", "conversions"],
        integrationPlatform: platform,
        integrationId,
        reportType: "keywords",
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/** Video funnel: views → 25% → 50% → 75% → 100% */
export function expandGoogleAdsVideoFunnelPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "funnel",
      title: "Video View Funnel",
      config: {
        metrics: [
          { key: "video_views", label: "Views" },
          { key: "video_quartile_p25", label: "25%" },
          { key: "video_quartile_p50", label: "50%" },
          { key: "video_quartile_p75", label: "75%" },
          { key: "video_quartile_p100", label: "100%" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/** Shopping table: product_title × revenue, units_sold, gross_profit, AOV, COGS */
export function expandGoogleAdsShoppingTablePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Shopping Product Performance",
      config: {
        dimensionKey: "segments.product_title",
        metricKeys: ["revenue", "units_sold", "gross_profit", "average_order_value", "cogs"],
        integrationPlatform: platform,
        integrationId,
        reportType: "shopping",
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

// ============================================================================
// PageSpeed Presets (IMC-10)
// ============================================================================

/**
 * Expand the PageSpeed Scorecard preset into 5 KPI widgets.
 * All 5 Lighthouse category scores: Performance, Accessibility, Best Practices, SEO, PWA.
 */
export function expandPageSpeedScorecardPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis = [
    { metricKey: "performanceScore", title: "Performance" },
    { metricKey: "accessibilityScore", title: "Accessibility" },
    { metricKey: "seoScore", title: "SEO" },
    { metricKey: "bestPracticesScore", title: "Best Practices" },
    { metricKey: "pwaScore", title: "PWA" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: { metricKey: kpi.metricKey, comparisonEnabled: false, integrationPlatform: platform, integrationId },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Expand the Core Web Vitals preset into 4 KPI widgets with CWV thresholds.
 * CrUX p75 values: LCP, FCP, CLS, INP with good/poor thresholds for color coding.
 */
export function expandCoreWebVitalsPreset(platform: string, integrationId: string): PresetWidget[] {
  const cwvMetrics = [
    { metricKey: "crux_lcp", title: "LCP (p75)", goodThreshold: 2500, poorThreshold: 4000 },
    { metricKey: "crux_fcp", title: "FCP (p75)", goodThreshold: 1800, poorThreshold: 3000 },
    { metricKey: "crux_cls", title: "CLS (p75)", goodThreshold: 0.1, poorThreshold: 0.25 },
    { metricKey: "crux_inp", title: "INP (p75)", goodThreshold: 200, poorThreshold: 500 },
  ];
  return cwvMetrics.map((g) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: g.title,
    config: {
      metricKey: g.metricKey,
      comparisonEnabled: false,
      goodThreshold: g.goodThreshold,
      poorThreshold: g.poorThreshold,
      integrationPlatform: platform,
      integrationId,
      reportType: "cruxUrl",
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

/**
 * Expand the CrUX History Trend preset into 1 line-chart widget.
 * 40-week trend of LCP, FCP, CLS, INP p75 values.
 */
export function expandCruxHistoryPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Core Web Vitals Trend (40 Weeks)",
      config: {
        metrics: [
          { key: "crux_lcp", label: "LCP (ms)" },
          { key: "crux_fcp", label: "FCP (ms)" },
          { key: "crux_inp", label: "INP (ms)" },
        ],
        xAxis: "date",
        integrationPlatform: platform,
        integrationId,
        reportType: "cruxHistory",
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

// ============================================================================
// GA4 Presets (IMC-11)
// ============================================================================

/**
 * GA4 Traffic Overview: 5 KPIs — sessions, activeUsers, newUsers, bounceRate, engagementRate
 */
export function expandGA4TrafficOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis = [
    { metricKey: "sessions", title: "Sessions" },
    { metricKey: "activeUsers", title: "Users" },
    { metricKey: "newUsers", title: "New Users" },
    { metricKey: "bounceRate", title: "Bounce Rate" },
    { metricKey: "engagementRate", title: "Engagement Rate" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * GA4 E-commerce Funnel: funnel widget — itemsViewed → addToCarts → checkouts → purchases
 */
export function expandGA4EcommerceFunnelPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "funnel" as const,
      title: "E-commerce Funnel",
      config: {
        metrics: [
          { key: "itemsViewed", label: "Items Viewed" },
          { key: "addToCarts", label: "Add to Cart" },
          { key: "checkouts", label: "Checkouts" },
          { key: "purchases", label: "Purchases" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * GA4 Revenue: line-chart for purchaseRevenue trend + KPIs: revenuePerUser, purchases, purchaserConversionRate
 */
export function expandGA4RevenuePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart" as const,
      title: "Revenue Trend",
      config: {
        metrics: [
          { key: "purchaseRevenue", label: "Purchase Revenue" },
          { key: "totalRevenue", label: "Total Revenue" },
        ],
        xAxis: "date",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
    {
      id: crypto.randomUUID(),
      type: "kpi" as const,
      title: "Revenue/User",
      config: {
        metricKey: "revenuePerUser",
        comparisonEnabled: true,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 2,
      rowSpan: 1,
    },
    {
      id: crypto.randomUUID(),
      type: "kpi" as const,
      title: "Purchases",
      config: {
        metricKey: "purchases",
        comparisonEnabled: true,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 2,
      rowSpan: 1,
    },
    {
      id: crypto.randomUUID(),
      type: "kpi" as const,
      title: "Purchaser Rate",
      config: {
        metricKey: "purchaserConversionRate",
        comparisonEnabled: true,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 2,
      rowSpan: 1,
    },
  ];
}

/**
 * GA4 Acquisition: bar-chart of sessions by channel + data-table: source/medium × sessions, conversions
 */
export function expandGA4AcquisitionPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart" as const,
      title: "Sessions by Channel",
      config: {
        metrics: [{ key: "sessions", label: "Sessions" }],
        xAxis: "sessionDefaultChannelGrouping",
        integrationPlatform: platform,
        integrationId,
        reportType: "trafficSources",
      },
      colSpan: 6,
      rowSpan: 3,
    },
    {
      id: crypto.randomUUID(),
      type: "data-table" as const,
      title: "Acquisition Report",
      config: {
        metrics: [
          { key: "sessions", label: "Sessions" },
          { key: "activeUsers", label: "Users" },
          { key: "conversions", label: "Conversions" },
          { key: "engagementRate", label: "Engagement Rate" },
        ],
        dimension: "sessionDefaultChannelGrouping",
        integrationPlatform: platform,
        integrationId,
        reportType: "trafficSources",
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

// ============================================================================
// YouTube Presets (IMC-12)
// ============================================================================

/**
 * YouTube Overview: 5 KPIs — views, estimatedMinutesWatched, subscribersGained, likes, videoThumbnailImpressionsClickRate
 */
export function expandYouTubeOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis = [
    { metricKey: "views", title: "Views" },
    { metricKey: "estimatedMinutesWatched", title: "Watch Time (min)" },
    { metricKey: "subscribersGained", title: "Subscribers Gained" },
    { metricKey: "likes", title: "Likes" },
    { metricKey: "videoThumbnailImpressionsClickRate", title: "Thumbnail CTR" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * YouTube Video Table: data-table — video × views, averageViewDuration, likes, comments, shares
 */
export function expandYouTubeVideoTablePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table" as const,
      title: "Top Videos",
      config: {
        metricKeys: ["views", "averageViewDuration", "likes", "comments", "shares"],
        dimensionKey: "video",
        sortBy: "views",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
        reportType: "topVideos",
      },
      colSpan: 12,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

/**
 * YouTube Audience: demographic widget — ageGroup × gender views breakdown
 */
export function expandYouTubeAudiencePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "demographic" as const,
      title: "Audience Demographics",
      config: {
        metricKeys: ["viewerPercentage"],
        dimensionKey: "ageGroup",
        secondaryDimensionKey: "gender",
        integrationPlatform: platform,
        integrationId,
        reportType: "demographics",
      },
      colSpan: 6,
      rowSpan: 2,
    },
  ];
}

// ── LinkedIn Ads Presets (IMC-13) ──────────────────────────────────────

/**
 * Expand the LinkedIn Ads Overview KPI preset into 5 KPI widgets.
 *
 * Uses LinkedIn Ads adapter metric keys:
 * - spend, impressions, clicks, ctr, conversions
 */
export function expandLinkedInAdsOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "spend", title: "Ad Spend" },
    { metricKey: "impressions", title: "Impressions" },
    { metricKey: "clicks", title: "Clicks" },
    { metricKey: "ctr", title: "CTR" },
    { metricKey: "conversions", title: "Conversions" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Expand the LinkedIn Ads Video Funnel preset into 1 funnel widget.
 *
 * Displays: videoStarts → videoViews → videoMidpoint → videoCompletions
 */
export function expandLinkedInAdsVideoPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "funnel",
      title: "Video View Funnel",
      config: {
        metrics: [
          { key: "video_starts", label: "Starts" },
          { key: "video_views", label: "Views (2s+)" },
          { key: "video_midpoint", label: "50% Watched" },
          { key: "video_completions", label: "Completed" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the LinkedIn Ads Demographics preset into 1 bar-chart widget.
 *
 * Impressions by MEMBER_INDUSTRY + MEMBER_SENIORITY pivots.
 */
export function expandLinkedInAdsDemographicsPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Professional Demographics",
      config: {
        metricKey: "impressions",
        dimensionKey: "member_industry",
        secondaryDimensionKey: "member_seniority",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the LinkedIn Ads Lead Gen preset into 4 KPI widgets.
 *
 * Uses: leads, cost_per_lead, qualified_leads, lead_form_opens
 */
export function expandLinkedInAdsLeadGenPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "leads", title: "Leads" },
    { metricKey: "cost_per_lead", title: "Cost per Lead" },
    { metricKey: "qualified_leads", title: "Qualified Leads" },
    { metricKey: "lead_form_opens", title: "Form Opens" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

// ── Microsoft Ads Presets (Story 19.3) ────────────────────────────────

/**
 * Expand the Microsoft Ads Overview KPI preset into 5 KPI widgets.
 * Uses Microsoft Ads adapter metric keys: spend, impressions, clicks, ctr,
 * conversionsQualified. Mirrors LinkedIn Ads / Google Ads precedents.
 */
export function expandMicrosoftAdsOverviewPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "spend", title: "Ad Spend" },
    { metricKey: "impressions", title: "Impressions" },
    { metricKey: "clicks", title: "Clicks" },
    { metricKey: "ctr", title: "CTR" },
    { metricKey: "conversionsQualified", title: "Conversions" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Expand the Microsoft Ads Impression Share preset.
 * Surfaces Bing's most-marketed metric: gauge for current IS + bar chart for
 * IS lost to budget vs. rank — agencies use this to ask "is the budget the
 * bottleneck or the bid?".
 */
export function expandMicrosoftAdsImpressionSharePreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "gauge",
      title: "Impression Share",
      config: {
        metricKey: "impressionSharePercent",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Impression Share Lost: Budget vs. Rank",
      config: {
        metrics: [
          { key: "impressionShareLostToBudgetPercent", label: "Lost to Budget" },
          { key: "impressionShareLostToRankPercent", label: "Lost to Rank" },
        ],
        dimensionKey: "campaignName",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Microsoft Ads Quality Score Keywords preset.
 * Search-only data table grouped by keyword, surfacing the 9 Quality Score
 * sub-fields plus delivery + cost metrics. Agencies use this to spot
 * keywords with `expectedCtr=1` (below average) that need bid lifts.
 */
export function expandMicrosoftAdsKeywordQualityPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Keyword Quality Score Performance",
      config: {
        dimensionKey: "keyword",
        metrics: [
          { key: "qualityScore", label: "Quality Score" },
          { key: "historicalQualityScore", label: "Historical QS" },
          { key: "expectedCtr", label: "Expected CTR" },
          { key: "adRelevance", label: "Ad Relevance" },
          { key: "landingPageExperience", label: "LP Experience" },
          { key: "currentMaxCpc", label: "Max CPC" },
          { key: "ctr", label: "CTR" },
          { key: "conversionsQualified", label: "Conversions" },
          { key: "costPerConversion", label: "CPA" },
        ],
        sortBy: "qualityScore",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * Expand the Microsoft Ads Campaign Performance preset.
 * Cross-campaign data table mirroring Google Ads / LinkedIn Ads precedents:
 * delivery + cost + IS in a single drill-down table sorted by spend desc.
 */
export function expandMicrosoftAdsCampaignPerformancePreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Campaign Performance",
      config: {
        dimensionKey: "campaignName",
        metrics: [
          { key: "campaignType", label: "Type" },
          { key: "spend", label: "Spend" },
          { key: "clicks", label: "Clicks" },
          { key: "impressions", label: "Impressions" },
          { key: "ctr", label: "CTR" },
          { key: "conversionsQualified", label: "Conversions" },
          { key: "conversionRate", label: "Conv Rate" },
          { key: "costPerConversion", label: "CPA" },
          { key: "returnOnAdSpend", label: "ROAS" },
          { key: "impressionSharePercent", label: "IS%" },
        ],
        sortBy: "spend",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Microsoft Ads PMax Final URL preset.
 * Built for Microsoft's April 2026 Performance Max landing page reporting
 * launch — surfaces spend / clicks / conversions / ROAS grouped by the
 * `finalUrl` dimension. Empty for accounts with no PMax campaigns; the
 * widget renderer's empty-state component handles that case.
 */
export function expandMicrosoftAdsPmaxFinalUrlPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Performance Max Final URL Performance",
      config: {
        dimensionKey: "finalUrl",
        metrics: [
          { key: "spend", label: "Spend" },
          { key: "impressions", label: "Impressions" },
          { key: "clicks", label: "Clicks" },
          { key: "ctr", label: "CTR" },
          { key: "conversionsQualified", label: "Conversions" },
          { key: "allRevenue", label: "Revenue" },
          { key: "returnOnAdSpend", label: "ROAS" },
        ],
        sortBy: "spend",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

// ── Pinterest Ads Presets (Story 19.6) ────────────────────────────────

/**
 * Expand the Pinterest Ads Overview KPI preset into 5 KPI widgets.
 * Bound metrics: spend, totalImpressions, totalEngagements, paidSaves, checkoutRoas.
 * Why these 5: agencies prioritize Spend + Total Impressions (uniquely surfaces
 * Pinterest's paid+earned amplification) + Total Engagements + Paid Saves
 * (Pinterest's #1 leading conversion indicator per WebFX 2026) + ROAS.
 *
 * Note: the "Saves" KPI binds to `paidSaves` (the metric agencies act on —
 * billed saves only). A "total saves" rollup metric is not registered in
 * PINTEREST_ADS_METRICS; if a future story adds it, swap the binding here.
 */
export function expandPinterestAdsOverviewPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "spend", title: "Ad Spend" },
    { metricKey: "totalImpressions", title: "Total Impressions" },
    { metricKey: "totalEngagements", title: "Total Engagements" },
    { metricKey: "paidSaves", title: "Paid Saves" },
    { metricKey: "checkoutRoas", title: "Checkout ROAS" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Expand the Pinterest Paid vs Earned preset into a single stacked bar chart.
 * Bound metrics: paidImpressions + earnedImpressions over time.
 * Why a separate preset: Pinterest's earned-amplification ratio is the
 * platform's competitive moat vs Meta/TikTok — no other platform surfaces this
 * paid-vs-earned split natively. Surfacing it as a 1-click preset puts
 * ProvenLeap ahead of AgencyAnalytics / Swydo / Whatagraph.
 */
export function expandPinterestAdsPaidVsEarnedPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Paid vs Earned Impressions",
      config: {
        metrics: [
          { key: "paidImpressions", label: "Paid Impressions" },
          { key: "earnedImpressions", label: "Earned Impressions" },
        ],
        dimensionKey: "date",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Pinterest Saves Performance preset into 1 KPI + 1 line chart +
 * 1 data table. Bound metrics: paidSaveRate (KPI), paidSaves+earnedSaves
 * (line chart over time), per-pin breakdown (data table sorted by paidSaves).
 * Why: Saves are Pinterest's #1 leading conversion indicator (WebFX 2026).
 * Agencies need the per-pin breakdown to identify breakout creative.
 */
export function expandPinterestAdsSavesPerformancePreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "kpi",
      title: "Paid Save Rate",
      config: {
        metricKey: "paidSaveRate",
        comparisonEnabled: true,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 3,
      rowSpan: 1,
    },
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Saves Over Time — Paid + Earned",
      config: {
        metrics: [
          { key: "paidSaves", label: "Paid Saves" },
          { key: "earnedSaves", label: "Earned Saves" },
        ],
        dimensionKey: "date",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 9,
      rowSpan: 3,
    },
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Saves by Promoted Pin",
      config: {
        dimensionKey: "pinTitle",
        metrics: [
          { key: "paidSaves", label: "Paid Saves" },
          { key: "earnedSaves", label: "Earned Saves" },
          { key: "paidSaveRate", label: "Paid Save Rate" },
          { key: "totalRepinRate", label: "Total Repin Rate" },
          { key: "paidImpressions", label: "Paid Impressions" },
          { key: "paidClicks", label: "Paid Clicks" },
        ],
        sortBy: "paidSaves",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * Expand the Pinterest Video Funnel preset into a single funnel widget.
 * Bound metrics: paidVideoP0 → paidVideoP25 → paidVideoP50 → paidVideoP75 →
 * paidVideoP95 → paidVideoP100 (6 funnel stages).
 * Why: Mirrors the Meta Ads / TikTok Ads / LinkedIn Ads video funnel pattern;
 * Pinterest's video ad inventory grew 50% in 2025 per April 2026 research.
 */
export function expandPinterestAdsVideoFunnelPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "funnel",
      title: "Pinterest Video Funnel — Paid",
      config: {
        metrics: [
          { key: "paidVideoP0", label: "Video Started (0%)" },
          { key: "paidVideoP25", label: "25% Watched" },
          { key: "paidVideoP50", label: "50% Watched" },
          { key: "paidVideoP75", label: "75% Watched" },
          { key: "paidVideoP95", label: "95% Watched" },
          { key: "paidVideoP100", label: "100% Complete" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * Expand the Pinterest Conversion ROAS Table preset into a single data table.
 * Bound metrics: spend, totalImpressions, totalEngagements, totalCheckouts,
 * checkoutConversionRate, totalCheckoutValue, checkoutRoas, pageVisitRoas,
 * customEventRoas + objectiveType — grouped by campaignName.
 * Mirrors Meta Ads / Microsoft Ads / Google Ads conversion-table presets.
 */
export function expandPinterestAdsConversionRoasTablePreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Conversion ROAS by Campaign",
      config: {
        dimensionKey: "campaignName",
        metrics: [
          { key: "objectiveType", label: "Objective" },
          { key: "spend", label: "Spend" },
          { key: "totalImpressions", label: "Impressions" },
          { key: "totalEngagements", label: "Engagements" },
          { key: "totalCheckouts", label: "Checkouts" },
          { key: "checkoutConversionRate", label: "Checkout Conv Rate" },
          { key: "totalCheckoutValue", label: "Checkout Value" },
          { key: "checkoutRoas", label: "Checkout ROAS" },
          { key: "pageVisitRoas", label: "Page Visit ROAS" },
          { key: "customEventRoas", label: "Custom Event ROAS" },
        ],
        sortBy: "checkoutRoas",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Pinterest Cross-Device Attribution Table preset into a single
 * data table surfacing Pinterest's distinct Web/In-App/Pinterest-native
 * attribution split. Bound metrics: totalWebCheckouts, totalInappCheckouts,
 * totalPinterestCheckouts, totalWebSessions, paidWebSessions, earnedWebSessions,
 * webCheckoutRoas, inappCheckoutRoas, pinterestCheckoutRoas — grouped by
 * campaignName. Pinterest's split is unique among integrations — Swydo and
 * AgencyAnalytics do NOT surface this as a default view.
 *
 * Note: live data requires Pinterest Tag installation. Demo mode always
 * populates these columns. Empty states are handled by the widget renderer's
 * empty-state component.
 */
export function expandPinterestAdsCrossDeviceAttributionTablePreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Cross-Device Attribution — Web / In-App / Pinterest-Native",
      config: {
        dimensionKey: "campaignName",
        metrics: [
          { key: "totalWebCheckouts", label: "Web Checkouts" },
          { key: "totalInappCheckouts", label: "In-App Checkouts" },
          { key: "totalPinterestCheckouts", label: "Pinterest Checkouts" },
          { key: "totalPinterestCheckoutValue", label: "Pinterest Checkout $" },
          { key: "totalWebSessions", label: "Total Web Sessions" },
          { key: "paidWebSessions", label: "Paid Web Sessions" },
          { key: "earnedWebSessions", label: "Earned Web Sessions" },
          { key: "webCheckoutRoas", label: "Web Checkout ROAS" },
          { key: "inappCheckoutRoas", label: "In-App Checkout ROAS" },
          { key: "pinterestCheckoutRoas", label: "Pinterest Checkout ROAS" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

// ── Snapchat Ads Presets (Story 19.9) ──────────────────────────────────

/**
 * Expand the Snapchat Ads Overview KPI preset into 5 KPI widgets.
 * Bound metrics: spend, impressions, swipeUps, swipeUpRate, conversionPurchasesValue.
 * Why these 5: per April 2026 Snapchat for Business + BeProfit + Megadigital,
 * agencies prioritise Spend (#1) + Impressions (delivery proof) + Swipe-Ups
 * (Snap's #1 leading indicator of conversion) + Swipe-Up Rate (the platform's
 * signature engagement metric — Snap-vocabulary equivalent of CTR) + Purchase
 * Value (proof of revenue impact). ROAS is computed client-side from
 * conversionPurchasesValue / spend (the `_derived_roas` apiField never emits).
 */
export function expandSnapchatAdsOverviewPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "spend", title: "Ad Spend" },
    { metricKey: "impressions", title: "Impressions" },
    { metricKey: "swipeUps", title: "Swipe-Ups" },
    { metricKey: "swipeUpRate", title: "Swipe-Up Rate" },
    { metricKey: "conversionPurchasesValue", title: "Purchase Value" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Expand the Snapchat Swipe-Up Funnel preset into a single funnel widget.
 * Bound stages: impressions → swipeUps → conversionPageViews →
 * conversionAddCart → conversionPurchases (5 stages).
 * Why a Swipe-Up Funnel preset: swipe-up is Snap's distinctive engagement
 * primitive — no other platform has the exact same swipe-up gesture as a
 * top-of-funnel signal. Surfacing the full funnel on swipe-ups demos Snap's
 * Gen-Z engagement economics per April 2026 Cometly + Nostra DTC playbooks.
 */
export function expandSnapchatAdsSwipeUpFunnelPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "funnel",
      title: "Snapchat Swipe-Up Funnel",
      config: {
        metrics: [
          { key: "impressions", label: "Impressions" },
          { key: "swipeUps", label: "Swipe-Ups" },
          { key: "conversionPageViews", label: "Page Views" },
          { key: "conversionAddCart", label: "Add to Cart" },
          { key: "conversionPurchases", label: "Purchases" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * Expand the Snapchat Video Quartile Funnel preset into a single funnel widget.
 * Bound stages: videoViews2s → videoQuartile1 → videoQuartile2 →
 * videoQuartile3 → viewCompletion (5 stages — Snap's video drop-off funnel).
 * Why: mirrors the Pinterest / Meta / TikTok / LinkedIn video preset patterns.
 * Snap's 50%+ engagement lift on AR / video creatives (per April 2026 Snap
 * 2025 performance review) makes video drop-off a first-class concern for
 * Snap fashion DTC agencies.
 */
export function expandSnapchatAdsVideoQuartileFunnelPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "funnel",
      title: "Snapchat Video Quartile Funnel",
      config: {
        metrics: [
          { key: "videoViews2s", label: "2s Views" },
          { key: "videoQuartile1", label: "25% Watched" },
          { key: "videoQuartile2", label: "50% Watched" },
          { key: "videoQuartile3", label: "75% Watched" },
          { key: "viewCompletion", label: "100% Complete" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * Expand the Snapchat App-vs-Web Conversions preset into 1 stacked bar chart
 * + 1 KPI for the App-vs-Web ratio.
 * Bound metrics: conversionPurchasesApp + conversionPurchasesWeb over time.
 * Why this is Snap-unique: Snap's `_app`/`_web`/total channel split (per
 * Story 19.8 transformer + integration-mapping-directive line 184) is the
 * platform's competitive moat vs Meta/Google — neither natively splits app
 * vs web in conversion reporting. Surfacing it as a 1-click preset puts
 * ProvenLeap ahead of AgencyAnalytics / Swydo / Whatagraph (none of them
 * surface this Snap-specific split as a default chart per April 2026
 * competitive analysis).
 */
export function expandSnapchatAdsAppVsWebConversionsPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Purchases — App vs Web",
      config: {
        metrics: [
          { key: "conversionPurchasesApp", label: "App Purchases" },
          { key: "conversionPurchasesWeb", label: "Web Purchases" },
        ],
        dimensionKey: "date",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
    {
      id: crypto.randomUUID(),
      type: "kpi",
      title: "App Purchase Value",
      config: {
        metricKey: "conversionPurchasesValueApp",
        comparisonEnabled: true,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 1,
    },
  ];
}

/**
 * Expand the Snapchat Demographic Breakdown preset into a single demographic
 * widget. Dimension: ageGender (Snap's combined breakdown — the only
 * combinable dimension pair besides country+os). Bound metrics:
 * impressions, swipeUps, swipeUpRate, conversionPurchases,
 * conversionPurchasesValue.
 * Why: Snap's 90% Gen-Z penetration (per April 2026 Snapchat for Business)
 * is the platform's #1 differentiator vs Meta/TikTok. Surfacing the age ×
 * gender breakdown — including the Feb 2025 `55+` bucket — proves Snap's
 * audience uniqueness to a buyer in 1 click.
 */
export function expandSnapchatAdsDemographicBreakdownPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "demographic",
      title: "Snapchat Demographic Breakdown — Age × Gender",
      config: {
        // The fixture's `_reportType: "ageGender"` records carry both
        // `ageBucket` and `gender` as separate keys (mirrors YouTube's
        // pattern at line ~1126). Bind primary dim to ageBucket + secondary
        // to gender so the demographic widget renders the cross-tab.
        dimensionKey: "ageBucket",
        secondaryDimensionKey: "gender",
        metrics: [
          { key: "impressions", label: "Impressions" },
          { key: "swipeUps", label: "Swipe-Ups" },
          { key: "swipeUpRate", label: "Swipe-Up Rate" },
          { key: "conversionPurchases", label: "Purchases" },
          { key: "conversionPurchasesValue", label: "Purchase Value" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * Expand the Snapchat Conversion ROAS Table preset into a single data table.
 * Dimension: campaignName. Bound metrics include both the App/Web split AND
 * the totals so an agency can see per-campaign channel attribution at a
 * glance. Sorted by conversionPurchasesValue desc.
 * Mirrors Meta Ads / Microsoft Ads / Google Ads / Pinterest conversion-table
 * presets.
 */
export function expandSnapchatAdsConversionRoasTablePreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Conversion ROAS by Campaign",
      config: {
        dimensionKey: "campaignName",
        metrics: [
          { key: "campaignObjective", label: "Objective" },
          { key: "spend", label: "Spend" },
          { key: "impressions", label: "Impressions" },
          { key: "swipeUps", label: "Swipe-Ups" },
          { key: "swipeUpRate", label: "Swipe-Up Rate" },
          { key: "totalInstalls", label: "Installs" },
          { key: "conversionPurchases", label: "Purchases" },
          { key: "conversionPurchasesApp", label: "App Purchases" },
          { key: "conversionPurchasesWeb", label: "Web Purchases" },
          { key: "conversionPurchasesValue", label: "Purchase Value" },
          { key: "conversionPurchasesValueApp", label: "App Value" },
          { key: "conversionPurchasesValueWeb", label: "Web Value" },
        ],
        sortBy: "conversionPurchasesValue",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

// ── Reddit Ads Presets (Story 19.12) ───────────────────────────────────

/**
 * Expand the Reddit Ads Overview KPI preset into 5 KPI widgets.
 * Bound metrics: spend, impressions, clicks, ctr, keyConversionTotalCount.
 * Why these 5: per April 2026 abetheagency.com Reddit B2B benchmarks +
 * Stackmatix CPC research, agencies prioritize Spend (#1 cost signal) +
 * Impressions (delivery proof) + Clicks (Reddit's #1 engagement primitive
 * vs swipe-ups on Snap) + CTR (Reddit-specific 0.2-1.0% range forces a
 * "is this performing?" framing on first glance) + Key Conversion Count
 * (CloudStack-specific trial-starts / demo-bookings — the agency's KPI).
 * Mirrors the 5-KPI count Snap 19.9 / Pinterest 19.6 / Microsoft 19.3 set.
 */
export function expandRedditAdsOverviewPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "spend", title: "Ad Spend" },
    { metricKey: "impressions", title: "Impressions" },
    { metricKey: "clicks", title: "Clicks" },
    { metricKey: "ctr", title: "CTR" },
    { metricKey: "keyConversionTotalCount", title: "Key Conversions" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Expand the Reddit Ads Subreddit Performance preset into a single data table.
 * Dimension: subreddit. Metrics: impressions, clicks, ctr, spend, cpc,
 * keyConversionTotalCount, purchaseTotalValue (sorted by spend desc).
 * Why this is THE strategic anchor: no other ad platform exposes "show me CTR
 * by /r/devops vs /r/sysadmin." The dimension exists in Story 19.11's
 * catalogue, the data is real, and the demo viewer instantly understands
 * Reddit's value prop on sight. Per April 2026 ALM Corp + Stackmatix research,
 * "subreddit-creative-fit drives CTR" is Reddit's signature lesson.
 */
export function expandRedditAdsSubredditPerformancePreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Reddit Subreddit / Community Performance",
      config: {
        dimensionKey: "subreddit",
        metrics: [
          { key: "impressions", label: "Impressions" },
          { key: "clicks", label: "Clicks" },
          { key: "ctr", label: "CTR" },
          { key: "spend", label: "Spend" },
          { key: "cpc", label: "CPC" },
          { key: "keyConversionTotalCount", label: "Key Conversions" },
          { key: "purchaseTotalValue", label: "Purchase Value" },
        ],
        sortBy: "spend",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * Expand the Reddit Ads Conversion Attribution Funnel preset into a single
 * funnel widget. Bound stages: impressions → clicks → pageVisitClicks →
 * addToCartClicks → purchaseClicks (5-stage CLICKS-only funnel).
 * Why a CLICKS-only funnel: Reddit's distinguishing v3 attribution model
 * splits every conversion event into _CLICKS and _VIEWS. Agencies starting
 * on Reddit often confuse the two — a CLICKS-only funnel is the cleanest
 * "high-intent click-attribution path" view, avoiding view-through inflation.
 */
export function expandRedditAdsConversionAttributionFunnelPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "funnel",
      title: "Reddit Conversion Funnel (Click-Attributed)",
      config: {
        metrics: [
          { key: "impressions", label: "Impressions" },
          { key: "clicks", label: "Clicks" },
          { key: "pageVisitClicks", label: "Page Visits" },
          { key: "addToCartClicks", label: "Add to Cart" },
          { key: "purchaseClicks", label: "Purchases" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * Expand the Reddit Ads Video Completion Funnel preset into a single funnel.
 * Bound stages: videoStarted → videoWatched3Seconds → videoWatched25Percent
 * → videoWatched50Percent → videoWatched75Percent → videoWatched100Percent
 * (6-stage drop-off — finer-grained than Snap's 5-stage quartile because
 * Reddit exposes the 3s sub-quartile + 25/50/75/100% percent fields per
 * Story 19.11 catalogue rows 19-33).
 * Why a separate Video Completion preset: mirrors Snap / Pinterest / Meta /
 * TikTok / LinkedIn video preset patterns. Per April 2026 Reddit for Business
 * research, Reddit Video Ads (Auto-Generate Video for B2B SaaS demo
 * conversion) drove ~3× higher CTR than image ads in 2025.
 */
export function expandRedditAdsVideoCompletionFunnelPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "funnel",
      title: "Reddit Video Completion Funnel",
      config: {
        metrics: [
          { key: "videoStarted", label: "Started" },
          { key: "videoWatched3Seconds", label: "3s" },
          { key: "videoWatched25Percent", label: "25%" },
          { key: "videoWatched50Percent", label: "50%" },
          { key: "videoWatched75Percent", label: "75%" },
          { key: "videoWatched100Percent", label: "100%" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * Expand the Reddit Ads Geo Country/Region preset into a demographic widget.
 * Dimension: country (drilling into region is a 2nd-click). Metrics:
 * impressions, clicks, ctr, spend, keyConversionTotalCount.
 * Why this is the geo preset: Reddit's `breakdowns=["COUNTRY", "REGION"]` is
 * the only combined-4-breakdown Reddit allows (Story 19.11 AC #3) — making
 * it the prime demonstration of Reddit's flexible-breakdown architecture.
 */
export function expandRedditAdsGeoCountryRegionPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "demographic",
      title: "Reddit Geo — Country × Region",
      config: {
        dimensionKey: "country",
        secondaryDimensionKey: "region",
        metrics: [
          { key: "impressions", label: "Impressions" },
          { key: "clicks", label: "Clicks" },
          { key: "ctr", label: "CTR" },
          { key: "spend", label: "Spend" },
          { key: "keyConversionTotalCount", label: "Key Conversions" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * Expand the Reddit Ads Click vs View Attribution preset into a stacked bar
 * chart + a KPI for the Click-vs-View ratio.
 * Bound metrics: purchaseClicks + purchaseViews stacked over time + KPI for
 * purchaseClicks (the "primary attribution path" signal).
 * Why this is Reddit-unique: Reddit's _CLICKS / _VIEWS attribution split per
 * conversion event (Story 19.11 catalogue + AC #5) is the competitive moat
 * vs every pre-2025 metric-suffix attribution model. Reddit Ads removed
 * `_1d` / `_7d` / `_28d` metric suffixes in Aug 2025 in favor of the
 * dimension-based attribution-window split.
 */
export function expandRedditAdsClickVsViewAttributionPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Purchases — Click vs View Attribution",
      config: {
        metrics: [
          { key: "purchaseClicks", label: "Click-Attributed" },
          { key: "purchaseViews", label: "View-Attributed" },
        ],
        dimensionKey: "date",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
    {
      id: crypto.randomUUID(),
      type: "kpi",
      title: "Click-Attributed Purchases",
      config: {
        metricKey: "purchaseClicks",
        comparisonEnabled: true,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 1,
    },
  ];
}

// ── Amazon Ads Presets (Story 19.17) ───────────────────────────────────
//
// 8 presets — Overview KPI / Advertised-Product / SB NTB Funnel / SD Click vs
// View / DSP Audience Performance / DSP Brand Store Engagement / ACOS-ROAS
// Trend / Cross-Surface Comparison. Closes Epic 19.
//
// Each preset sets `requestedLevel` to the Amazon Ads surface hint
// ("amazon-sp" / "amazon-sb" / "amazon-sd" / "amazon-dsp" / undefined for
// cross-surface) so the adapter's `loadAmazonAdsFixture` filter can serve
// surface-specific records from a single fixture KV key (Story 19.17 AC #9).

/**
 * Expand the Amazon Ads Overview KPI preset into 5 KPI widgets.
 * Per Improvado / AgencyAnalytics / Triple Whale May 2026 — the canonical
 * Amazon Ads agency executive overview is Spend / Impressions / Clicks /
 * ACOS / ROAS. TACOS (Total ACOS) is the agency-best-practice metric per
 * Improvado but requires Amazon Sales Reports (deferred to a future
 * cross-integration story).
 */
export function expandAmazonAdsOverviewPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "cost", title: "Ad Spend" },
    { metricKey: "impressions", title: "Impressions" },
    { metricKey: "clicks", title: "Clicks" },
    { metricKey: "acosClicks14d", title: "ACOS (14d)" },
    { metricKey: "roasClicks14d", title: "ROAS (14d)" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
      requestedLevel: "amazon-sp",
      // TACOS deferral notice (Round 1 Wellness DTC E-commerce Owner finding).
      tooltip:
        "TACOS not yet available — requires Amazon Sales Reports integration; coming in a future story.",
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Expand the Amazon Ads Advertised-Product Performance preset into 1 data
 * table. Dimension: advertisedAsin. Per AgencyAnalytics May 2026 — the
 * Advertised-Product table is the #1 chart for white-label Amazon Ads
 * reports. ProvenLeap's 1-click preset surface puts us ahead of competitors.
 */
export function expandAmazonAdsAdvertisedProductPerformancePreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Advertised Product Performance (ASIN-Level)",
      config: {
        dimensionKey: "advertisedAsin",
        metrics: [
          { key: "advertisedProductTitle", label: "Product" },
          { key: "impressions", label: "Impressions" },
          { key: "clicks", label: "Clicks" },
          { key: "clickThroughRate", label: "CTR" },
          { key: "cost", label: "Spend" },
          { key: "sales14d", label: "Sales (14d)" },
          { key: "purchases14d", label: "Purchases (14d)" },
          { key: "acosClicks14d", label: "ACOS (14d)" },
          { key: "roasClicks14d", label: "ROAS (14d)" },
        ],
        sortBy: "cost",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
        requestedLevel: "amazon-sp",
        tooltip:
          "ASIN-level reporting: Amazon's signature surface. SKU-level rollup available via the secondary advertisedSku dimension.",
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * Expand the Amazon Ads SB New-to-Brand Funnel preset into 1 funnel.
 * Per advertising.amazon.com May 2026 — branded search lift + NTB rate are
 * SB's two flagship advertiser-success metrics. The 5-stage funnel isolates
 * SB's "branded discovery" lift from generic spend efficiency.
 */
export function expandAmazonAdsSbNtbFunnelPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "funnel",
      title: "Sponsored Brands — New-to-Brand Funnel",
      config: {
        metrics: [
          { key: "impressions", label: "Impressions" },
          { key: "viewableImpressions", label: "Viewable" },
          { key: "clicks", label: "Clicks" },
          { key: "brandedSearches", label: "Branded Searches" },
          { key: "newToBrandSales14d", label: "NTB Sales (14d)" },
        ],
        integrationPlatform: platform,
        integrationId,
        requestedLevel: "amazon-sb",
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * Expand the Amazon Ads SD Click-vs-View preset into a stacked-bar chart.
 * Per Velocity Sellers / Improvado May 2026 — view-through conversions are
 * SD's flagship feature. The Click-vs-View visual answers "what % of SD's
 * reported conversions are click-through vs view-through?" — proving real
 * retargeting lift vs attribution-window-stuffing.
 */
export function expandAmazonAdsSdClickVsViewPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Sponsored Display — Click vs View Attribution",
      config: {
        metrics: [
          { key: "purchasesClicks14d", label: "Click-Attributed (14d)" },
          { key: "purchasesViews14d", label: "View-Attributed (14d)" },
        ],
        dimensionKey: "date",
        integrationPlatform: platform,
        integrationId,
        requestedLevel: "amazon-sd",
        tooltip:
          "Sponsored Display's view-through conversions are 14-day attributed by default. Agencies report click-through and view-through separately to prove genuine retargeting lift vs attribution noise.",
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * Expand the Amazon Ads DSP Audience Performance preset into 1 data table.
 * STORY 19.16.1 INVESTMENT PAYOFF — Story 19.16.1 added DSP_AUDIENCE as a
 * new report type specifically because agencies actively use this metric.
 * Per Pacvue / Improvado May 2026 — AMC lookalike audiences hit 3-5× better
 * ROAS. The table answers DSP's flagship question: "which audience segment
 * is converting best?" — Amazon's own reporting UI does NOT answer this in
 * a single sortable view.
 */
export function expandAmazonAdsDspAudiencePerformancePreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "DSP Audience Performance",
      config: {
        dimensionKey: "segmentName",
        metrics: [
          { key: "segmentType", label: "Segment Type" },
          { key: "impressions", label: "Impressions" },
          { key: "clicks", label: "Clicks" },
          { key: "spend", label: "Spend" },
          { key: "detailPageViews", label: "DPV" },
          { key: "addToCart", label: "Add to Cart" },
          { key: "purchases", label: "Purchases" },
          { key: "audienceOverlap", label: "Overlap" },
        ],
        sortBy: "purchases",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
        requestedLevel: "amazon-dsp",
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * Expand the Amazon Ads DSP Brand Store Engagement Funnel preset.
 * Story 19.16.1 added the brand-store-engagement levels 1-7 grid (35
 * fields per `STORE_ANALYTICS_SECTION_LEVEL_COLUMN_MAP`). Per
 * advertising.amazon.com May 2026 — brand store ASIN engagement metrics
 * are an agency-priority surface launched late 2025. No competitor
 * reporting tool surfaces this as a default chart.
 */
export function expandAmazonAdsDspBrandStoreEngagementFunnelPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "funnel",
      title: "DSP Brand Store Engagement Funnel",
      config: {
        metrics: [
          { key: "brandStoreVisits", label: "Store Visits" },
          { key: "brandStoreLevel1Views", label: "Level 1" },
          { key: "brandStoreLevel2Views", label: "Level 2" },
          { key: "brandStoreLevel3Views", label: "Level 3" },
          { key: "brandStorePurchases", label: "Store Purchases" },
        ],
        integrationPlatform: platform,
        integrationId,
        requestedLevel: "amazon-dsp",
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * Expand the Amazon Ads ACOS / ROAS Trend preset into 1 dual-axis line chart.
 * Per AgencyAnalytics May 2026 — the ACOS/ROAS pair over 90 days is the
 * agency executive-dashboard signature. Plotted together, they show "is
 * this campaign ramping up profitably?" in a single chart — the highest-
 * importance preset for buyer-evaluation purposes.
 */
export function expandAmazonAdsAcosRoasTrendPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "ACOS / ROAS Trend (14d Attribution)",
      config: {
        dimensionKey: "date",
        metrics: [
          { key: "acosClicks14d", label: "ACOS (14d)", axis: "left" },
          { key: "roasClicks14d", label: "ROAS (14d)", axis: "right" },
        ],
        integrationPlatform: platform,
        integrationId,
        requestedLevel: "amazon-sp",
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Amazon Ads Cross-Surface (SP+SB+SD) Comparison preset into a
 * stacked-bar chart with 3 bars (one per surface). THE multi-platform-reach
 * moat — no other reporting tool surfaces this in 1 widget. Amazon's own
 * Seller Central UI does NOT answer "what % of my Amazon Ads budget is
 * going to SP vs SB vs SD?" in one view (it forces 3 separate report
 * exports). DSP excluded — programmatic CPM-based vs PPC CPC-based.
 *
 * No `requestedLevel` set — Cross-Surface preset deliberately requests all
 * 4 surfaces from `loadAmazonAdsFixture` (it filters surface dispatch
 * based on `requestedLevel`).
 */
export function expandAmazonAdsCrossSurfaceComparisonPreset(
  platform: string,
  integrationId: string
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Cross-Surface Comparison (SP / SB / SD)",
      config: {
        dimensionKey: "_adProduct",
        // Pass 2 review fix: explicitly include only PPC surfaces (SP/SB/SD).
        // The fixture-mode helper returns all 4 surfaces when no
        // `requestedLevel` is set; the widget renderer applies this allowlist
        // to drop DSP (programmatic CPM-based — apples-to-oranges vs PPC CPC).
        dimensionValueAllowlist: ["SP", "SB", "SD"],
        metrics: [
          { key: "cost", label: "Spend" },
          { key: "sales14d", label: "Sales (14d)" },
        ],
        integrationPlatform: platform,
        integrationId,
        // No requestedLevel — pulls all 4 surfaces from the fixture.
        tooltip:
          "DSP excluded from this comparison — programmatic vs PPC cost models differ. See DSP-specific presets above.",
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

// ── Ahrefs Presets (Story 20.3 — opens SEO category, closes Epic 20) ──

/**
 * Expand the Ahrefs Domain Rating Gauge preset.
 * Per AgencyAnalytics + DashThis + Whatagraph + Looker Studio May 2026
 * templates — DR is the universal SEO headline metric on every agency
 * client report. Threshold band: 0-30 weak / 30-60 mid / 60-100 strong.
 */
export function expandAhrefsDomainRatingGaugePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "gauge",
      title: "Domain Rating",
      config: {
        metricKey: "domainRating",
        target: 100,
        thresholds: [
          { value: 30, color: "red" },
          { value: 60, color: "amber" },
          { value: 100, color: "green" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 2,
    },
  ];
}

/**
 * Expand the Ahrefs Backlink Profile Overview preset (4 KPIs).
 * Per AgencyAnalytics dashboard May 2026 — the backlinks reporting golden
 * path, 4 KPIs in one row above the trend chart.
 */
export function expandAhrefsBacklinkProfileOverviewPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "liveBacklinks", title: "Live Backlinks" },
    { metricKey: "liveReferringDomains", title: "Referring Domains" },
    { metricKey: "backlinksDofollow", title: "Dofollow Backlinks" },
    { metricKey: "referringDomainsDofollow", title: "Dofollow Ref Domains" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

/**
 * Expand the Ahrefs Organic Traffic & Value Trend preset (dual-axis line).
 * Per Improvado SEO dashboard May 2026 — "we 3xed your organic traffic
 * value" is the executive-dashboard signature line agencies use to prove
 * SEO ROI in 1 chart.
 */
export function expandAhrefsOrganicTrafficValueTrendPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Organic Traffic & Value Trend (90-day)",
      config: {
        dimensionKey: "date",
        metrics: [
          { key: "organicTraffic", label: "Organic Sessions", axis: "right" },
          {
            key: "organicTrafficValue",
            label: "Organic Traffic Value (USD)",
            axis: "left",
          },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Ahrefs Keyword Position Distribution preset (5-bucket bar).
 * The agency-reporting golden path for keyword movement — every monthly
 * client deck includes a position distribution.
 */
export function expandAhrefsKeywordPositionDistributionPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Keyword Position Distribution",
      config: {
        metrics: [
          { key: "organicKeywordsTop3", label: "Top 3" },
          { key: "organicKeywordsPositions4_10", label: "4-10" },
          { key: "organicKeywordsPositions11_20", label: "11-20" },
          { key: "organicKeywordsPositions21_50", label: "21-50" },
          { key: "organicKeywordsPositions51Plus", label: "51+" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Ahrefs Site Audit Health Score preset (gauge).
 * Per Story 20.2d AC #11 — the technical-SEO headline gauge that defines
 * whether the site is "healthy" or "needs attention" in 1 widget.
 * Threshold band: 0-50 critical / 50-75 needs attention / 75-100 healthy.
 */
export function expandAhrefsSiteAuditHealthScorePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "gauge",
      title: "Site Audit Health Score",
      config: {
        metricKey: "healthScore",
        target: 100,
        thresholds: [
          { value: 50, color: "red" },
          { value: 75, color: "amber" },
          { value: 100, color: "green" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 2,
    },
  ];
}

/**
 * Expand the Ahrefs AI Visibility — Share of Voice preset (gauge).
 * Story 20.2e Brand Radar — 2026's flagship new SEO surface measuring
 * brand presence in ChatGPT/Gemini/Perplexity/Copilot/AI Overviews. No
 * competitor SEO reporting tool surfaces this as a 1-click preset as of
 * May 2026.
 */
export function expandAhrefsAiVisibilitySovPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "gauge",
      title: "AI Visibility — Share of Voice",
      config: {
        metricKey: "shareOfVoice",
        target: 1,
        format: "percentage",
        thresholds: [
          { value: 0.1, color: "red" },
          { value: 0.3, color: "amber" },
          { value: 1, color: "green" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 2,
    },
  ];
}

/**
 * Expand the Ahrefs Web Analytics Overview preset (4 KPIs).
 * Story 20.2f /stats — first-party web analytics from Ahrefs as an
 * alternative to GA4 for clients who can't or won't use Google Analytics.
 */
export function expandAhrefsWebAnalyticsOverviewPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "webAnalyticsPageviews", title: "Pageviews" },
    { metricKey: "webAnalyticsVisitors", title: "Visitors" },
    { metricKey: "webAnalyticsVisits", title: "Visits" },
    { metricKey: "webAnalyticsSessionBounceRate", title: "Bounce Rate" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

/**
 * Expand the Ahrefs Cross-Endpoint KPI Bundle preset (4 KPIs from 4
 * different endpoint families). The C-suite executive scorecard answering
 * "is the agency's SEO investment paying off?" in 4 numbers across 4
 * endpoint families. **No competitor reporting tool surfaces this in 1
 * widget as of May 2026** — ProvenLeap's Ahrefs differentiator.
 */
export function expandAhrefsCrossEndpointKpiBundlePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "domainRating", title: "Domain Rating" },
    { metricKey: "organicTrafficValue", title: "Organic Traffic Value" },
    { metricKey: "shareOfVoice", title: "AI Share of Voice" },
    { metricKey: "healthScore", title: "Site Health Score" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

// ============================================================================
// Story 20.6 — Semrush widget presets (closes Epic 20 Semrush slice)
// ============================================================================
//
// 8 widget presets spanning all 10 `_reportType` discriminators (Story 20.4
// 4 + Story 20.5 6). Mirrors the Ahrefs Story 20.3 preset block structure.
//
// Synthetic-aggregate metrics (`aiOverviewPresenceRate`, `positionsNew`,
// `positionsImproved`, `positionsDeclined`, `positionsLost`) are computed at
// preset-expand time from per-row data already in the catalogue. They're
// declared in `SEMRUSH_METRICS` with `apiField: "_synthetic_aggregate"` and
// listed in `SEMRUSH_SYNTHETIC_METRIC_KEYS` so the widget config UI hides
// them from per-endpoint selectors but exposes them via preset expansions.
// ============================================================================

/**
 * Expand the Semrush Authority Score preset (gauge).
 * THE flagship Semrush widget — every SEO agency dashboard's #1 widget per
 * AgencyAnalytics + DashThis + Whatagraph + Databloo May 2026 templates.
 * Threshold band: 0-30 weak / 30-60 mid / 60-100 strong per Semrush
 * Authority Score 2026 benchmarks.
 */
export function expandSemrushAuthorityScoreGaugePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "gauge",
      title: "Authority Score",
      config: {
        metricKey: "authorityScore",
        target: 100,
        thresholds: [
          { value: 30, color: "red" },
          { value: 60, color: "amber" },
          { value: 100, color: "green" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 2,
    },
  ];
}

/**
 * Expand the Semrush Backlinks Profile Overview preset (4 KPIs).
 * THE backlinks reporting golden path per AgencyAnalytics dashboard May 2026 —
 * 4 KPIs in one row above the trend chart. Note: differs from Ahrefs Story
 * 20.3 preset #2 because Semrush emits `referringIps` as a first-class field
 * whereas Ahrefs surfaces it only as a Site Audit derived.
 */
export function expandSemrushBacklinksProfileOverviewPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "totalBacklinks", title: "Total Backlinks" },
    { metricKey: "referringDomains", title: "Referring Domains" },
    { metricKey: "dofollowBacklinks", title: "Dofollow Backlinks" },
    { metricKey: "referringIps", title: "Referring IPs" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

/**
 * Expand the Semrush Organic Traffic & Cost Trend preset (line chart).
 * THE executive-dashboard signature per Improvado SEO dashboard May 2026 —
 * "we 2× organic-traffic-cost equivalent over 90 days" demo line. CRITICAL
 * divergence from Ahrefs preset #3 — Semrush uses `domain-rank-history`
 * monthly-24-month surface; Ahrefs uses `metrics-history` daily-90-day.
 */
export function expandSemrushOrganicTrafficCostTrendPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Organic Traffic & Cost Trend",
      config: {
        // Catalogue-key binding (Story 20.5 scope-prefix convention):
        // `organicTrafficHistory` / `organicTrafficCostHistory` are the
        // catalogue's scope-prefixed names for the `Or`/`Oc` wire fields
        // when sourced from the `domain_rank_history` endpoint. Renderer
        // resolves catalogue→row keys at render time via category+apiField.
        // Pass 1 review fix: added `reportTypeFilter` so this preset is
        // unambiguously bound to the 24-month history surface (not the
        // single `domain-ranks` snapshot row that also carries
        // `organicTraffic`/`organicTrafficCost` keys).
        metrics: [
          { key: "organicTrafficHistory", label: "Organic Traffic" },
          { key: "organicTrafficCostHistory", label: "Organic Traffic Cost (USD)" },
        ],
        xAxis: "displayDate",
        reportTypeFilter: "domain-rank-history",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Semrush Position Changes Distribution preset (bar chart).
 * THE keyword movement reporting widget — every agency reports keyword
 * position changes on every monthly client deck per Semrush KB-495.
 *
 * Aggregation: the 4 buckets (positionsNew / positionsImproved / positionsDeclined /
 * positionsLost) are SYNTHETIC AGGREGATES computed at preset-expand time
 * from `_reportType: "domain-organic"` rows by inspecting `organicPositionDiff`
 * sign + `previousOrganicPosition === null` cases. Documented as the
 * 4-bucket KB-495 surface.
 */
export function expandSemrushPositionChangesDistributionPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Position Changes",
      config: {
        metrics: [
          { key: "positionsNew", label: "New" },
          { key: "positionsImproved", label: "Improved" },
          { key: "positionsDeclined", label: "Declined" },
          { key: "positionsLost", label: "Lost" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Semrush Top Organic Keywords preset (data table).
 * THE keyword-rank table — every SEO agency dashboard's most-loved widget
 * per AgencyAnalytics 2026 templates. Headline column is `organicPositionDiff`
 * with red ↓ / green ↑ chevron. The `hasAiOverview` boolean column is
 * Story 20.5's 2026 differentiator.
 */
export function expandSemrushTopOrganicKeywordsTablePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Top Organic Keywords",
      config: {
        // Catalogue-key binding per Story 20.5 scope-prefix convention.
        // `organicPosition` / `organicPositionDiff` / `trafficShare` /
        // `hasAiOverview` are emitted directly by `transformDomainOrganicRow`
        // (transformers.ts:393) AND match catalogue keys verbatim.
        // `domainOrganicSearchVolume` is the catalogue scope-prefixed alias
        // for the row-level `searchVolume` field — the renderer resolves
        // catalogue→row keys by category+apiField mapping at render time
        // (the same pattern Story 20.5's metric-allowlist test validates).
        // Pass 1 review fix: added `reportTypeFilter` so the table is
        // unambiguously bound to `domain-organic` rows (not the cross-
        // `_reportType` blend).
        metricKeys: [
          "organicPosition",
          "organicPositionDiff",
          "domainOrganicSearchVolume",
          "trafficShare",
          "hasAiOverview",
        ],
        dimensionKey: "keyword",
        sortBy: "trafficShare",
        sortDirection: "desc",
        limit: 25,
        reportTypeFilter: "domain-organic",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
      autoSize: true,
    },
  ];
}

/**
 * Expand the Semrush Top Organic Competitors preset (data table).
 * THE competitive-landscape widget — answers "who's ranking for the same
 * keywords my client ranks for?" Agency-canonical per Whatagraph 2026
 * templates. Sorted by `competitionRelevance` DESC.
 */
export function expandSemrushTopOrganicCompetitorsTablePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Top Organic Competitors",
      config: {
        metricKeys: [
          "competitionRelevance",
          "commonKeywordsCount",
          "competitorOrganicKeywords",
          "competitorOrganicTraffic",
        ],
        dimensionKey: "competitorDomain",
        sortBy: "competitionRelevance",
        sortDirection: "desc",
        limit: 25,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

/**
 * Expand the Semrush AI Visibility — Mixed Surface preset (4 KPIs).
 * THE 2026 flagship surface — Semrush's mixed-AI-visibility widget that NO
 * COMPETITOR REPORTING TOOL surfaces in 1 click as of May 2026 (per
 * `semrush.com/blog/how-to-measure-ai-share-of-voice/`). Combines Trends API
 * domain-scoped traffic (`aiAssistantsTraffic` + `aiSearchTraffic` +
 * `unknownChannelTraffic`) with keyword-scoped AI Overview presence
 * (`aiOverviewPresenceRate` — synthetic aggregate from `domain-organic` rows).
 *
 * **Trends-API entitlement fallback contract** (Pass 1 Auditor #5 fix —
 * documenting renderer responsibility): preset expansion is
 * entitlement-agnostic by design — `expandPreset` does not have access to
 * the runtime `ctx.metadata`. The renderer reads `metadata.trendsEntitled`
 * at render time and:
 *   - When `metadata.trendsEntitled !== true`: renders the 3 Trends-sourced
 *     KPIs (aiAssistantsTraffic / aiSearchTraffic / unknownChannelTraffic)
 *     as a single "Trends API Required — Upgrade to See AI Traffic" card
 *     spanning all 3 KPI slots. The keyword-scoped `aiOverviewPresenceRate`
 *     KPI (sourced from `domain-organic` rows, NOT Trends-API-gated) still
 *     renders normally.
 *   - When `metadata.trendsEntitled === true`: renders all 4 KPIs.
 *
 * The fixture-mode helper (`loadSemrushFixture`) force-overwrites
 * `metadata.trendsEntitled = true` per Round 4 ADR-D, so demo mode always
 * shows all 4 KPIs. Production renders apply the fallback when the
 * customer's Semrush plan lacks the Trends API add-on.
 *
 * Each KPI carries a `trendsApiGated` flag so the renderer can identify
 * which KPIs to merge into the fallback card.
 */
export function expandSemrushAiVisibilityMixedPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: {
    metricKey: string;
    title: string;
    trendsApiGated: boolean;
  }[] = [
    { metricKey: "aiAssistantsTraffic", title: "AI Assistants Traffic", trendsApiGated: true },
    { metricKey: "aiSearchTraffic", title: "AI Search Traffic", trendsApiGated: true },
    { metricKey: "aiOverviewPresenceRate", title: "AI Overview Presence", trendsApiGated: false },
    { metricKey: "unknownChannelTraffic", title: "Unknown Channel", trendsApiGated: true },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
      // Pass 1 Auditor fix: render-time entitlement gating contract.
      trendsApiGated: kpi.trendsApiGated,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

/**
 * Expand the Semrush Cross-Endpoint KPI Bundle preset (4 KPIs from 4
 * different `_reportType` discriminators). THE C-suite executive scorecard
 * — answers "is the agency's SEO investment paying off?" in 4 numbers
 * across 4 endpoint families (Backlinks Overview + Domain Overview + Trends
 * Summary + Domain Organic). No competitor reporting tool surfaces this in
 * 1 widget as of May 2026.
 */
export function expandSemrushCrossEndpointKpiBundlePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "authorityScore", title: "Authority Score" },
    { metricKey: "organicTrafficCost", title: "Organic Traffic Cost" },
    { metricKey: "aiAssistantsTraffic", title: "AI Assistants Traffic" },
    { metricKey: "positionsImproved", title: "Positions Improved" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

// ────────────────────────────────────────────────────────────────────────
// Story 20.8 — Moz Presets (THIRD SEO entry, closes Epic 20 Moz slice)
// ────────────────────────────────────────────────────────────────────────

/**
 * Expand the Moz Domain Authority Gauge preset (1 gauge widget).
 *
 * THE flagship Moz widget — every SEO agency dashboard's #1 widget since
 * 2010 per AgencyAnalytics + Databox + DashThis + Reliqus 2026 templates.
 * The most-recognized SEO score among non-technical agency clients.
 *
 * Threshold band per linkscope.io/blog/domain-authority-ranking-benchmarks
 * verified 2026-05-11:
 *   0-20 low / 20-50 moderate / 50-70 competitive / 70-100 very strong.
 */
export function expandMozDomainAuthorityGaugePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "gauge",
      title: "Domain Authority",
      config: {
        metricKey: "domainAuthority",
        target: 100,
        // Threshold band per linkscope 2026 + AC #10: 0-20 low (red) /
        // 20-50 moderate (amber) / 50-70 competitive (green) / 70-100
        // very-strong (dark-green). The renderer treats each threshold's
        // `value` as the UPPER BOUND of its color band.
        //
        // Code Review Pass 2 AA2-2 — restored the 4th band ("very-strong")
        // per spec AC #10. The Pass 1 BH-15 collapse to 3 thresholds
        // dropped the semantic distinction the demo narrative depends on.
        thresholds: [
          { value: 20, color: "red" },
          { value: 50, color: "amber" },
          { value: 70, color: "green" },
          { value: 100, color: "dark-green" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 2,
    },
  ];
}

/**
 * Expand the Moz Page Authority Gauge preset (1 gauge widget).
 *
 * The page-level companion gauge that pairs with the DA gauge in agency
 * reporting. PA is page-scoped; the homepage of a domain typically has
 * PA = DA × 0.7-0.85 per `dashthis.com/kpi-examples/domain-authority`.
 */
export function expandMozPageAuthorityGaugePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "gauge",
      title: "Page Authority",
      config: {
        metricKey: "pageAuthority",
        target: 100,
        // Threshold band per spec AC #10: 0-20 low / 20-40 moderate /
        // 40-60 competitive / 60-100 very-strong. Pass 2 AA2-2 — 4 bands.
        thresholds: [
          { value: 20, color: "red" },
          { value: 40, color: "amber" },
          { value: 60, color: "green" },
          { value: 100, color: "dark-green" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 2,
    },
  ];
}

/**
 * Expand the Moz Brand Authority Gauge preset (1 gauge widget).
 *
 * THE 2026 flagship surface — Moz's brand-recognition signal that NO
 * competitor (Ahrefs, Semrush) surfaces in their API as of May 2026. Per
 * searchengineland.com/moz-debuts-brand-authority-metric-430325 verified
 * 2026-05-11 — Brand Authority captures branded-mention quality whereas
 * DA captures generic link strength.
 *
 * Threshold band:
 *   0-25 unknown / 25-50 emerging / 50-75 established / 75-100 household-name.
 *
 * `subscription_required` fallback: the renderer respects
 * `ctx.metadata.brandAuthorityEntitled !== true` to show the "upgrade Moz API
 * to ≥ $20/mo plan" badge. In demo mode AC #1 stamps
 * `brandAuthorityEntitled: true` so the gauge demos with the populated
 * state (BA 42 for GreenLife).
 */
export function expandMozBrandAuthorityGaugePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "gauge",
      title: "Brand Authority",
      config: {
        metricKey: "brandAuthorityScore",
        target: 100,
        // Threshold band per spec AC #10: 0-25 unknown / 25-50 emerging /
        // 50-75 established / 75-100 household-name. Pass 2 AA2-2 — 4 bands.
        thresholds: [
          { value: 25, color: "red" },
          { value: 50, color: "amber" },
          { value: 75, color: "green" },
          { value: 100, color: "dark-green" },
        ],
        // The renderer reads this entitlement key off the integration's
        // metadata to decide whether to render the value or the upgrade
        // badge. Story 20.8 fixture-mode helper forces this to TRUE in
        // demo mode (Round 4 ADR-D).
        requiresEntitlement: "brandAuthorityEntitled",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 2,
    },
  ];
}

/**
 * Expand the Moz Backlink Profile Overview preset (4 KPIs).
 *
 * THE backlinks reporting golden path per AgencyAnalytics + Databox 2026
 * templates — 4 KPIs in one row showing total inbound link strength:
 *   pagesToPage (18.5K) / externalPagesToPage (11.5K) /
 *   rootDomainsToPage (1.24K) / nofollowRootDomainsToPage (320).
 *
 * Note: divergent from Ahrefs Story 20.3 preset #2 (`liveBacklinks` +
 * `liveReferringDomains`) and Semrush Story 20.6 preset #2 (`totalBacklinks`
 * + `referringDomains`) because Moz emits `pagesToPage` and
 * `externalPagesToPage` as first-class fields whereas Ahrefs/Semrush
 * surface these as derived.
 */
export function expandMozBacklinkProfileOverviewPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "pagesToPage", title: "Inbound Pages" },
    { metricKey: "externalPagesToPage", title: "External Inbound Pages" },
    { metricKey: "rootDomainsToPage", title: "Linking Root Domains" },
    {
      metricKey: "nofollowRootDomainsToPage",
      title: "Nofollow Root Domains",
    },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

/**
 * Expand the Moz Top Pages preset (1 data-table widget bound to
 * `_reportType: "top-pages"` rows).
 *
 * THE page-level performance table — every Moz agency dashboard's most-loved
 * data-table widget per AgencyAnalytics 2026 templates. Top 50 pages by
 * Page Authority.
 */
export function expandMozTopPagesTablePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Top Pages (by Page Authority)",
      config: {
        metricKeys: [
          "topPagePageUrl",
          "topPageTitle",
          "topPagePageAuthority",
          "topPagePagesToPage",
          "topPageHttpCode",
          "topPageLastCrawled",
        ],
        reportTypeFilter: "top-pages",
        sortBy: "topPagePageAuthority",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
      autoSize: true,
    },
  ];
}

/**
 * Expand the Moz Top Linking Root Domains preset (1 data-table widget bound
 * to `_reportType: "linking-root-domains"` rows).
 *
 * THE referring-domain authority table — answers "who links to my client,
 * and how authoritative are they?" per Databox 2026 Moz templates.
 */
export function expandMozTopLinkingRootDomainsTablePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Top Linking Root Domains (by Source DA)",
      config: {
        metricKeys: [
          "linkingRootDomain",
          "sourceDomainAuthority",
          "linkerLinkPropensity",
          "linkerSpamScore",
          "nofollowPagesPerLinker",
          "redirectPagesPerLinker",
        ],
        reportTypeFilter: "linking-root-domains",
        sortBy: "sourceDomainAuthority",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

/**
 * Expand the Moz Domain Authority Trend preset (1 line-chart widget).
 *
 * THE BUILD-TO-SELL PAYOFF — Moz V3 has NO time-series API; this widget
 * is impossible without Story 20.7's `moz_authority_snapshots` D1 persistence
 * layer. The renderer queries the D1 table directly via the `dataSource`
 * config block, ordering by capturedAt ASC and limiting to 12 monthly
 * snapshots.
 *
 * **Empty-state handling**: if `moz_authority_snapshots` query returns 0
 * rows (new tenant, no snapshots yet), the widget renders "Domain Authority
 * history coming soon — your first snapshot will appear next month after
 * the Moz Index refreshes" per Story 20.7 graceful-degradation pattern.
 *
 * Demo workspace seeds 6 monthly snapshots (Dec-2025 DA=47 → May-2026
 * DA=50) per AC #8 so the widget renders 6 data points immediately.
 */
export function expandMozDaTrendPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Domain Authority Trend (6-month)",
      config: {
        dimensionKey: "capturedAt",
        metrics: [
          {
            key: "domainAuthority",
            label: "Domain Authority",
            axis: "left",
          },
        ],
        // Story 20.8 build-to-sell payoff: the chart reads from the
        // ProvenLeap-persisted `moz_authority_snapshots` D1 table, not from
        // Moz wire data. Story 20.9 cross-SEO-tool comparison extends this
        // to a 3-line chart (DA + DR + Authority Score over time).
        //
        // Code Review Pass 1 AA-3 — the renderer resolves `tenantId` and
        // `integrationId` from the session/widget context at runtime via
        // `integrationPlatform` + `integrationId` below; the explicit
        // `filter` block in the spec is encoded by those two fields. The
        // `scope` filter narrows to the agency-default scope.
        //
        // Code Review Pass 2 BH2-8 — `scope` literal imported from
        // `@provenleap/integrations` (`MOZ_DEMO_SNAPSHOT_SCOPE`) so the
        // chart's filter and the seed's INSERT cannot drift. Pass 2 EH2-7
        // — removed duplicate top-level `scope` key; the filter block
        // is the single source of truth for the renderer.
        dataSource: {
          kind: "d1",
          table: "moz_authority_snapshots",
          columns: ["captured_at", "domain_authority"],
          filter: {
            // tenantId + integrationId are resolved from
            // integrationPlatform + integrationId by the renderer.
            //
            // Code Review Pass 3 BH3-3 — `target` was missing from the
            // filter. The `moz_authority_snapshots` PK includes target;
            // a single Moz integration can persist snapshots for multiple
            // targets, so filtering by just (tenant, integration, scope)
            // produces an interleaved chart across targets. The demo
            // workspace uses `MOZ_DEMO_SNAPSHOT_TARGET` exclusively.
            target: MOZ_DEMO_SNAPSHOT_TARGET,
            scope: MOZ_DEMO_SNAPSHOT_SCOPE,
          },
          orderBy: "captured_at ASC",
          limit: 12,
        },
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Moz Cross-Endpoint Scorecard preset (4 KPIs across 4 surfaces).
 *
 * THE Moz-specific C-suite scorecard — answers "is GreenLife's Moz
 * authority growing?" in 4 numbers across all 4 endpoint surfaces + 1
 * synthetic trend signal:
 *   - DA (50) — site-metrics surface
 *   - BA (42) — brand-authority surface (2026 flagship metric)
 *   - Linking Root Domains (1240) — site-metrics surface
 *   - DA Trend Direction (▲ +3 over 6 months) — synthetic aggregate from
 *     `moz_authority_snapshots`
 *
 * Note: the equivalent widgets in Ahrefs Story 20.3 (DR + Organic Traffic
 * Value + AI SoV + Health Score) and Semrush Story 20.6 (AS + Traffic Cost
 * + AI Assistants + Position Δ) surface different KPIs because each tool
 * has different headline metrics.
 */
export function expandMozCrossEndpointScorecardPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  // Code Review Pass 2 BH2-19 — `daTrendDirection` is a synthetic-aggregate
  // metric whose value is computed at preset-expand time from
  // `moz_authority_snapshots` D1 rows (NOT from `records[]`). Attach a
  // `dataSource: { kind: "d1", ... }` config so the renderer queries the
  // snapshot table directly and computes `currentDA - oldestDA`. The other
  // 3 KPIs (DA / BA / rootDomainsToPage) read from records[] as normal.
  interface MozScorecardKpi {
    metricKey: string;
    title: string;
    dataSource?: Record<string, unknown>;
  }
  const kpis: MozScorecardKpi[] = [
    { metricKey: "domainAuthority", title: "Domain Authority" },
    { metricKey: "brandAuthorityScore", title: "Brand Authority" },
    { metricKey: "rootDomainsToPage", title: "Linking Root Domains" },
    {
      metricKey: "daTrendDirection",
      title: "DA Trend (6mo)",
      dataSource: {
        kind: "d1",
        table: "moz_authority_snapshots",
        // Aggregate hint — the renderer should compute
        // `currentDA - oldestDA` over the result set. `columns` is the
        // raw D1 column the aggregator reads; `aggregate` names the
        // synthetic field expected by the metric definition.
        columns: ["captured_at", "domain_authority"],
        filter: {
          // Code Review Pass 3 BH3-3 — `target` must filter by demo
          // target to avoid mixing rows across targets when an
          // integration has multiple persisted targets.
          target: MOZ_DEMO_SNAPSHOT_TARGET,
          scope: MOZ_DEMO_SNAPSHOT_SCOPE,
        },
        orderBy: "captured_at ASC",
        limit: 12,
        aggregate: "daTrendDirection",
      },
    },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
      ...(kpi.dataSource ? { dataSource: kpi.dataSource } : {}),
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

// ============================================================================
// Story 20.9 — SEO Cross-Tool Presets (Epic 20 cherry-on-top)
//
// 3 widget presets that join Ahrefs + Semrush + Moz authority data into a
// single side-by-side comparison view. The category-defining ProvenLeap
// differentiator (no competitor reporting tool surfaces this — joining 3
// different time-series surfaces requires the Moz V3 snapshot persistence
// layer ProvenLeap shipped in Stories 20.7+20.8 PLUS the parallel
// Ahrefs+Semrush snapshot layers shipped in this story).
//
// ## How the renderer resolves the 3 integrationIds
//
// Per-tool presets (Stories 20.3 / 20.6 / 20.8) take a single
// `integrationId` parameter for their single underlying platform.
// Cross-tool presets are different — they reference 3 platforms but the
// existing `expandPreset(presetType, platform, integrationId)` dispatcher
// signature can only pass one ID through. The first cross-tool preset
// family resolves this by:
//
// 1. The `integrationId` parameter here is treated as the **Ahrefs**
//    integration ID (because the widget palette section's runtime gate
//    requires all 3 SEO tools connected, the caller already knows the
//    user has all 3; the dispatcher just needs to ship 1 ID through and
//    let the renderer derive the other 2).
// 2. The dataSource block uses sentinel `"resolve:semrush"` /
//    `"resolve:moz"` placeholders for the other 2 integration IDs. The
//    render layer (server-side widget-populate) resolves these by
//    looking up the tenant's integration rows for `platform = "semrush"`
//    and `platform = "moz"` respectively at fetch time. Sentinel format
//    is forward-compatible — any future cross-platform preset family
//    can register a new resolver.
//
// This avoids invasive surgery on the ~5000-line dispatcher while
// keeping the cross-tool preset functions self-contained.
// ============================================================================

/** Sentinel string the renderer interprets as "look up the integration ID
 *  for this platform from the current tenant's connected integrations".
 *  Story 20.9 — first use of this pattern; future cross-platform presets
 *  may extend the sentinel space. */
export const CROSS_TOOL_RESOLVE_SENTINEL_AHREFS = "resolve:ahrefs" as const;
export const CROSS_TOOL_RESOLVE_SENTINEL_SEMRUSH = "resolve:semrush" as const;
export const CROSS_TOOL_RESOLVE_SENTINEL_MOZ = "resolve:moz" as const;
// Story 23.7 — Reviews Cross-Platform sentinels. Extends the SEO Cross-Tool
// sentinel space (`resolve:<platform>`) for the Reviews Cross-Platform
// preset family. Google Reviews integrationId is passed as the function
// argument; Trustpilot + Yelp are resolved via these sentinels.
export const CROSS_TOOL_RESOLVE_SENTINEL_TRUSTPILOT = "resolve:trustpilot" as const;
export const CROSS_TOOL_RESOLVE_SENTINEL_YELP = "resolve:yelp" as const;

/**
 * Expand the SEO Authority Scoreboard preset (Story 20.9 AC #4).
 *
 * Returns exactly 3 KPI widgets side-by-side rendering the 3 industry-
 * standard authority scores (Ahrefs DR + Semrush AS + Moz DA) — the
 * agency-canonical "what's our authority?" answer in one widget.
 *
 * `integrationId` parameter is treated as the Ahrefs integration ID; the
 * Semrush + Moz integration IDs are resolved by the renderer via the
 * `resolve:<platform>` sentinel pattern documented above.
 *
 * `_platform` is the dispatcher's `platform` argument from `expandPreset`,
 * carried for signature uniformity but intentionally NOT bound — the
 * cross-tool widget renders 3 platforms simultaneously, so a single
 * platform label is meaningless. Round 1 Code Review H3-Edge addressed
 * the misleading-parameter concern by adding this explicit docblock.
 *
 * Build-to-sell rationale: this is the agency client's most-asked SEO
 * question per the May 2026 industry-reporting review of
 * agencyanalytics.com/templates/dashboards/seo-dashboard +
 * whatagraph.com/blog/articles/seo-dashboard-examples. No competitor
 * reporting tool surfaces all 3 scores in 1 widget.
 */
export function expandSeoCrossToolAuthorityScoreboardPreset(
  _platform: string,
  ahrefsIntegrationId: string,
): PresetWidget[] {
  // GreenLife demo baselines per AC #4 — DR=56 > AS=52 > DA=50.
  // Other clients render whatever each tool's records[] contains for
  // domainRating / authorityScore / domainAuthority.
  interface CrossToolKpi {
    metricKey: string;
    title: string;
    integrationPlatform: "ahrefs" | "semrush" | "moz";
    integrationId: string;
    color: string;
  }
  const kpis: CrossToolKpi[] = [
    {
      metricKey: "domainRating",
      title: SEO_TOOL_DISPLAY_NAMES.ahrefs,
      integrationPlatform: "ahrefs",
      integrationId: ahrefsIntegrationId,
      color: SEO_TOOL_BRAND_COLORS.ahrefs,
    },
    {
      metricKey: "authorityScore",
      title: SEO_TOOL_DISPLAY_NAMES.semrush,
      integrationPlatform: "semrush",
      integrationId: CROSS_TOOL_RESOLVE_SENTINEL_SEMRUSH,
      color: SEO_TOOL_BRAND_COLORS.semrush,
    },
    {
      metricKey: "domainAuthority",
      title: SEO_TOOL_DISPLAY_NAMES.moz,
      integrationPlatform: "moz",
      integrationId: CROSS_TOOL_RESOLVE_SENTINEL_MOZ,
      color: SEO_TOOL_BRAND_COLORS.moz,
    },
  ];
  // Round 2 Code Review Auditor #4 fix — prepend a top-level heading
  // widget "SEO Authority Scoreboard" so the scoreboard composition
  // (heading + 3 KPIs) matches AC #4's "top-level widget heading;
  // rendered above the 3 KPIs" requirement. The heading is colSpan: 12
  // (full row) above the 3-by-4-col KPI row that follows.
  const headingWidget: PresetWidget = {
    id: crypto.randomUUID(),
    type: "heading",
    title: "SEO Authority Scoreboard",
    config: {
      content: "SEO Authority Scoreboard",
    },
    colSpan: 12,
    rowSpan: 1,
  };
  const kpiWidgets: PresetWidget[] = kpis.map((kpi, idx) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: kpi.integrationPlatform,
      integrationId: kpi.integrationId,
      color: kpi.color,
      // Render the calibration footnote below the FIRST KPI per AC #4
      // (one footnote per scoreboard, not 3 — duplicate footnotes would
      // crowd the layout per Round 1 UX review).
      ...(idx === 0
        ? { footnote: SEO_CROSS_TOOL_CALIBRATION_FOOTNOTE }
        : {}),
    },
    // 3 KPIs side-by-side at colSpan 4 each = 12-column full-row width.
    colSpan: 4,
    rowSpan: 1,
  }));
  return [headingWidget, ...kpiWidgets];
}

/**
 * Expand the SEO Authority Bar Comparison preset (Story 20.9 AC #5).
 *
 * Returns 1 bar-chart widget with 3 bars on the same 0-100 y-axis.
 * The visual punchline for "your authority across all 3 industry tools" —
 * agency client sees the calibration divergence (DR 56 / AS 52 / DA 50
 * for GreenLife) in one glance.
 *
 * NO normalization, NO log-scale, NO calibration-adjusted values. The
 * visual divergence IS the demo punchline per AC #5 bar-height-invariant.
 */
export function expandSeoCrossToolAuthorityBarComparisonPreset(
  _platform: string,
  ahrefsIntegrationId: string,
): PresetWidget[] {
  // Round 2 Code Review M-R2-2 fix — renderer contract clarification.
  //
  // The bar-chart widget config declares `metrics: [{ key: "score" }]` —
  // a synthetic key that doesn't appear in `METRIC_PLATFORM_MAP` or any
  // adapter's records[]. The cross-SEO renderer (deferred to a future
  // IMC story per AC #7 scope boundary) is expected to materialize the
  // 3-row synthetic array at fetch time by:
  //   1. Reading each `sources[i].metricKey` (`domainRating` /
  //      `authorityScore` / `domainAuthority`) from its respective
  //      per-tool records[] (fetched via the resolved integrationId).
  //   2. Composing a synthetic row per source: { platform, score, displayLabel }.
  //   3. Returning the 3-row array as `records` keyed by `dimensionKey: "platform"`
  //      with `metrics[0].key = "score"` as the y-axis value column.
  // Until the cross-SEO renderer ships, widget-populate.ts emits an
  // informative demo-only envelope (see Round 2 C1 fix at
  // apps/server/src/services/widget-populate.ts).
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "SEO Authority Bar Comparison",
      config: {
        dimensionKey: "platform",
        metrics: [
          { key: "score", label: "Authority Score (0-100)", axis: "left" },
        ],
        yAxisMin: 0,
        yAxisMax: 100,
        // Story 20.9 AC #5 — synthetic-aggregate dataSource. Renderer
        // JOINs the 3 per-tool records[] into a 3-row array at
        // widget-expand-time. Each row carries `{ platform, score,
        // displayLabel }`. Sentinel resolution pattern documented at top
        // of this section.
        dataSource: {
          kind: "synthetic-aggregate",
          aggregateKey: "seoAuthorityScoreboard",
          sources: [
            {
              platform: "ahrefs",
              integrationId: ahrefsIntegrationId,
              metricKey: "domainRating",
              displayLabel: SEO_TOOL_DISPLAY_NAMES.ahrefs,
              color: SEO_TOOL_BRAND_COLORS.ahrefs,
            },
            {
              platform: "semrush",
              integrationId: CROSS_TOOL_RESOLVE_SENTINEL_SEMRUSH,
              metricKey: "authorityScore",
              displayLabel: SEO_TOOL_DISPLAY_NAMES.semrush,
              color: SEO_TOOL_BRAND_COLORS.semrush,
            },
            {
              platform: "moz",
              integrationId: CROSS_TOOL_RESOLVE_SENTINEL_MOZ,
              metricKey: "domainAuthority",
              displayLabel: SEO_TOOL_DISPLAY_NAMES.moz,
              color: SEO_TOOL_BRAND_COLORS.moz,
            },
          ],
        },
        calibrationFootnote: SEO_CROSS_TOOL_CALIBRATION_FOOTNOTE,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the SEO Authority 6-Month Trend preset (Story 20.9 AC #6) —
 * **the build-to-sell category-defining widget**.
 *
 * Returns 1 line-chart widget with 3 lines on the same 0-100 y-axis over
 * a 6-month range (Dec-2025 → May-2026 in the demo workspace; up to 12
 * months of history in production).
 *
 * Reads from 3 D1 snapshot tables (`ahrefs_authority_snapshots` +
 * `semrush_authority_snapshots` + `moz_authority_snapshots`) joined on
 * monthly bucket. No competitor reporting tool surfaces this because
 * joining 3 different time-series surfaces requires the Moz V3
 * has-no-time-series-API workaround (ProvenLeap snapshot persistence
 * layer shipped in Story 20.7+20.8 + this story).
 *
 * Graceful-degrade invariant (AC #6 + Round 2 Pre-mortem C): if one
 * platform returns 0 rows, the renderer renders the other 2 lines with
 * an inline warning chip "{Tool} history unavailable — connect API + run
 * sync".
 */
export function expandSeoCrossToolAuthorityTrendPreset(
  _platform: string,
  ahrefsIntegrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "SEO Authority 6-Month Trend",
      config: {
        dimensionKey: "capturedAt",
        metrics: [
          {
            key: "ahrefsDR",
            label: SEO_TOOL_DISPLAY_NAMES.ahrefs,
            axis: "left",
            color: SEO_TOOL_BRAND_COLORS.ahrefs,
          },
          {
            key: "semrushAS",
            label: SEO_TOOL_DISPLAY_NAMES.semrush,
            axis: "left",
            color: SEO_TOOL_BRAND_COLORS.semrush,
          },
          {
            key: "mozDA",
            label: SEO_TOOL_DISPLAY_NAMES.moz,
            axis: "left",
            color: SEO_TOOL_BRAND_COLORS.moz,
          },
        ],
        yAxisMin: 0,
        yAxisMax: 100,
        // Story 20.9 AC #6 — synthetic-aggregate D1 JOIN data source.
        // Renderer joins the 3 snapshot tables on monthly bucket
        // (`capturedAt` = month-15 across all 3 tables).
        dataSource: {
          kind: "synthetic-aggregate-d1-join",
          aggregateKey: "seoAuthorityTrend3Tool",
          joinKey: "capturedAt",
          sources: [
            {
              platform: "ahrefs",
              integrationId: ahrefsIntegrationId,
              table: "ahrefs_authority_snapshots",
              valueColumn: "domain_rating",
              dateColumn: "index_captured_at",
              filter: { target: AHREFS_DEMO_SNAPSHOT_TARGET },
            },
            {
              platform: "semrush",
              integrationId: CROSS_TOOL_RESOLVE_SENTINEL_SEMRUSH,
              table: "semrush_authority_snapshots",
              valueColumn: "authority_score",
              dateColumn: "display_date",
              filter: { target: SEMRUSH_DEMO_SNAPSHOT_TARGET },
            },
            {
              platform: "moz",
              integrationId: CROSS_TOOL_RESOLVE_SENTINEL_MOZ,
              table: "moz_authority_snapshots",
              valueColumn: "domain_authority",
              dateColumn: "captured_at",
              filter: {
                target: MOZ_DEMO_SNAPSHOT_TARGET,
                scope: MOZ_DEMO_SNAPSHOT_SCOPE,
              },
            },
          ],
          orderBy: "capturedAt ASC",
          limit: 12,
        },
        calibrationFootnote: SEO_CROSS_TOOL_CALIBRATION_FOOTNOTE,
        // Round 4 Pre-mortem C / AC #6 — if one platform returns 0 rows,
        // render the other 2 with inline warning chip.
        emptyStateBehavior: "graceful-degrade",
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

// ============================================================================
// REVIEWS CROSS-PLATFORM PRESETS (Story 23.7 — Epic 23 cherry-on-top)
// ============================================================================
//
// Architectural template: mirrors the SEO Cross-Tool family above. Same
// `resolve:<platform>` sentinel pattern for the OTHER 2 platforms (Trustpilot
// + Yelp); Google Reviews integrationId is passed as the function argument
// per Story 20.9's "ONE platform as parameter; OTHERS as sentinels"
// convention.
//
// Build-to-sell rationale (verified May 2026):
//   No competitor reporting tool surfaces a unified 3-platform reputation
//   widget joining Google Reviews + Trustpilot + Yelp. Per the May 2026
//   competitive scan against AgencyAnalytics + Whatagraph + Databox +
//   Birdeye + Podium + Reputation.com — each ships per-platform widgets but
//   none ship cross-platform velocity-over-time joined in one widget.
//   Preset #3 (`reviews-cross-platform-review-velocity-trend`) is the
//   category-defining differentiator.
//
// Per-platform metric keys consumed:
//   - Google Reviews: `averageRating` (KPI) + `reviewVelocityPerMonth`
//     (velocity) + `positiveReviewCount`/`neutralReviewCount`/
//     `negativeReviewCount` (sentiment)
//   - Trustpilot: `trustScore` (KPI) + `reviewVelocityPerMonth` +
//     `positiveReviewCount`/`neutralReviewCount`/`negativeReviewCount`
//   - Yelp: `businessRating` (KPI) + `reviewVelocityPerMonth` +
//     `positiveExcerptCount`/`neutralExcerptCount`/`negativeExcerptCount`
//
// Calibration: all 3 platforms use 0-5 decimal scale → no normalization,
// no log-scale, no calibration-adjusted values. Bar heights are directly
// comparable. The lightweight calibration footnote
// (`REVIEWS_CROSS_PLATFORM_CALIBRATION_FOOTNOTE`) only discloses the Yelp
// 3-excerpt cap so consumers don't misinterpret Yelp's review-count surface.

/**
 * Expand the Reviews Cross-Platform Unified Scoreboard preset (Story 23.7 AC #4).
 *
 * Returns 4 widgets: 1 heading widget "Unified Reputation Scoreboard"
 * (`colSpan: 12, rowSpan: 1`) + 3 KPI widgets side-by-side
 * (`colSpan: 4, rowSpan: 1` each), rendering the 3 industry-standard review
 * platform ratings — the agency-canonical "what's our reputation across all
 * platforms?" answer in one widget.
 *
 * `googleReviewsIntegrationId` parameter is treated as the Google Reviews
 * integration ID; the Trustpilot + Yelp integration IDs are resolved by the
 * renderer via the `resolve:<platform>` sentinel pattern. Mirrors Story
 * 20.9's `expandSeoCrossToolAuthorityScoreboardPreset` convention
 * (ONE platform as parameter; OTHERS as sentinels).
 *
 * `_platform` is the dispatcher's `platform` argument from `expandPreset`,
 * carried for signature uniformity per Round 1 Code Review H3-Edge
 * precedent (Story 20.9). Intentionally NOT bound — the cross-platform
 * widget renders 3 platforms simultaneously, so a single platform label is
 * meaningless.
 *
 * Build-to-sell rationale: the agency client's most-asked reputation
 * question per the May 2026 competitive scan
 * (agencyanalytics.com/solutions/reputation-management-reporting +
 * whatagraph.com/reviews/agencyanalytics + birdeye.com/blog/birdeye-vs-podium-comparison
 * — none ship a 3-platform unified scoreboard with directly-comparable bars).
 */
export function expandReviewsCrossPlatformUnifiedScoreboardPreset(
  _platform: string,
  googleReviewsIntegrationId: string,
): PresetWidget[] {
  interface CrossPlatformKpi {
    metricKey: string;
    title: string;
    integrationPlatform: "google-reviews" | "trustpilot" | "yelp";
    integrationId: string;
    color: string;
  }
  // Per-platform headline rating metric keys (AC #4) — each platform's own
  // canonical 0-5 decimal rating field on its records[].
  const kpis: CrossPlatformKpi[] = [
    {
      metricKey: "averageRating",
      title: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES["google-reviews"],
      integrationPlatform: "google-reviews",
      integrationId: googleReviewsIntegrationId,
      color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS["google-reviews"],
    },
    {
      metricKey: "trustScore",
      title: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.trustpilot,
      integrationPlatform: "trustpilot",
      integrationId: CROSS_TOOL_RESOLVE_SENTINEL_TRUSTPILOT,
      color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS.trustpilot,
    },
    {
      metricKey: "businessRating",
      title: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.yelp,
      integrationPlatform: "yelp",
      integrationId: CROSS_TOOL_RESOLVE_SENTINEL_YELP,
      color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS.yelp,
    },
  ];
  // Heading widget per AC #4 — colSpan: 12 (full row) above the 3-KPI row.
  const headingWidget: PresetWidget = {
    id: crypto.randomUUID(),
    type: "heading",
    title: "Unified Reputation Scoreboard",
    config: {
      content: "Unified Reputation Scoreboard",
    },
    colSpan: 12,
    rowSpan: 1,
  };
  const kpiWidgets: PresetWidget[] = kpis.map((kpi, idx) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: kpi.integrationPlatform,
      integrationId: kpi.integrationId,
      color: kpi.color,
      // Footnote on FIRST KPI only per AC #4 / Story 20.9 layout precedent.
      ...(idx === 0
        ? { footnote: REVIEWS_CROSS_PLATFORM_CALIBRATION_FOOTNOTE }
        : {}),
    },
    // 3 KPIs side-by-side at colSpan 4 each = 12-column full-row width.
    colSpan: 4,
    rowSpan: 1,
  }));
  return [headingWidget, ...kpiWidgets];
}

/**
 * Expand the Reviews Cross-Platform Rating Bar Comparison preset (Story 23.7 AC #4).
 *
 * Returns 1 bar-chart widget (`colSpan: 12, rowSpan: 3`) with 3 bars on the
 * same 0-5 y-axis. The visual punchline for "your reputation across all 3
 * review platforms" — agency client sees the per-platform divergence in one
 * glance (e.g., GreenLife: Trustpilot 4.5 > Yelp 4.4 > Google 4.4 — the 0.1
 * spread tells a story about which platform's reviewer-pool is friendliest).
 *
 * NO log-scale, NO normalization — the visual divergence IS the demo
 * punchline per AC #4 bar-height-invariant. All 3 platforms use 0-5
 * decimal so values are directly comparable.
 *
 * Bar order: Google → Trustpilot → Yelp (alphabetical / deterministic per
 * Round 5 Edge Case Hunter), NOT highest-to-lowest. The order must be
 * stable regardless of which platform has the higher rating.
 *
 * Empty-state (Round 5 Edge Case Hunter — when all 3 platforms have 0
 * reviews): the explicit `yAxisMin: 0, yAxisMax: 5` ensures the chart
 * renders 3 zero-height bars rather than a NaN axis.
 */
export function expandReviewsCrossPlatformRatingBarComparisonPreset(
  _platform: string,
  googleReviewsIntegrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Cross-Platform Rating Bar Comparison",
      config: {
        dimensionKey: "platform",
        metrics: [
          { key: "rating", label: "Rating (0-5)", axis: "left" },
        ],
        // Explicit y-axis range (Round 5 Edge Case Hunter — handles the
        // empty-state where all 3 ratings are 0).
        yAxisMin: 0,
        yAxisMax: 5,
        // AC #4 — synthetic-aggregate dataSource. Renderer JOINs the 3
        // per-platform records[] into a 3-row array at widget-expand-time.
        // Sentinel resolution pattern documented at top of this section.
        dataSource: {
          kind: "synthetic-aggregate",
          // Round 2 Code Review Blind Hunter Finding R2-BH-19 fix —
          // unique aggregateKey per preset (was sharing
          // `reviewsUnifiedReputationScoreboard` with Preset #1's KPI
          // scoreboard). The convention is one aggregateKey per preset
          // for routing + future production-path-resolver disambiguation;
          // Preset #2 is a chart-shape variant of the same metric, so
          // the naming `reviewsUnifiedRatingBarComparison` is distinct
          // from the KPI scoreboard's `reviewsUnifiedReputationScoreboard`.
          aggregateKey: "reviewsUnifiedRatingBarComparison",
          sources: [
            {
              platform: "google-reviews",
              integrationId: googleReviewsIntegrationId,
              metricKey: "averageRating",
              displayLabel:
                REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES["google-reviews"],
              color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS["google-reviews"],
            },
            {
              platform: "trustpilot",
              integrationId: CROSS_TOOL_RESOLVE_SENTINEL_TRUSTPILOT,
              metricKey: "trustScore",
              displayLabel: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.trustpilot,
              color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS.trustpilot,
            },
            {
              platform: "yelp",
              integrationId: CROSS_TOOL_RESOLVE_SENTINEL_YELP,
              metricKey: "businessRating",
              displayLabel: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.yelp,
              color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS.yelp,
            },
          ],
        },
        calibrationFootnote: REVIEWS_CROSS_PLATFORM_CALIBRATION_FOOTNOTE,
        // Round 1 Code Review Edge Case Hunter Finding #8 fix — align
        // empty-state UX across all 3 chart presets in this family.
        // Without this, the bar chart renders 3 zero-height bars when
        // all 3 platforms have 0 reviews (loading or unconnected state).
        emptyStateMessage:
          "Connect data to populate cross-platform reputation comparison",
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Reviews Cross-Platform Review Velocity Trend preset
 * (Story 23.7 AC #4) — **the build-to-sell category-defining widget**.
 *
 * Returns 1 line-chart widget (`colSpan: 12, rowSpan: 3`) with 3 series
 * (Google + Trustpilot + Yelp `reviewVelocityPerMonth` over a fixed 6-month
 * window). NO date-range picker — the velocity is always rolling 6 months
 * per AC #4.
 *
 * Joins 3 different review platforms' velocity-over-time signals in a
 * single line chart — verified May 2026, no competitor reporting tool
 * surfaces this widget. The architectural moat: Stories 23.2 + 23.4 + 23.6
 * each shipped `reviewVelocityPerMonth` as a synthetic-aggregate; Story
 * 23.7 cashes in the join at preset-expand time.
 *
 * Per-platform metric keys consumed: `reviewVelocityPerMonth` (identical
 * key name across all 3 adapters by design — Stories 23.2/23.4/23.6
 * coordinated the naming).
 */
export function expandReviewsCrossPlatformReviewVelocityTrendPreset(
  _platform: string,
  googleReviewsIntegrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Cross-Platform Review Velocity Trend",
      config: {
        dimensionKey: "monthLabel",
        // Round 1 Code Review Blind Hunter Finding fix — series keys are
        // joined-row COLUMN names that the future renderer materializes
        // from each platform's reviewVelocityPerMonth synthetic-aggregate
        // (mirrors Story 20.9's `ahrefsDR` / `semrushAS` / `mozDA`
        // pattern at widgetPresets.ts:3782+). NOT platform slugs (which
        // would not exist as record columns).
        metrics: [
          {
            key: "googleReviewsVelocity",
            label: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES["google-reviews"],
            axis: "left",
            color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS["google-reviews"],
          },
          {
            key: "trustpilotVelocity",
            label: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.trustpilot,
            axis: "left",
            color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS.trustpilot,
          },
          {
            key: "yelpVelocity",
            label: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.yelp,
            axis: "left",
            color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS.yelp,
          },
        ],
        // AC #4 — synthetic-aggregate dataSource over each platform's
        // reviewVelocityPerMonth metric. Renderer materializes a 6-month
        // rolling window joining the 3 series on `monthLabel`.
        dataSource: {
          kind: "synthetic-aggregate",
          aggregateKey: "reviewsUnifiedReviewVelocityTrend",
          windowMonths: 6,
          sources: [
            {
              platform: "google-reviews",
              integrationId: googleReviewsIntegrationId,
              metricKey: "reviewVelocityPerMonth",
              displayLabel:
                REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES["google-reviews"],
              color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS["google-reviews"],
            },
            {
              platform: "trustpilot",
              integrationId: CROSS_TOOL_RESOLVE_SENTINEL_TRUSTPILOT,
              metricKey: "reviewVelocityPerMonth",
              displayLabel: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.trustpilot,
              color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS.trustpilot,
            },
            {
              platform: "yelp",
              integrationId: CROSS_TOOL_RESOLVE_SENTINEL_YELP,
              metricKey: "reviewVelocityPerMonth",
              displayLabel: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.yelp,
              color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS.yelp,
            },
          ],
        },
        calibrationFootnote: REVIEWS_CROSS_PLATFORM_CALIBRATION_FOOTNOTE,
        // AC #4 — if one platform returns 0 rows, render the other 2 with
        // an inline warning chip rather than blanking the chart.
        emptyStateBehavior: "graceful-degrade",
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Reviews Cross-Platform Sentiment Stack preset (Story 23.7 AC #4).
 *
 * Returns 1 stacked-bar-chart widget (`colSpan: 12, rowSpan: 3`) with 3
 * categories on the x-axis (Google / Trustpilot / Yelp) and 3 stacked
 * series (positive / neutral / negative review counts per platform).
 * Reveals which platform has the most polarized review distribution and
 * which is most evenly distributed.
 *
 * NOT a pie chart — pies are per-platform; stacked bar is the cross-
 * platform shape per Round 4 ADR-A.
 *
 * Sentiment normalization is rule-based per platform — each adapter
 * already applies its own star→sentiment bucketing (GR
 * `sentimentBucketForStar` + TP `trustpilotSentimentBucket` + Yelp
 * `yelpSentimentBucket`); the unified widget consumes the bucketed counts
 * without re-bucketing. Cross-adapter bucket coherence is enforced by the
 * test in `reviews-cross-platform/metrics.test.ts`.
 *
 * Per-platform metric keys consumed: `positiveReviewCount` /
 * `neutralReviewCount` / `negativeReviewCount` on Google Reviews +
 * Trustpilot; `positiveExcerptCount` / `neutralExcerptCount` /
 * `negativeExcerptCount` on Yelp (Yelp's 3-excerpt cap means its sentiment
 * row reflects only the recent excerpt subset — disclosed in the
 * calibration footnote).
 */
export function expandReviewsCrossPlatformSentimentStackPreset(
  _platform: string,
  googleReviewsIntegrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Cross-Platform Sentiment Stack",
      config: {
        dimensionKey: "platform",
        // Stacked-bar config — 3 categories (platforms) on x-axis; 3
        // stacked series (positive / neutral / negative) per category.
        // Round 1 Code Review Edge Case Hunter Finding #7 fix —
        // `stacked: true` is a forward-compat flag the bar-chart renderer
        // does NOT consume today; pairing with `stackId` mirrors recharts'
        // canonical convention (see AudienceGrowthChart.tsx for the
        // pattern). When the production resolver lands (deferred per
        // AC #16), the renderer can read either `stacked: true` (legacy
        // flag) or `stackId` (recharts-native) to opt into stacked
        // rendering. For now, the demo-only envelope intercepts before
        // render, so the visual is moot until the resolver ships.
        //
        // Round 2 Code Review Blind Hunter Finding R2-BH-10 fix —
        // `stackId` is preset-unique (`reviews-cross-platform-sentiment-stack`)
        // rather than the generic `"sentiment"`. recharts groups bars by
        // shared stackId within a single BarChart parent — using a
        // generic stackId value invites accidental cross-widget stacking
        // if a future stacked-bar widget also picks the word
        // "sentiment".
        stacked: true,
        stackId: "reviews-cross-platform-sentiment-stack",
        metrics: [
          { key: "positive", label: "Positive (4-5★)", axis: "left", color: REVIEWS_CROSS_PLATFORM_SENTIMENT_COLORS.positive },
          { key: "neutral", label: "Neutral (3★)", axis: "left", color: REVIEWS_CROSS_PLATFORM_SENTIMENT_COLORS.neutral },
          { key: "negative", label: "Negative (1-2★)", axis: "left", color: REVIEWS_CROSS_PLATFORM_SENTIMENT_COLORS.negative },
        ],
        // AC #4 — synthetic-aggregate dataSource over each platform's
        // positive/neutral/negative review-count synthetic-aggregates.
        dataSource: {
          kind: "synthetic-aggregate",
          aggregateKey: "reviewsUnifiedSentimentStack",
          sources: [
            {
              platform: "google-reviews",
              integrationId: googleReviewsIntegrationId,
              metricKeys: {
                positive: "positiveReviewCount",
                neutral: "neutralReviewCount",
                negative: "negativeReviewCount",
              },
              displayLabel:
                REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES["google-reviews"],
            },
            {
              platform: "trustpilot",
              integrationId: CROSS_TOOL_RESOLVE_SENTINEL_TRUSTPILOT,
              metricKeys: {
                positive: "positiveReviewCount",
                neutral: "neutralReviewCount",
                negative: "negativeReviewCount",
              },
              displayLabel: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.trustpilot,
            },
            {
              platform: "yelp",
              integrationId: CROSS_TOOL_RESOLVE_SENTINEL_YELP,
              // Yelp's 3-excerpt cap — sentiment counts reflect only the
              // most-recent-excerpt subset (per Story 23.5 AC #3); the
              // calibration footnote discloses this.
              metricKeys: {
                positive: "positiveExcerptCount",
                neutral: "neutralExcerptCount",
                negative: "negativeExcerptCount",
              },
              displayLabel: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.yelp,
            },
          ],
        },
        calibrationFootnote: REVIEWS_CROSS_PLATFORM_CALIBRATION_FOOTNOTE,
        // Round 5 Edge Case Hunter — empty-state guard: when all 3
        // platforms have 0 reviews, render an empty-state card rather than
        // 3 zero-height bars.
        emptyStateMessage:
          "Connect data to populate cross-platform sentiment",
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

// ============================================================================
// Story 23.8 — Reviews Cross-Platform Response Tracking presets (closes Epic
// 23 in full). 4 NEW preset functions answering the agency's SECOND most-
// asked reputation question: "are we RESPONDING across our review platforms?"
//
// 2-PLATFORM SCOPE — DIVERGENT from Story 23.7's 3-platform widgets:
//   Yelp Fusion ships NO `companyReply` / `owner_response` field per Story
//   23.5 documented API constraint. ALL 4 presets below structurally render
//   exactly 2 platforms (Google Reviews + Trustpilot). The 2-platform scope
//   is enforced by `dataSource.sources[]` length-2 invariant — a future
//   regression that adds Yelp would be caught by widgetPresets.test.ts's
//   structural-2-platform assertion.
//
// `_platform` parameter rationale (Round 1 Code Review H3-Edge precedent
// carried forward from Story 20.9 + 23.7): the dispatcher's `platform`
// argument is unused by these functions because cross-platform widgets
// render multiple platforms simultaneously; a single platform label is
// meaningless. The parameter remains for signature uniformity with the
// rest of the dispatcher.
//
// `integrationId` resolution pattern: the caller passes the Google
// Reviews integration ID as the `integrationId` argument; the Trustpilot
// integration ID is resolved at preset-expand time via the
// `CROSS_TOOL_RESOLVE_SENTINEL_TRUSTPILOT` sentinel pattern documented
// at widgetPresets.ts:3554-3566. NO Yelp sentinel is used (2-platform
// scope structurally enforced).
//
// Build-to-sell rationale (verified May 2026): No competitor reporting
// tool surfaces cross-platform reply-rate trend-over-time joined in one
// widget per the May 2026 competitive scan (AgencyAnalytics + Whatagraph
// + Databox + Birdeye + Podium + Reputation.com + ReviewTrackers +
// RightResponse AI). The category-defining widget is preset #3
// (`reviewsUnifiedReplyRateTrend` — joins 2 review platforms' reply-rate
// over a fixed 6-month window per ReplyOnTheFly 2026 industry benchmarks).
//
// @see _bmad-output/implementation-artifacts/23-8-review-response-tracking.md
// ============================================================================

/**
 * Expand the Reviews Cross-Platform Reply Rate Scoreboard preset (Story 23.8 AC #4).
 *
 * Returns **3 widgets**: 1 heading widget ("Cross-Platform Reply Rate
 * Scoreboard") at `colSpan: 12, rowSpan: 1` + 2 KPI widgets side-by-side
 * at `colSpan: 6, rowSpan: 1` each (NOT 4-col like Story 23.7's 3-KPI
 * scoreboard — the 2-platform scope means each KPI takes half the row).
 *
 * **Story 23.8 Pass 1 Code Review M1 fix:** the AC #4 spec described
 * "1 heading + 2 KPI + 1 invisible footnote-carrier widget = 4 widgets",
 * but matching Story 23.7's layout precedent the footnote is carried on
 * KPI #1's `config.footnote` field (no separate carrier widget). The
 * widget count is 3, not 4 — the footnote-carrier was a documentation
 * artifact from an early design iteration, not a renderer requirement.
 * Story 23.7's `expandReviewsCrossPlatformUnifiedScoreboardPreset`
 * follows the same 3-widget-plus-config.footnote pattern.
 *
 * Per-platform metric keys consumed:
 *   - Google Reviews: `replyRate` (APPROVED-state replies / fetchedReviewCount × 100;
 *     per Story 23.2 adapter at google-reviews.ts:1392 — PENDING replies
 *     are NOT counted)
 *   - Trustpilot: `companyReplyRate` (companyReply non-null /
 *     fetchedReviewCount × 100; Trustpilot has no moderation states)
 *
 * Yelp is EXCLUDED — Yelp Fusion ships no `companyReply` / `owner_response`
 * field per Story 23.5 documented constraint. The 2-platform scope is
 * intentional, not a missing-data state. The calibration footnote (extended
 * in Story 23.8) discloses this directly.
 *
 * `_platform` is the dispatcher's `platform` argument from `expandPreset`,
 * carried for signature uniformity per Round 1 Code Review H3-Edge
 * precedent (Story 20.9). Intentionally NOT bound.
 */
export function expandReviewsCrossPlatformReplyRateScoreboardPreset(
  _platform: string,
  googleReviewsIntegrationId: string,
): PresetWidget[] {
  interface ReplyRateKpi {
    metricKey: string;
    title: string;
    integrationPlatform: "google-reviews" | "trustpilot";
    integrationId: string;
    color: string;
  }
  const kpis: ReplyRateKpi[] = [
    {
      metricKey: "replyRate",
      title: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES["google-reviews"],
      integrationPlatform: "google-reviews",
      integrationId: googleReviewsIntegrationId,
      color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS["google-reviews"],
    },
    {
      metricKey: "companyReplyRate",
      title: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.trustpilot,
      integrationPlatform: "trustpilot",
      integrationId: CROSS_TOOL_RESOLVE_SENTINEL_TRUSTPILOT,
      color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS.trustpilot,
    },
  ];
  const headingWidget: PresetWidget = {
    id: crypto.randomUUID(),
    type: "heading",
    title: "Cross-Platform Reply Rate Scoreboard",
    config: {
      content: "Cross-Platform Reply Rate Scoreboard",
    },
    colSpan: 12,
    rowSpan: 1,
  };
  const kpiWidgets: PresetWidget[] = kpis.map((kpi, idx) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: kpi.integrationPlatform,
      integrationId: kpi.integrationId,
      color: kpi.color,
      valueSuffix: "%",
      // Synthetic-aggregate dataSource — the production resolver (deferred
      // per AC #16) materializes the 2-platform reply-rate join at fetch
      // time. The 2-platform `sources[]` length is the structural guarantee
      // of the 2-platform scope.
      dataSource: {
        kind: "synthetic-aggregate",
        aggregateKey: "reviewsUnifiedReplyRateScoreboard",
        sources: [
          {
            platform: "google-reviews",
            integrationId: googleReviewsIntegrationId,
            metricKey: "replyRate",
            displayLabel:
              REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES["google-reviews"],
            color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS["google-reviews"],
          },
          {
            platform: "trustpilot",
            integrationId: CROSS_TOOL_RESOLVE_SENTINEL_TRUSTPILOT,
            metricKey: "companyReplyRate",
            displayLabel: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.trustpilot,
            color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS.trustpilot,
          },
        ],
      },
      // Footnote on FIRST KPI only per Story 23.7 layout precedent.
      ...(idx === 0
        ? { footnote: REVIEWS_CROSS_PLATFORM_CALIBRATION_FOOTNOTE }
        : {}),
    },
    // 2 KPIs side-by-side at colSpan 6 each = 12-column full-row width.
    // DIVERGENT from Story 23.7's 3-KPI scoreboard at colSpan 4 each.
    colSpan: 6,
    rowSpan: 1,
  }));
  return [headingWidget, ...kpiWidgets];
}

/**
 * Expand the Reviews Cross-Platform Avg Response Time Bar preset
 * (Story 23.8 AC #4).
 *
 * Returns 1 bar-chart widget at `colSpan: 12, rowSpan: 3` with 2 bars
 * (Google + Trustpilot avg-response-time in HOURS, rendered as
 * `avgReplyLatencySeconds / 3600` at draw time).
 *
 * Build-to-sell wedge — per ReplyOnTheFly 2026 benchmark verified
 * 2026-05-19 (`replyonthefly.com/blog/2026-google-review-response-benchmark`):
 * consumer same-day-response expectation rose from 6% → 19% in 12
 * months. Agencies need to surface response-latency as a first-class KPI
 * to justify reputation-management retainers. NO competitor reporting
 * tool joins Google + Trustpilot avg-response-time in one bar chart
 * (re-verified May 2026 against AgencyAnalytics + Birdeye + Podium +
 * Whatagraph + Databox + Reputation.com + ReviewTrackers + RightResponse).
 *
 * Color bands (NOT brand colors — band-based severity coloring):
 *   - < 2h: excellent (emerald) — top-performer band
 *   - < 24h: good (blue) — meets-expectation band
 *   - < 72h: fair (amber) — at-risk band
 *   - >= 72h: poor (red) — reputational-risk band
 * The renderer reads thresholds from
 * `REVIEWS_CROSS_PLATFORM_RESPONSE_TIME_THRESHOLDS_HOURS` + colors from
 * `REVIEWS_CROSS_PLATFORM_RESPONSE_TIME_BAND_COLORS`. The intentional
 * divergence from per-platform brand coloring communicates fast/slow
 * rather than which-platform; the heading widget's title is what
 * disambiguates the bars.
 *
 * Per-platform metric keys consumed:
 *   - Google Reviews: `avgReplyLatencySeconds` (Story 23.2 adapter —
 *     computed only across APPROVED replies; NULL if no APPROVED replies)
 *   - Trustpilot: `avgCompanyReplyLatencySeconds` (Story 23.4 adapter —
 *     mean seconds between review createdAt and companyReply.createdAt)
 *
 * Round 4 ADR-C decision: the per-adapter naming inconsistency
 * (`avgReplyLatencySeconds` vs `avgCompanyReplyLatencySeconds`) is
 * PRESERVED at this layer; the unified synthetic-aggregate metric
 * (`reviewsUnifiedAvgResponseTimeBar`) abstracts the naming difference
 * at the widget layer.
 *
 * Yelp is EXCLUDED — Yelp Fusion ships no reply-latency surface (because
 * it ships no reply surface at all). Bar order: Google → Trustpilot
 * (alphabetical / deterministic), NOT highest-to-lowest.
 */
export function expandReviewsCrossPlatformAvgResponseTimeBarPreset(
  _platform: string,
  googleReviewsIntegrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Cross-Platform Avg Response Time",
      config: {
        dimensionKey: "platform",
        // Story 23.8 Pass 1 Code Review M2 fix — explicit unit
        // declaration. The metric.unit field tells the renderer "the
        // upstream sources emit seconds; divide by this divisorSeconds
        // before rendering". This replaces the implicit
        // string-match-on-yAxisLabel convention that was fragile.
        metrics: [
          {
            key: "avgResponseTimeHours",
            label: "Avg Response Time (hours)",
            axis: "left",
            unit: "hours" as const,
            divisorSeconds: 3600,
          },
        ],
        yAxisLabel: "Hours",
        // Synthetic-aggregate dataSource over each platform's avg-latency
        // metric. Renderer divides seconds by 3600 at draw time.
        dataSource: {
          kind: "synthetic-aggregate",
          aggregateKey: "reviewsUnifiedAvgResponseTimeBar",
          sources: [
            {
              platform: "google-reviews",
              integrationId: googleReviewsIntegrationId,
              metricKey: "avgReplyLatencySeconds",
              displayLabel:
                REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES["google-reviews"],
            },
            {
              platform: "trustpilot",
              integrationId: CROSS_TOOL_RESOLVE_SENTINEL_TRUSTPILOT,
              metricKey: "avgCompanyReplyLatencySeconds",
              displayLabel: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.trustpilot,
            },
          ],
        },
        // Band-based color encoding. The bar color is determined by which
        // band the value falls into (NOT the per-platform brand color) —
        // disambiguates fast/slow from which-platform.
        colorBands: REVIEWS_CROSS_PLATFORM_RESPONSE_TIME_BAND_COLORS,
        thresholds: REVIEWS_CROSS_PLATFORM_RESPONSE_TIME_THRESHOLDS_HOURS,
        // Story 23.8 Pass 1 Code Review M4 fix — explicit no-data
        // color. The renderer reads this token when a per-platform
        // avgReplyLatencySeconds resolves to null (newly-listed
        // business, all-PENDING moderation state, or zero APPROVED
        // replies). Gray band disambiguates "no signal" from "0 hours
        // = impossibly excellent".
        noDataColor: REVIEWS_CROSS_PLATFORM_RESPONSE_TIME_NO_DATA_COLOR,
        calibrationFootnote: REVIEWS_CROSS_PLATFORM_CALIBRATION_FOOTNOTE,
        // Story 23.2 empty-state-NULL semantic carry-forward: if Google's
        // `avgReplyLatencySeconds` is NULL (no APPROVED replies), the
        // renderer should render the bar in the gray empty-state band
        // rather than the poor (red) band — Round 5 Edge Case Hunter
        // empty-state guard.
        emptyStateMessage:
          "Connect data to populate cross-platform response time",
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Reviews Cross-Platform Reply Rate Trend preset
 * (Story 23.8 AC #4) — **the build-to-sell category-defining widget for
 * Story 23.8.**
 *
 * Returns 1 line-chart widget at `colSpan: 12, rowSpan: 3` with 2 series
 * (Google + Trustpilot rolling-30-day reply-rate over a fixed 6-month
 * window). NO date-range picker — the 6-month window is FIXED per AC #4.
 *
 * Joins 2 different review platforms' reply-rate-over-time signals in a
 * single line chart — verified May 2026, no competitor reporting tool
 * surfaces this widget (re-verified against AgencyAnalytics +
 * Whatagraph + Databox + Birdeye + Podium + Reputation.com +
 * ReviewTrackers + RightResponse AI per the Story 23.8 spec rationale).
 *
 * Agency use case: "Your reputation-management campaign drove Google
 * reply-rate from 45% → 72% and Trustpilot from 38% → 68% over Q1 — a
 * 23-percentage-point cross-platform lift in response discipline. No
 * other reporting tool can show this curve in one widget."
 *
 * Per-platform metric keys consumed:
 *   - Google Reviews: `replyRate` (APPROVED-only semantic — same as
 *     ReplyRateScoreboard preset's source)
 *   - Trustpilot: `companyReplyRate` (non-null semantic)
 *
 * y-axis range is FIXED at [0, 100] (percentage). The 6-month window is
 * structurally enforced by `dataSource.windowMonths: 6`.
 *
 * Yelp is EXCLUDED — same constraint as the other Story 23.8 presets.
 */
export function expandReviewsCrossPlatformReplyRateTrendPreset(
  _platform: string,
  googleReviewsIntegrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Cross-Platform Reply Rate Trend (6 months)",
      config: {
        dimensionKey: "monthLabel",
        // Series keys are joined-row COLUMN names that the future renderer
        // materializes from each platform's reply-rate over time. Mirrors
        // Story 23.7's Velocity Trend `{platform}Velocity` convention.
        metrics: [
          {
            key: "googleReplyRate",
            label: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES["google-reviews"],
            axis: "left",
            color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS["google-reviews"],
          },
          {
            key: "trustpilotReplyRate",
            label: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.trustpilot,
            axis: "left",
            color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS.trustpilot,
          },
        ],
        yAxisRange: [0, 100],
        yAxisLabel: "Reply Rate %",
        // Synthetic-aggregate dataSource — 6-month rolling window joining
        // 2 series on `monthLabel`.
        dataSource: {
          kind: "synthetic-aggregate",
          aggregateKey: "reviewsUnifiedReplyRateTrend",
          windowMonths: 6,
          sources: [
            {
              platform: "google-reviews",
              integrationId: googleReviewsIntegrationId,
              metricKey: "replyRate",
              displayLabel:
                REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES["google-reviews"],
              color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS["google-reviews"],
            },
            {
              platform: "trustpilot",
              integrationId: CROSS_TOOL_RESOLVE_SENTINEL_TRUSTPILOT,
              metricKey: "companyReplyRate",
              displayLabel: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.trustpilot,
              color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS.trustpilot,
            },
          ],
        },
        calibrationFootnote: REVIEWS_CROSS_PLATFORM_CALIBRATION_FOOTNOTE,
        // Round 5 Edge Case Hunter — sparse-data guard: if one platform
        // has < 6 months of history, render only the months with data and
        // gray out the rest. Do NOT extrapolate, do NOT flat-line at 0.
        emptyStateBehavior: "graceful-degrade",
        // Story 23.8 Pass 1 Code Review M6 fix — explicit minimum-buckets
        // threshold below which the renderer SHOULD show a "sparse
        // data — connect data farther back to populate trend" empty-
        // state alongside the rendered bars. Summit Legal's fixture
        // generator places only ~10 reviews in the 6-month window so
        // the renderer must NOT panic when most months are empty.
        sparseDataThreshold: 3,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Reviews Cross-Platform Awaiting Response Queue preset
 * (Story 23.8 AC #4).
 *
 * Returns 3 widgets: 1 heading widget "Awaiting Response Queue"
 * (`colSpan: 12, rowSpan: 1`) + 2 KPI widgets side-by-side at `colSpan: 6,
 * rowSpan: 2` each — LARGER rowSpan than the Reply Rate Scoreboard preset
 * because each Awaiting-Queue KPI displays both the unreplied-count AND
 * the oldest-unreplied-days subtitle.
 *
 * Per-platform metric keys consumed:
 *   - Google Reviews: `unrepliedReviewCount` (per Story 23.2 adapter —
 *     PENDING replies are NOT counted as unreplied; they're counted in
 *     `pendingOrRejectedReplies` separately)
 *   - Trustpilot: `unrepliedReviewCount` (Trustpilot's companyReply ===
 *     null count). SAME key name across both adapters by design — the
 *     synthetic-aggregate uses the unified key name directly.
 *
 * Subtitle template `Oldest: {oldestUnrepliedDays...} days` references
 * synthetic per-platform sub-metrics (`oldestUnrepliedDaysGoogle` +
 * `oldestUnrepliedDaysTrustpilot`) computed at widget-expand-time from
 * each platform's per-record drill-down — max(now - createTime) over
 * records without a reply. Round 3 Red Team #2 — the subtitle renders
 * only the integer day count, NEVER the reviewer-name or absolute date,
 * so PII leakage via subtitle is impossible.
 *
 * Color bands on the count: 0-3 days normal / 4-7 days warn / 8+ days
 * alert. Round 5 Edge Case Hunter — `oldestUnrepliedDays === 0` (all
 * caught up) renders green-band styling with "All caught up" tooltip
 * rather than red-alert.
 *
 * Yelp is EXCLUDED — Yelp Fusion ships no `unrepliedReviewCount` (because
 * it ships no reply surface).
 */
export function expandReviewsCrossPlatformAwaitingResponseQueuePreset(
  _platform: string,
  googleReviewsIntegrationId: string,
): PresetWidget[] {
  interface AwaitingQueueKpi {
    metricKey: string;
    oldestDaysSubMetric: string;
    title: string;
    integrationPlatform: "google-reviews" | "trustpilot";
    integrationId: string;
    color: string;
  }
  const kpis: AwaitingQueueKpi[] = [
    {
      metricKey: "unrepliedReviewCount",
      oldestDaysSubMetric: "oldestUnrepliedDaysGoogle",
      title: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES["google-reviews"],
      integrationPlatform: "google-reviews",
      integrationId: googleReviewsIntegrationId,
      color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS["google-reviews"],
    },
    {
      metricKey: "unrepliedReviewCount",
      oldestDaysSubMetric: "oldestUnrepliedDaysTrustpilot",
      title: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.trustpilot,
      integrationPlatform: "trustpilot",
      integrationId: CROSS_TOOL_RESOLVE_SENTINEL_TRUSTPILOT,
      color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS.trustpilot,
    },
  ];
  const headingWidget: PresetWidget = {
    id: crypto.randomUUID(),
    type: "heading",
    title: "Awaiting Response Queue",
    config: {
      content: "Awaiting Response Queue",
    },
    colSpan: 12,
    rowSpan: 1,
  };
  const kpiWidgets: PresetWidget[] = kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: false,
      integrationPlatform: kpi.integrationPlatform,
      integrationId: kpi.integrationId,
      color: kpi.color,
      // Subtitle template renders ONLY the integer day count — no
      // reviewer-name, no absolute date (Round 3 Red Team #2 PII defense).
      subtitle: `Oldest: {${kpi.oldestDaysSubMetric}} days`,
      // Synthetic-aggregate dataSource — the production resolver
      // materializes both `unrepliedCount` + `oldestUnrepliedDays`
      // per platform at fetch time.
      dataSource: {
        kind: "synthetic-aggregate",
        aggregateKey: "reviewsUnifiedAwaitingResponseQueue",
        sources: [
          {
            platform: "google-reviews",
            integrationId: googleReviewsIntegrationId,
            metricKey: "unrepliedReviewCount",
            oldestDaysSubMetricKey: "oldestUnrepliedDaysGoogle",
            displayLabel:
              REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES["google-reviews"],
            color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS["google-reviews"],
          },
          {
            platform: "trustpilot",
            integrationId: CROSS_TOOL_RESOLVE_SENTINEL_TRUSTPILOT,
            metricKey: "unrepliedReviewCount",
            oldestDaysSubMetricKey: "oldestUnrepliedDaysTrustpilot",
            displayLabel: REVIEWS_CROSS_PLATFORM_DISPLAY_NAMES.trustpilot,
            color: REVIEWS_CROSS_PLATFORM_BRAND_COLORS.trustpilot,
          },
        ],
      },
      // Color bands: 0-3 normal / 4-7 warn / 8+ alert. Bands compute on
      // the OLDEST-DAYS sub-metric, NOT the count itself — a high-count-
      // but-recent queue is healthier than a low-count-but-stale queue.
      ageBands: {
        normal: 3,
        warn: 7,
      },
      // Story 23.8 Pass 1 Code Review L6 fix — explicit zero-state
      // token. When `oldestUnrepliedDays === 0` AND `unrepliedCount
      // === 0` (i.e., literally no unreplied reviews), render the
      // "All caught up" emerald-band styling + tooltip rather than
      // the "normal" 0-3-day yellow band. This is the empty-state
      // success-affirmation pattern Round 5 Edge Case Hunter
      // described in the in-line comment block of this preset.
      caughtUpState: {
        threshold: 0,
        color: "#10B981", // emerald-500 (matches excellent-band response-time color)
        tooltip: "All caught up — no reviews awaiting response",
      },
    },
    // 2 KPIs side-by-side at colSpan 6 each. rowSpan 2 (larger than
    // ReplyRateScoreboard's rowSpan 1) accommodates both the count value
    // AND the oldest-unreplied-days subtitle.
    colSpan: 6,
    rowSpan: 2,
  }));
  return [headingWidget, ...kpiWidgets];
}

// ── Meta Ads Presets (IMC-14) ──────────────────────────────────────────

/**
 * Expand the Meta Ads Overview KPI preset into 5 KPI widgets.
 * Uses: spend, impressions, reach, ctr, purchase_roas
 */
export function expandMetaAdsOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "spend", title: "Ad Spend" },
    { metricKey: "impressions", title: "Impressions" },
    { metricKey: "reach", title: "Reach" },
    { metricKey: "ctr", title: "CTR" },
    { metricKey: "purchase_roas", title: "ROAS" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Expand the Meta Ads Video Funnel preset into 1 funnel widget.
 * Shows video completion stages: plays → 25% → 50% → 75% → 100%
 */
export function expandMetaAdsVideoFunnelPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "funnel",
      title: "Video View Funnel",
      config: {
        stages: [
          { metricKey: "video_plays", label: "Video Plays" },
          { metricKey: "video_p25", label: "25% Watched" },
          { metricKey: "video_p50", label: "50% Watched" },
          { metricKey: "video_p75", label: "75% Watched" },
          { metricKey: "video_p100", label: "100% Watched" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Meta Ads Conversion Table preset into 1 data-table widget.
 * Campaign breakdown with spend, conversions, cost per conversion, ROAS, LP view/link click.
 */
export function expandMetaAdsConversionTablePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Campaign Conversion Performance",
      config: {
        metricKeys: ["spend", "conversions", "cost_per_conversion", "purchase_roas", "landing_page_view_per_link_click"],
        dimensionKey: "campaign_name",
        sortBy: "spend",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

/**
 * Expand the Meta Ads Quality Table preset into 1 data-table widget.
 * Ad-level quality rankings: quality, engagement rate, conversion rate.
 */
export function expandMetaAdsQualityTablePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Ad Quality Rankings",
      config: {
        metricKeys: ["quality_ranking", "engagement_rate_ranking", "conversion_rate_ranking"],
        dimensionKey: "ad_name",
        sortBy: "quality_ranking",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

// ============================================================================
// LinkedIn Pages Presets (IMC-15)
// ============================================================================

/**
 * Expand the LinkedIn Pages Overview KPI preset into 5 KPI widgets.
 *
 * Uses actual LinkedIn Pages adapter metric keys:
 * - followers, impressions, clicks, engagement_rate, page_views_total
 */
export function expandLinkedInPagesOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "followers", title: "Followers" },
    { metricKey: "impressions", title: "Impressions" },
    { metricKey: "clicks", title: "Clicks" },
    { metricKey: "engagement_rate", title: "Engagement Rate" },
    { metricKey: "page_views_total", title: "Page Views" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Expand the LinkedIn Pages Demographics preset into 1 demographic widget.
 *
 * Shows follower breakdown by industry + seniority + job function.
 */
export function expandLinkedInPagesDemographicsPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "demographic",
      title: "Follower Demographics",
      config: {
        dimensionKeys: ["industry", "seniority", "function"],
        metricKey: "follower_count",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

// ============================================================================
// Story 22.2 — CallRail Presets (closes Epic 22 CallRail slice)
//
// 8 widget presets for the Summit Legal Group demo workspace + every real-
// CallRail customer. Mirrors the Moz 20.8 + Semrush 20.6 + Ahrefs 20.3
// preset pattern.
//
// Preset slug naming convention: `callrail-<surface>-<widget>`.
// Backing `_reportType` discriminators per Story 22.1 transformers:
//   - `calls-summary` — 1 row of aggregate counts (4-KPI scorecard sources)
//   - `calls-timeseries` — 90 daily rows (line-chart trend source)
//   - `calls-list` — 100 per-call records (data-table + Sentiment Pie source)
//   - `form-submissions` — 30 intake-form records (form-presets, no preset
//     in Story 22.2 — deferred)
// ============================================================================

/**
 * Expand the CallRail Call Volume KPI preset (4 KPIs).
 *
 * The headline CallRail widget — every local-services agency dashboard's
 * first row per AgencyAnalytics + Databox + DashThis + Swydo + Whatagraph
 * 2026 templates verified 2026-05-15. Bound to `_reportType: "calls-summary"`.
 */
export function expandCallRailCallVolumeKpiPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "totalCalls", title: "Total Calls" },
    { metricKey: "answeredCalls", title: "Answered Calls" },
    { metricKey: "missedCalls", title: "Missed Calls" },
    { metricKey: "firstTimeCallers", title: "First-Time Callers" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

/**
 * Expand the CallRail Answer Rate Gauge preset (1 gauge widget).
 *
 * THE single most-watched call-tracking quality metric for HVAC/legal/dental
 * verticals — answer rate is the proxy for "are clients investing enough in
 * answering services?" Industry-standard band 85-92% per AgencyAnalytics
 * 2026 verified 2026-05-15.
 */
export function expandCallRailAnswerRateGaugePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "gauge",
      title: "Answer Rate",
      config: {
        metricKey: "answerRate",
        target: 100,
        // Threshold band per AgencyAnalytics 2026: 0-50% poor / 50-80%
        // acceptable / 80-90% good / 90-100% excellent. Renderer treats
        // each threshold's `value` as the UPPER BOUND of its color band.
        thresholds: [
          { value: 50, color: "red" },
          { value: 80, color: "amber" },
          { value: 90, color: "green" },
          { value: 100, color: "dark-green" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 2,
    },
  ];
}

/**
 * Expand the CallRail Calls Over Time preset (1 line-chart widget).
 *
 * THE canonical agency-reporting trend chart — answers "are we trending
 * up?" on the cover page. 90-day daily series powered by Story 22.1's
 * `/calls/timeseries.json` discriminator. B2B-dip weekend pattern visible.
 */
export function expandCallRailCallsOverTimePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Calls Over Time (90-Day)",
      config: {
        metricKeys: ["totalCalls"],
        dimensionKey: "date",
        reportTypeFilter: "calls-timeseries",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the CallRail Calls by Source bar-chart preset (1 widget).
 *
 * THE #1 breakdown widget per AgencyAnalytics 2026 "Top CallRail Analytics
 * to Track for Clients" verified 2026-05-15. Per-source call volume
 * showing which marketing channels drive phone leads.
 */
export function expandCallRailCallsBySourceBarPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Calls by Source",
      config: {
        // `metrics: string[]` per BarChartWidget contract — renderer reads
        // calls-list records, groups by `source`, and emits per-bucket
        // counts of `totalCalls` rows. Mirrors existing Mailchimp / Moz
        // bar-chart preset shape (Pass 2 review fix — earlier object-shape
        // tripped the widget's `metrics.join(", ")` aria-label).
        metrics: ["totalCalls"],
        groupBy: "source",
        reportTypeFilter: "calls-list",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the CallRail Calls × Source × Answer Rate stacked-bar preset
 * (1 widget — Story 22.2 BUILD-TO-SELL CATEGORY-DEFINING differentiator).
 *
 * No competitor reporting tool surfaces this cross-tab in one click —
 * verified 2026-05-15 across AgencyAnalytics + Databox + Whatagraph +
 * DashThis + Swydo May 2026 dashboard templates. Shows answered calls
 * stacked on missed calls per source, exposing paid-channel waste:
 * "Google Ads 95% answer rate vs Microsoft Ads 78% answer rate".
 *
 * The widget joins Story 22.1's `/calls/summary.json?group_by=source`
 * (per-source answered+missed counts) at preset-expansion time. Per Databox
 * `databox.com/dashboard-examples/callrail-source-keyword-performance`
 * verified 2026-05-15 — the closest analog templated in the industry, and
 * still missing the answer-rate cross-tab.
 */
export function expandCallRailCallsBySourceAnswerRateStackedPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Calls × Source × Answer Rate",
      config: {
        // `metrics: string[]` per BarChartWidget contract — TWO metrics
        // with the same `groupBy` automatically renders stacked bars
        // (renderer convention). Each metric is a per-source aggregate
        // count of rows in the corresponding answerStatus bucket.
        metrics: ["answeredCalls", "missedCalls"],
        groupBy: "source",
        stacked: true,
        reportTypeFilter: "calls-list",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the CallRail Calls List data-table preset (1 widget).
 *
 * THE per-call drill-down table — agencies pivot through this when clients
 * ask "show me the call from yesterday at 2pm". Per AgencyAnalytics +
 * Databox 2026 templates verified 2026-05-15 — the agency-canonical "Call
 * Log" widget.
 *
 * Columns include `sentiment` (visible only when
 * `conversationIntelligenceEntitled === true` — the fixture force-claims
 * `true`; production paths honor the entitlement probe).
 */
export function expandCallRailCallsListTablePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Calls List",
      config: {
        metricKeys: [
          "startTime",
          "source",
          "customerName",
          "trackingPhoneNumber",
          "formattedTrackingPhoneNumber",
          "direction",
          "answerStatus",
          "callDuration",
          "callValue",
          "leadStatus",
          "sentiment",
          // Story 22.5 — `id` is the CallRail per-call identifier; the
          // DataTable widget hides this column from the visible header
          // when `recordingColumn` is set and uses it as the recordingId
          // source for the ▶ Play button.
          "id",
        ],
        reportTypeFilter: "calls-list",
        sortBy: "startTime",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
        // Story 22.5 AC #9 — Play button column config
        recordingColumn: {
          platform: "callrail",
          recordingIdField: "id",
        },
      },
      colSpan: 12,
      rowSpan: 4,
      autoSize: true,
    },
  ];
}

/**
 * Expand the CallRail Conversation Intelligence Sentiment Pie preset
 * (1 widget — 2026 AI-era flagship surface).
 *
 * Premium-CI-gated. The 2026 flagship CallRail surface — Conversation
 * Intelligence is the only AI-era call-quality signal that Google Ads +
 * Meta Ads do NOT have, per `support.callrail.com/hc/en-us/articles/
 * 14738235036301-Call-sentiments` + Multi-Conversation Insights verified
 * 2026-05-15.
 *
 * Subscription_required fallback: if
 * `ctx.metadata.conversationIntelligenceEntitled !== true`, renderer shows
 * "Conversation Intelligence requires CallRail Premium CI plan — Upgrade"
 * badge. Demo always shows populated state because Story 22.2's fixture-
 * mode helper force-stamps `conversationIntelligenceEntitled: true`.
 */
export function expandCallRailConversationIntelligenceSentimentPiePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "pie-chart",
      title: "Sentiment Distribution",
      config: {
        // Pie chart consumes calls-list rows + groups by `sentiment` —
        // standard `metricKey` + `groupBy` shape. The renderer counts
        // rows per sentiment bucket; rows with `sentiment: null`
        // (CI-gated base-plan customers) are skipped naturally by the
        // grouping algorithm. Story 22.2 fixture-mode helper forces
        // `conversationIntelligenceEntitled: true` so every demo row
        // has a populated sentiment, but a real-API run with a base-
        // plan customer would yield an empty pie (UI shows a placeholder).
        groupBy: "sentiment",
        metricKey: "totalCalls",
        reportTypeFilter: "calls-list",
        // Renderer-side entitlement hint (Moz Story 20.8 precedent —
        // shared metadata key the renderer reads to gate widget display).
        requiresEntitlement: "conversationIntelligenceEntitled",
        upgradeUrl: "https://www.callrail.com/conversation-intelligence",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the CallRail Cross-Endpoint Scorecard preset (4 KPIs).
 *
 * THE CallRail-specific C-suite scorecard — answers "are our calls
 * converting?" in 4 numbers across all 4 endpoint surfaces:
 *   - totalCalls (calls-summary)
 *   - answerRate (derived from calls-summary)
 *   - firstTimeCallerRate (derived from calls-summary)
 *   - leadConversionRate (derived from calls-summary)
 *
 * Mirrors the Moz Story 20.8 + Ahrefs Story 20.3 cross-endpoint-scorecard
 * pattern: every tool gets a 4-KPI executive overview answering the tool's
 * canonical "is this working?" question in one widget.
 */
export function expandCallRailCrossEndpointScorecardPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "totalCalls", title: "Total Calls" },
    { metricKey: "answerRate", title: "Answer Rate" },
    { metricKey: "firstTimeCallerRate", title: "First-Time Caller Rate" },
    { metricKey: "leadConversionRate", title: "Lead Conversion Rate" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

// ============================================================================
// Story 22.4 — CTM Presets (closes Epic 22 in full)
// ============================================================================
//
// 9 widget presets for CallTrackingMetrics. 8 mirror the CallRail 22.2
// structure (KPI / Gauge / Line / Bar / Stacked-Bar / Table / Pie /
// Scorecard) with CTM-specific divergences:
//   - Calls Over Time: client-side daily bucketing (CTM has NO server-side
//     /timeseries endpoint — DIVERGENT from CallRail 22.2)
//   - Likelihood Quality Pie: 4-band quartile bucketing of likelihood
//     Decimal 0-1 (DIVERGENT from CallRail's categorical sentiment 3-band)
//   - Cross-Endpoint Scorecard: substitutes likelihoodAvg for CallRail's
//     firstTimeCallerRate (CTM AI-Likelihood is a stronger signal than
//     first-time-caller detection)
//
// 9th preset = `ctm-omnichannel-touch-funnel` — THE build-to-sell category-
// defining widget. No CallRail analogue. No competitor reporting tool
// surfaces Calls + Forms + SMS in 1 widget. Cross-surface aggregation of
// all 3 wire-format `_reportType` discriminators in one stacked bar.

/**
 * Expand the CTM Call Volume KPI preset (4 KPI widgets in a horizontal grid).
 *
 * 4 KPIs: Total Calls (65 Summit Legal CTM) + Answered Calls (56) + Missed
 * Calls (9) + New Callers (41). Mirrors `callrail-call-volume-kpi`.
 */
export function expandCtmCallVolumeKpiPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "totalCalls", title: "Total Calls" },
    { metricKey: "answeredCalls", title: "Answered Calls" },
    { metricKey: "missedCalls", title: "Missed Calls" },
    { metricKey: "newCallerCount", title: "New Callers" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

/**
 * Expand the CTM Answer Rate Gauge preset (1 gauge widget).
 *
 * Single derived metric `answerRate` (answeredCalls / totalCalls; 56/65 =
 * 86%). Threshold band per CTM omnichannel benchmark: 0-50% poor / 50-80%
 * acceptable / 80-90% good / 90-100% excellent.
 */
export function expandCtmAnswerRateGaugePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "gauge",
      title: "Answer Rate",
      config: {
        metricKey: "answerRate",
        target: 100,
        thresholds: [
          { value: 50, color: "red" },
          { value: 80, color: "amber" },
          { value: 90, color: "green" },
          { value: 100, color: "dark-green" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 2,
    },
  ];
}

/**
 * Expand the CTM Calls Over Time line-chart preset.
 *
 * **CRITICAL divergence from CallRail 22.2's Calls Over Time:** CTM does
 * NOT have a server-side `/timeseries.json` endpoint — Story 22.3 ships
 * only `/calls.json` per-call records. The renderer aggregates the
 * calls-list at preset-expansion time by bucketing `calledAtUtc` into
 * daily buckets. Config carries `aggregationMode: "client-side-daily-bucket"`
 * to signal the renderer.
 */
export function expandCtmCallsOverTimePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Calls Over Time (90-Day)",
      config: {
        metricKeys: ["totalCalls"],
        dimensionKey: "calledAtUtc",
        reportTypeFilter: "calls-list",
        aggregationMode: "client-side-daily-bucket",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the CTM Calls by Source bar-chart preset (1 widget).
 *
 * Per-source call volume showing attribution breakdown. Groups
 * `_reportType: "calls-list"` records by `source` at preset-expansion time.
 */
export function expandCtmCallsBySourceBarPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Calls by Source",
      config: {
        metrics: ["totalCalls"],
        groupBy: "source",
        reportTypeFilter: "calls-list",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the CTM Calls × Source × Answer Rate stacked-bar preset.
 *
 * Source-quality cross-tab — exposes paid-channel waste (e.g., Google Ads
 * 91% answer rate vs Microsoft Ads 75% answer rate). Mirrors the CallRail
 * 22.2 build-to-sell narrative but tied to CTM's data.
 */
export function expandCtmCallsBySourceAnswerRateStackedPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Calls × Source × Answer Rate",
      config: {
        metrics: ["answeredCalls", "missedCalls"],
        groupBy: "source",
        stacked: true,
        reportTypeFilter: "calls-list",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the CTM Calls List data-table preset.
 *
 * Per-call drill-down table. Columns: calledAtUtc (sorted DESC), source,
 * name (PII-safe synthetic), trackingNumber, dialStatus (color band),
 * talkTime, likelihood (0-1 Decimal with color band), sale.value (currency
 * null-coalesced to $0), sale.conversion (badge), tagList (badge cluster).
 */
export function expandCtmCallsListTablePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Calls List",
      config: {
        metricKeys: [
          "calledAtUtc",
          "source",
          "name",
          "trackingNumber",
          "dialStatus",
          "talkTime",
          "likelihood",
          "saleValue",
          "saleConversion",
          "tagList",
          // Story 22.5 — `id` is the CTM per-call numeric identifier; the
          // DataTable widget uses it as the recordingId source for the ▶
          // Play button.
          "id",
        ],
        reportTypeFilter: "calls-list",
        sortBy: "calledAtUtc",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
        // Story 22.5 AC #9 — Play button column config
        recordingColumn: {
          platform: "calltrackingmetrics",
          recordingIdField: "id",
        },
      },
      colSpan: 12,
      rowSpan: 4,
      autoSize: true,
    },
  ];
}

/**
 * Expand the CTM Conversation Intelligence Likelihood Quality Pie preset.
 *
 * **CRITICAL divergence from CallRail 22.2's Sentiment Pie:** CTM does NOT
 * ship a categorical `sentiment` field; CallRail's sentiment is
 * positive/neutral/negative (3 buckets), CTM's `likelihood` is Decimal 0-1
 * (continuous, bucketed by renderer into 4 quality bands).
 *
 * Quality bands: high (>=0.8) / good (0.6-0.8) / fair (0.4-0.6) /
 * low (<0.4). Config flag `bucketingMode: "likelihood-quartile"` signals
 * the renderer. Honors `conversationIntelligenceEntitled` flag for
 * graceful degradation (fixture-mode helper force-stamps `true`).
 */
export function expandCtmConversationIntelligenceLikelihoodQualityPiePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "pie-chart",
      title: "AI Likelihood Quality",
      config: {
        groupBy: "likelihood",
        metricKey: "totalCalls",
        reportTypeFilter: "calls-list",
        bucketingMode: "likelihood-quartile",
        requiresEntitlement: "conversationIntelligenceEntitled",
        upgradeUrl:
          "https://www.calltrackingmetrics.com/features/conversation-intelligence/",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the CTM Cross-Endpoint Scorecard preset (4 KPIs).
 *
 * **CTM-specific divergence from CallRail's scorecard**: CallRail had
 * totalCalls / answerRate / firstTimeCallerRate / leadConversionRate. CTM
 * substitutes `likelihoodAvg` for `firstTimeCallerRate` (CTM's AI-Likelihood
 * is a stronger flagship signal than first-time-caller detection in the
 * 2026 AI era) and uses inline-sale-derived `conversionRate` instead of
 * CallRail's lead-status-derived.
 */
export function expandCtmCrossEndpointScorecardPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "totalCalls", title: "Total Calls" },
    { metricKey: "answerRate", title: "Answer Rate" },
    { metricKey: "likelihoodAvg", title: "AI Likelihood Avg" },
    { metricKey: "conversionRate", title: "Conversion Rate" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

/**
 * Expand the CTM Omnichannel Touch Funnel preset (Story 22.4 BUILD-TO-SELL
 * CATEGORY-DEFINING differentiator — preset #9, NO CallRail analogue).
 *
 * No competitor reporting tool surfaces Calls + Forms + SMS in 1 widget —
 * verified 2026-05-15 across AgencyAnalytics + Databox + Whatagraph +
 * DashThis + Swydo May 2026 dashboard templates. Competitors treat the
 * surfaces as separate platforms; ProvenLeap treats CTM as ONE platform
 * with 3 `_reportType` discriminators, so the widget renders trivially.
 *
 * Stacked-bar with 3 segments per "All Channels" axis-label. Height
 * encodes total touches (65 Calls + 20 Forms + 25 SMS = 110); segments
 * encode per-surface contribution.
 *
 * THE widget that wins the omnichannel-enabled multi-location-HVAC /
 * multi-location-dental / enterprise-legal-services pitch.
 */
export function expandCtmOmnichannelTouchFunnelPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      // Code Review Round 2 Blind Hunter #13 — widget type aligned with
      // palette icon (`Funnel`) and label ("Omnichannel Touch Funnel").
      // Was `bar-chart` for stacked rendering, but the funnel widget
      // renders the 3-segment surface aggregation more honestly (the
      // 3 surfaces ARE funnel stages by nature: customers move
      // Calls → Forms → SMS or vice versa). The renderer accepts the
      // `surfaces` config either way.
      type: "funnel",
      title: "Omnichannel Touch Funnel",
      config: {
        metrics: [
          { key: "totalCalls", label: "Calls" },
          { key: "totalFormSubmissions", label: "Forms" },
          { key: "totalSmsMessages", label: "SMS" },
        ],
        // FIRST cross-surface-aggregation preset in the codebase. The
        // renderer reads all 3 wire-format `_reportType` discriminators
        // and aggregates `count()` per surface.
        surfaces: ["calls-list", "form-submissions", "sms"],
        aggregationMode: "count-per-surface",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

// ============================================================================
// Story 23.2 — Google Reviews Expanded Presets (8 — closes Epic 23 GR slice)
// ============================================================================

/**
 * Story 23.2 Preset #1 — Reviews Overview KPI (4 KPIs).
 *
 * Canonical 4-KPI agency-dashboard layout per AgencyAnalytics + Whatagraph
 * + Databox May 2026 verified 2026-05-16. Each KPI binds to a base
 * metric: totalReviewCount + averageRating + replyRate + reviewsInPeriod.
 */
export function expandGoogleReviewsOverviewKpiPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "totalReviewCount", title: "Total Reviews" },
    { metricKey: "averageRating", title: "Average Rating" },
    { metricKey: "replyRate", title: "Reply Rate" },
    { metricKey: "reviewsInPeriod", title: "Reviews in Period" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

/**
 * Story 23.2 Preset #2 — Rating Distribution bar-chart.
 *
 * 5-bar breakdown: oneStarCount → fiveStarCount. Surfaces the rating-mix
 * shape (e.g., bimodal distribution of 5★ enthusiasts + 1★ detractors).
 */
export function expandGoogleReviewsRatingDistributionBarPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Rating Distribution",
      config: {
        metrics: [
          { key: "fiveStarCount", label: "5★" },
          { key: "fourStarCount", label: "4★" },
          { key: "threeStarCount", label: "3★" },
          { key: "twoStarCount", label: "2★" },
          { key: "oneStarCount", label: "1★" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Story 23.2 Preset #3 — Sentiment Distribution pie-chart.
 *
 * 3-wedge rule-based sentiment: positive (4-5★) + neutral (3★) + negative
 * (1-2★). Google does NOT provide a sentiment field — derivation is
 * adapter-side via sentimentBucketForStar (google-reviews.ts:413).
 * Backed by the reviewSentiment _reportType records.
 */
export function expandGoogleReviewsSentimentPiePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "pie-chart",
      title: "Sentiment Distribution",
      config: {
        groupBy: "sentimentBucket",
        metricKey: "count",
        reportTypeFilter: "reviewSentiment",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Story 23.2 Preset #4 — Reply Rate gauge.
 *
 * 0-100% gauge with thresholds at 50/70/90. Legal-vertical benchmark is
 * 70-90% per AgencyAnalytics 2026 verified 2026-05-16.
 */
export function expandGoogleReviewsReplyRateGaugePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "gauge",
      title: "Reply Rate",
      config: {
        metricKey: "replyRate",
        min: 0,
        max: 100,
        // Pass 1 Blind Hunter M1 — thresholds aligned with the description
        // "legal-vertical benchmark 70-90%". Bands: <50 weak, 50-70 fair,
        // 70-90 good, ≥90 excellent.
        thresholds: [50, 70, 90],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 2,
    },
  ];
}

/**
 * Story 23.2 Preset #5 — Reviews Over Time line-chart.
 *
 * Client-side bucketed from `googleReview.createTime` because Google v4
 * has NO time-series endpoint. SAME pattern as CTM 22.4's
 * `ctm-calls-over-time` client-side daily bucketing. Renderer
 * aggregates `count()` per day from the googleReview records.
 */
export function expandGoogleReviewsReviewsOverTimePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Reviews Over Time",
      config: {
        metrics: [{ key: "reviewCount", label: "Reviews" }],
        xAxis: "createTime",
        aggregationMode: "count-per-day",
        reportTypeFilter: "googleReview",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Story 23.2 Preset #6 — Reviews List data-table.
 *
 * Per-review drill-down. Columns: createTime (DESC sort), reviewerName
 * (PII-safe synthetic), starRating (color-coded), sentimentBucket
 * (badge), comment (truncated), replyComment (truncated), replyState
 * (color-coded). Renders the build-to-sell "show me the reviews"
 * artifact in the agency-handoff PDF.
 */
export function expandGoogleReviewsReviewsListTablePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Reviews List",
      config: {
        metricKeys: [
          "createTime",
          "reviewerName",
          "starRating",
          "sentimentBucket",
          "comment",
          "replyComment",
          "replyState",
        ],
        reportTypeFilter: "googleReview",
        sortBy: "createTime",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
      autoSize: true,
    },
  ];
}

/**
 * Story 23.2 Preset #7 — Reply Moderation State pie-chart.
 *
 * **BUILD-TO-SELL CATEGORY-DEFINING WIDGET.** Surfaces Google's
 * 2026-04-01 `ReviewReplyState` API additive (PENDING / REJECTED /
 * APPROVED). NO competitor reporting tool (AgencyAnalytics / Databox /
 * Whatagraph / DashThis / Swydo May 2026 verified 2026-05-16) surfaces
 * this slice because their adapters predate the API change.
 *
 * Agency pitch: "Your competitor shows 92% reply rate publicly; you
 * show 78% — but 5% are stuck in PENDING moderation. That's solvable
 * today." No other reporting tool can answer this question.
 */
export function expandGoogleReviewsReplyModerationStatePiePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "pie-chart",
      title: "Reply Moderation State",
      config: {
        groupBy: "replyState",
        metricKey: "count",
        reportTypeFilter: "reviewReplyState",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Story 23.2 Preset #8 — Reputation Scorecard (4 KPIs).
 *
 * C-suite cross-endpoint scorecard. Total Reviews + Average Rating +
 * Reply Rate + Reputation Trend Direction (the synthetic-aggregate metric
 * from AC #11). The 4th KPI surfaces the rolling-month trajectory
 * (-1 declining / 0 flat / 1 improving) that the line-chart preset
 * shows over time, but compressed into a single up/down arrow KPI for
 * the cover-page scorecard.
 */
export function expandGoogleReviewsReputationScorecardPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "totalReviewCount", title: "Total Reviews" },
    { metricKey: "averageRating", title: "Average Rating" },
    { metricKey: "replyRate", title: "Reply Rate" },
    { metricKey: "reputationTrendDirection", title: "Reputation Trend" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

// ============================================================================
// Story 23.4 — Trustpilot Presets (closes Epic 23 Trustpilot slice)
// ============================================================================

/**
 * Story 23.4 Preset #1 — Trustpilot Overview KPI (4 KPIs).
 *
 * Canonical 4-KPI agency-dashboard layout per AgencyAnalytics + Whatagraph
 * + Databox May 2026 verified 2026-05-16. Each KPI binds to a base
 * metric: TrustScore + Stars + Total Reviews + Verified %.
 */
export function expandTrustpilotOverviewKpiPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "trustScore", title: "TrustScore" },
    { metricKey: "stars", title: "Stars" },
    { metricKey: "totalReviewCount", title: "Total Reviews" },
    { metricKey: "verifiedReviewPercentage", title: "Verified %" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

/**
 * Story 23.4 Preset #2 — Rating Distribution bar-chart.
 *
 * 5-bar top-to-bottom 5★ → 1★ — Trustpilot brand convention (verified
 * AgencyAnalytics + Trustpilot's own profile UI 2026-05-16). Opposite of
 * common low-to-high ascending bar charts.
 */
export function expandTrustpilotRatingDistributionBarPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Rating Distribution",
      config: {
        // Trustpilot brand convention: 5★ at top, 1★ at bottom.
        metrics: [
          { key: "fiveStarCount", label: "5★" },
          { key: "fourStarCount", label: "4★" },
          { key: "threeStarCount", label: "3★" },
          { key: "twoStarCount", label: "2★" },
          { key: "oneStarCount", label: "1★" },
        ],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Story 23.4 Preset #3 — Sentiment Distribution pie-chart.
 *
 * 3-wedge rule-based sentiment: positive (4-5★) + neutral (3★) + negative
 * (1-2★). Trustpilot does NOT provide a sentiment field — derivation is
 * adapter-side via trustpilotSentimentBucket (transformers.ts:37-43).
 */
export function expandTrustpilotSentimentPiePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "pie-chart",
      title: "Sentiment Distribution",
      config: {
        groupBy: "sentimentBucket",
        metricKey: "count",
        reportTypeFilter: "trustpilotReview",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Story 23.4 Preset #4 — Reply Rate gauge.
 *
 * 0-100% gauge with thresholds at 50/75/90. DTC benchmark 70-80% per
 * Opensend 2026 verified 2026-05-16.
 */
export function expandTrustpilotReplyRateGaugePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "gauge",
      title: "Reply Rate",
      config: {
        metricKey: "companyReplyRate",
        min: 0,
        max: 100,
        thresholds: [50, 75, 90],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 2,
    },
  ];
}

/**
 * Story 23.4 Preset #5 — Reviews Over Time line-chart.
 *
 * Client-side bucketed from `trustpilotReview.createdAt` because
 * Trustpilot v1 has NO time-series endpoint. SAME pattern as Google
 * Reviews 23.2's `google-reviews-reviews-over-time` client-side daily
 * bucketing. Renderer aggregates `count()` per day from per-review
 * records.
 */
export function expandTrustpilotReviewsOverTimePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Reviews Over Time",
      config: {
        metrics: [{ key: "reviewCount", label: "Reviews" }],
        xAxis: "createdAt",
        aggregationMode: "count-per-day",
        reportTypeFilter: "trustpilotReview",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Story 23.4 Preset #6 — Reviews List data-table.
 *
 * Per-review drill-down. Columns: createdAt (DESC sort), consumerDisplayName
 * (PII-safe synthetic), consumerDisplayLocation, stars (color-coded),
 * sentimentBucket (badge), isVerified, text (truncated), companyReplyText
 * (truncated), companyReplyCreatedAt, replyLatencySeconds, source
 * (paid-tier-gated column — hidden when metadata.privateApiEntitled
 * === false).
 */
export function expandTrustpilotReviewsListTablePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Reviews List",
      config: {
        // Use the unprefixed wire-field names that the record renderer
        // reads directly (matches GR 23.2's reviews-list-table convention
        // at widgetPresets.ts:4877-4885). The `reviewSentimentBucket` /
        // `reviewReplyLatencySeconds` keys in TRUSTPILOT_METRICS resolve
        // via `apiField` to these wire names, but data-table renders
        // properties directly off each record.
        metricKeys: [
          "createdAt",
          "consumerDisplayName",
          "consumerDisplayLocation",
          "stars",
          "sentimentBucket",
          "isVerified",
          "text",
          "companyReplyText",
          "companyReplyCreatedAt",
          "replyLatencySeconds",
          "source",
        ],
        reportTypeFilter: "trustpilotReview",
        sortBy: "createdAt",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
      autoSize: true,
    },
  ];
}

/**
 * Story 23.4 Preset #7 — Content Integrity Flag pie-chart.
 *
 * **BUILD-TO-SELL CATEGORY-DEFINING WIDGET.** Surfaces Trustpilot's
 * private-tier `complianceLabels` array distribution: None / Reported-Pending /
 * Defamation / Hate-Speech / Obscenity / Threats. NO competitor reporting
 * tool (AgencyAnalytics + Databox + Whatagraph + DashThis + Swydo May 2026
 * verified 2026-05-16) surfaces this slice because their adapters don't
 * have private-tier API depth.
 *
 * Renderer hides wedges with count 0 to avoid empty-pie visual noise.
 *
 * Agency pitch: "Your competitor has 8% of recent reviews flagged for
 * review by Trustpilot Content Integrity; yours has 1% — that's a
 * competitive moat signal." No other reporting tool can answer this
 * question.
 */
export function expandTrustpilotContentIntegrityFlagPiePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "pie-chart",
      title: "Content Integrity Flags",
      config: {
        groupBy: "flagType",
        metricKey: "count",
        reportTypeFilter: "contentIntegrityFlag",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Story 23.4 Preset #8 — Trustpilot Reputation Scorecard (4 KPIs).
 *
 * C-suite cross-endpoint scorecard. TrustScore + Total Reviews + Verified %
 * + **TrustScore-Eligible % (synthetic-aggregate — Trustpilot-specific
 * wedge)**. Per ADR-C, each platform's scorecard surfaces a platform-
 * specific transparency wedge as its 4th slot:
 *  - Google Reviews 23.2: Reputation Trend Direction
 *  - Trustpilot 23.4: TrustScore-Eligible % (the Bayesian-eligibility gap)
 *  - Future Yelp 23.6: TBD (likely filtered-review ratio)
 */
export function expandTrustpilotReputationScorecardPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "trustScore", title: "TrustScore" },
    { metricKey: "totalReviewCount", title: "Total Reviews" },
    { metricKey: "verifiedReviewPercentage", title: "Verified %" },
    { metricKey: "trustScoreEligibleReviewPercentage", title: "TrustScore-Eligible %" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

// ============================================================================
// Story 23.6 — Yelp Presets (closes Epic 23 Yelp slice + closes Epic 23 in full)
// ============================================================================
//
// Build-to-sell category-defining widget = preset #5
// (`yelp-rating-trend-over-time` — exploits Story 23.5's D1
// `yelp_rating_snapshots` workaround which makes a Rating × time
// line chart possible despite Yelp Fusion having no historical API.
// NO competitor reporting tool surfaces this widget per
// `agencyanalytics.com/integrations/yelp` + `trustindex.io/yelp-reviews-widget`
// + Databox + Whatagraph + DashThis + Swydo + Supermetrics May 2026
// verified 2026-05-18).

/**
 * Story 23.6 Preset #1 — Yelp Overview KPI grid.
 *
 * 4-KPI grid: Business Rating + Total Reviews + Price Tier + Listing Status.
 * Mirrors the canonical reviews-overview KPI layout (GR 23.2 + Trustpilot
 * 23.4 precedent).
 */
export function expandYelpOverviewKpiPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "businessRating", title: "Business Rating" },
    { metricKey: "reviewCount", title: "Total Reviews" },
    { metricKey: "priceTier", title: "Price Tier" },
    { metricKey: "isClaimed", title: "Listing Claimed" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

/**
 * Story 23.6 Preset #2 — Yelp Rating Distribution bar-chart.
 *
 * 5-bar top-to-bottom 5★ → 1★ — Yelp brand convention parallel to
 * Trustpilot's. Renders OVER THE 3-EXCERPT SUBSET (Yelp Fusion's
 * architectural cap — verified at docs.developer.yelp.com 2026-05-18).
 * Header annotation in the widget renderer clarifies the small-N
 * limitation ("Based on 3 most recent excerpts").
 */
export function expandYelpRatingDistributionBarPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Rating Distribution",
      config: {
        // Yelp brand convention: 5★ at top, 1★ at bottom.
        metrics: [
          { key: "fiveStarCount", label: "5★" },
          { key: "fourStarCount", label: "4★" },
          { key: "threeStarCount", label: "3★" },
          { key: "twoStarCount", label: "2★" },
          { key: "oneStarCount", label: "1★" },
        ],
        integrationPlatform: platform,
        integrationId,
        // Small-sample annotation surfaced by the renderer.
        annotation: "Based on 3 most recent excerpts (Yelp Fusion API limitation)",
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Story 23.6 Preset #3 — Yelp Sentiment Distribution pie-chart.
 *
 * 3-wedge rule-based sentiment: positive (4-5★) + neutral (3★) + negative
 * (1-2★). Yelp does NOT provide a sentiment field — derivation is adapter-
 * side via `yelpSentimentBucket` (transformers.ts:44). Renders over the
 * 3-excerpt subset (same small-N caveat as preset #2).
 */
export function expandYelpSentimentPiePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "pie-chart",
      title: "Sentiment Distribution",
      config: {
        groupBy: "sentimentBucket",
        metricKey: "count",
        reportTypeFilter: "reviewExcerpt",
        integrationPlatform: platform,
        integrationId,
        annotation: "Based on 3 most recent excerpts (Yelp Fusion API limitation)",
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Story 23.6 Preset #4 — Yelp Review Count Growth line-chart.
 *
 * Daily Review Count over the 45-day snapshot window — Story 23.5 D1
 * `yelp_rating_snapshots` source; reads from `ratingSnapshot` records.
 * The "review growth velocity" wedge.
 */
export function expandYelpReviewCountGrowthLinePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Review Count Growth",
      config: {
        metrics: [{ key: "reviewCount", label: "Review Count" }],
        xAxis: "snapshotDate",
        reportTypeFilter: "ratingSnapshot",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Story 23.6 Preset #5 — Yelp Rating Trend Over Time line-chart.
 *
 * **BUILD-TO-SELL CATEGORY-DEFINING WIDGET.** Surfaces Yelp's D1
 * `yelp_rating_snapshots` workaround as a Rating × time line chart.
 * NO competitor reporting tool (AgencyAnalytics + Trustindex + Databox +
 * Whatagraph + DashThis + Swydo + Supermetrics May 2026 verified
 * 2026-05-18) ships this widget because: (a) Yelp Fusion's API has NO
 * historical endpoint; (b) no other vendor persists Yelp snapshots
 * themselves. ProvenLeap's Story 23.5 D1 table is the architectural
 * moat that makes this widget possible.
 *
 * Agency pitch: "Your client's Yelp Rating climbed from 4.1 to 4.4 over
 * 45 days — the result of your reputation-management campaign. No other
 * reporting tool can show this curve because no other vendor persists
 * Yelp snapshots."
 */
export function expandYelpRatingTrendOverTimePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Rating Trend (45-day)",
      config: {
        metrics: [{ key: "rating", label: "Business Rating" }],
        xAxis: "snapshotDate",
        reportTypeFilter: "ratingSnapshot",
        yAxisMin: 0,
        yAxisMax: 5,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Story 23.6 Preset #6 — Yelp Tier Coverage annotation card.
 *
 * Empty-state card surfacing Starter / Plus / Enterprise tier UX as a
 * first-class state. When `metadata.reviewsEndpointEntitled === false`
 * (production Starter-tier customer), the widget renders an "Upgrade to
 * Yelp Fusion Plus" CTA. In fixture mode (forced Plus tier) the Plus row
 * is highlighted as the active tier.
 *
 * No competitor surfaces an inline tier-limitation annotation card
 * (verified May 2026). Build-to-sell payoff: prospective acquirers see
 * how ProvenLeap surfaces paid-tier reality as UX state rather than
 * hiding the dropoff.
 */
export function expandYelpTierCoverageAnnotationPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "kpi",
      title: "Tier Coverage",
      config: {
        metricKey: "tier",
        // Custom widget config — the renderer reads `metadata.tier` +
        // `metadata.reviewsEndpointEntitled` to render the active row +
        // upgrade CTA.
        widgetVariant: "tier-coverage-annotation",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 1,
    },
  ];
}

/**
 * Story 23.6 Preset #7 — Yelp Review Excerpts List data-table.
 *
 * Per-excerpt drill-down. Columns: timeCreated (DESC sort), userName
 * (PII-safe synthetic), rating (color-coded), sentimentBucket (badge),
 * text (160-char truncated), reviewUrl (Yelp deep-link). 3 rows max
 * (Enhanced cap; Premium tier 7 reserved for future).
 *
 * Empty-state when `metadata.reviewsEndpointEntitled === false`.
 */
export function expandYelpReviewExcerptsListTablePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Review Excerpts",
      config: {
        metricKeys: [
          "reviewCreatedTime",
          "userName",
          "reviewRating",
          "sentimentBucket",
          "reviewExcerptText",
          "reviewUrl",
        ],
        reportTypeFilter: "reviewExcerpt",
        sortBy: "reviewCreatedTime",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 2,
      autoSize: true,
    },
  ];
}

/**
 * Story 23.6 Preset #8 — Yelp Reputation Scorecard (4 KPIs).
 *
 * C-suite cross-endpoint scorecard. Business Rating + Total Reviews +
 * Review Velocity (per month) + **Rating Trend Direction (synthetic-
 * aggregate — Yelp-specific wedge per ADR-C)**. Per ADR-C, each
 * platform's scorecard surfaces a platform-specific transparency wedge
 * as its 4th slot:
 *  - Google Reviews 23.2: Reputation Trend Direction
 *  - Trustpilot 23.4: TrustScore-Eligible %
 *  - Yelp 23.6: Rating Trend Direction (the snapshot-derived signal)
 */
export function expandYelpReputationScorecardPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "businessRating", title: "Business Rating" },
    { metricKey: "reviewCount", title: "Total Reviews" },
    { metricKey: "reviewVelocityPerMonth", title: "Reviews / Month" },
    { metricKey: "ratingTrendDirection", title: "Rating Trend" },
  ];
  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi" as const,
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

// ============================================================================
// IMC-18: Mailchimp Expanded Presets
// ============================================================================

/**
 * Mailchimp Revenue preset — 3 KPI widgets for e-commerce metrics.
 * Requires Mailchimp connected store (Shopify, WooCommerce).
 */
export function expandMailchimpRevenuePreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "revenue", title: "Revenue" },
    { metricKey: "orders", title: "Orders" },
    { metricKey: "avg_order_revenue", title: "Avg Order Revenue" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 4,
    rowSpan: 1,
  }));
}

/**
 * Mailchimp Benchmark preset — benchmark widget comparing campaign rates
 * to industry averages from Mailchimp's industry_stats.
 *
 * Note: `benchmarkMetricKey` is forward-looking config for when the benchmark
 * widget supports side-by-side comparison rendering. Until then, it serves as
 * metadata indicating which industry metric to pair with the client metric.
 */
export function expandMailchimpBenchmarkPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "benchmark",
      title: "Open Rate vs Industry",
      config: {
        metricKey: "average_open_rate",
        benchmarkMetricKey: "industry_open_rate",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 4,
    },
    {
      id: crypto.randomUUID(),
      type: "benchmark",
      title: "Click Rate vs Industry",
      config: {
        metricKey: "average_click_rate",
        benchmarkMetricKey: "industry_click_rate",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 4,
    },
  ];
}

/**
 * Mailchimp Click Map preset — data table showing per-URL click performance.
 */
export function expandMailchimpClickMapPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "URL Click Performance",
      config: {
        reportType: "clickMap",
        dimensionKey: "url",
        metricKeys: ["url_total_clicks", "url_unique_clicks", "url_click_percentage"],
        sortBy: "url_total_clicks",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Mailchimp Automation preset — data table showing per-workflow automation metrics.
 */
export function expandMailchimpAutomationPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Automation Performance",
      config: {
        reportType: "automation",
        dimensionKey: "automation_workflow",
        metricKeys: ["automation_opens", "automation_clicks", "automation_open_rate", "automation_click_rate"],
        sortBy: "automation_opens",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

// ============================================================================
// Klaviyo Presets (Story 12.8)
// ============================================================================
//
// These 6 presets use Klaviyo adapter metric keys directly (Option B: platform-
// namespaced presets, not shared with Mailchimp). See Story 12.8 AC #11 and the
// "Preset Strategy" Dev Notes section for rationale.
//
// Key contract:
// - Campaign/Flow/Form/Segment record types come from the Reporting API and are
//   **window totals**, not daily series. Data tables use reportType filters to
//   scope records appropriately.
// - Daily time-series (e.g. segment growth line chart) relies on dailyOverview
//   records from metric-aggregates if present, otherwise degrades gracefully.

/**
 * Klaviyo Overview preset — 5 KPI widgets covering core email performance.
 * Mirrors email-overview-kpi but uses Klaviyo adapter keys directly.
 *
 * - emails_sent: derived as delivered + bounced (Klaviyo has no "Sent Email" event)
 * - emails_delivered: metric-aggregates `Delivered Email` event
 * - open_rate / click_rate: derived in summary record
 * - unsubscribe_rate: derived in summary record
 *
 * Note: `list_total_members` (from accountMetadata.listTotalMembers) is not used here
 * because Story 12.8 does not populate that field end-to-end — the wire-up from list
 * selection to accountMetadata is deferred to a follow-up story. Users who want list
 * size can add the Segment Growth preset instead (total_members from Reporting API).
 */
export function expandKlaviyoOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string; showPrivacyDisclaimer?: boolean }[] = [
    { metricKey: "emails_sent", title: "Emails Sent" },
    { metricKey: "emails_delivered", title: "Emails Delivered" },
    { metricKey: "open_rate", title: "Open Rate", showPrivacyDisclaimer: true },
    { metricKey: "click_rate", title: "Click Rate" },
    { metricKey: "unsubscribe_rate", title: "Unsubscribe Rate" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
      ...(kpi.showPrivacyDisclaimer ? { showPrivacyDisclaimer: true } : {}),
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Klaviyo Campaign Performance Table preset.
 * Data-table filtered to _reportType === "campaign", showing per-campaign stats.
 * Title includes "(period total)" to communicate the window-totals design —
 * Reporting API records are NOT daily series.
 */
export function expandKlaviyoCampaignTablePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Campaign Performance (period total)",
      config: {
        reportType: "campaign",
        metricKeys: ["recipients", "open_rate", "click_rate", "conversion_value"],
        dimensionKey: "campaignName",
        sortBy: "recipients",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

/**
 * Klaviyo Top Campaigns preset.
 * Data-table sorted by open_rate desc, limited to email campaigns (sendChannel === "email"),
 * with Apple MPP privacy disclaimer. Shows the best-performing email campaigns.
 */
export function expandKlaviyoTopCampaignsPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Top Klaviyo Campaigns",
      config: {
        reportType: "campaign",
        metricKeys: ["open_rate", "click_rate", "recipients", "conversion_value"],
        dimensionKey: "campaignName",
        sortBy: "open_rate",
        sortDirection: "desc",
        limit: 5,
        sendChannelFilter: ["email"],
        showPrivacyDisclaimer: true,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

/**
 * Klaviyo Flow Performance preset.
 * Data-table filtered to _reportType === "flow". Each row is a flow message
 * (one flow can have multiple messages). Window total, not daily series.
 */
export function expandKlaviyoFlowPerformancePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Flow Performance (period total)",
      config: {
        reportType: "flow",
        metricKeys: ["recipients", "open_rate", "click_rate", "conversion_value"],
        dimensionKey: "flowMessageName",
        sortBy: "recipients",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

/**
 * Klaviyo Form Conversion preset.
 * 4 KPI widgets covering the full form analytics funnel:
 * views → submissions → submit rate → qualified visitors.
 */
export function expandKlaviyoFormConversionPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "viewed_form", title: "Form Views" },
    { metricKey: "submits", title: "Form Submissions" },
    { metricKey: "submit_rate", title: "Submit Rate" },
    { metricKey: "qualified_form", title: "Qualified Visitors" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      reportType: "form",
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));
}

/**
 * Klaviyo Segment Growth preset.
 * Line chart of net_members_changed per segment over the period.
 * Uses _reportType === "segment" — if only a single entry per segment is
 * available (window total), renders as a bar-style single-point series.
 */
export function expandKlaviyoSegmentGrowthPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Segment Growth",
      config: {
        reportType: "segment",
        metrics: [{ key: "net_members_changed", label: "Net Members Change" }],
        xAxis: "segmentName",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

// ============================================================================
// HubSpot CRM Presets (Story 25.4)
// ============================================================================

/**
 * Expand the Pipeline Overview KPI preset into 5 KPI widgets.
 * Metrics: deals_total, pipeline_value, revenue_closed_won, win_rate, avg_days_to_close
 */
export function expandHubSpotPipelineOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "deals_total", title: "Total Deals" },
    { metricKey: "pipeline_value", title: "Pipeline Value" },
    { metricKey: "revenue_closed_won", title: "Closed Won Revenue" },
    { metricKey: "win_rate", title: "Win Rate" },
    { metricKey: "avg_days_to_close", title: "Avg Days to Close" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * Expand the Deal Stage Funnel preset into 1 funnel widget.
 * Groups deals by pipeline stage using dealsByStage reportType.
 */
export function expandHubSpotDealFunnelPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "funnel",
      title: "Deal Stage Funnel",
      config: {
        groupBy: "deal_stage",
        metricKey: "deals_by_stage",
        comparisonEnabled: true,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Lifecycle Funnel preset into 1 funnel widget.
 * Static funnel reading lifecycle stage counts from summary record.
 */
export function expandHubSpotLifecycleFunnelPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "funnel",
      title: "Lifecycle Funnel",
      config: {
        metrics: [
          { key: "subscriber_count", label: "Subscriber" },
          { key: "lead_count", label: "Lead" },
          { key: "mql_count", label: "MQL" },
          { key: "sql_count", label: "SQL" },
          { key: "opportunity_count", label: "Opportunity" },
          { key: "customer_count", label: "Customer" },
        ],
        comparisonEnabled: true,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Lead Source Breakdown preset into 1 pie chart widget.
 * Contacts grouped by original source.
 */
export function expandHubSpotLeadSourcePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "pie-chart",
      title: "Lead Source Breakdown",
      config: {
        groupBy: "source",
        metricKey: "contacts_count",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Deals by Owner preset into 1 bar chart widget.
 * Deal count + amount grouped by owner.
 */
export function expandHubSpotDealsByOwnerPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Deals by Owner",
      config: {
        groupBy: "owner",
        metrics: [{ key: "deals_count", label: "Deals" }],
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Revenue Trend preset into 1 line chart widget.
 * Daily revenue_closed_won over time.
 */
export function expandHubSpotRevenueTrendPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Revenue Trend",
      config: {
        metrics: [{ key: "revenue_closed_won", label: "Closed Won Revenue" }],
        xAxis: "date",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 2,
    },
  ];
}

/**
 * Expand the Deals Table preset into 1 data-table widget.
 * Deal stage breakdown with count, amount, avg deal size columns.
 */
export function expandHubSpotDealsTablePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Deals by Stage",
      config: {
        metricKeys: ["deals_by_stage", "pipeline_value"],
        dimensionKey: "deal_stage",
        reportType: "dealsByStage",
        sortBy: "deals_by_stage",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
      autoSize: true,
    },
  ];
}

/**
 * Expand the Lead Source Detail preset into 1 data-table widget.
 * Contacts by source with drill-down 1 channel breakdown (Story 25.5, AC #6).
 */
export function expandHubSpotLeadSourceDetailPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Lead Source Detail",
      config: {
        metricKeys: ["contacts_count"],
        dimensionKey: "source_detail",
        reportType: "contactsBySourceDetail",
        sortBy: "contacts_count",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
      autoSize: true,
    },
  ];
}

/**
 * Expand the First vs Last Touch preset into 2 side-by-side pie-chart widgets (Story 25.5, AC #7).
 * Option A: Two pie charts — the bar-chart transformer does not support per-metric reportType overrides.
 */
export function expandHubSpotFirstVsLastTouchPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "pie-chart",
      title: "Lead Sources (First Touch)",
      config: {
        groupBy: "source",
        metricKey: "contacts_count",
        reportType: "contactsBySource",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
    {
      id: crypto.randomUUID(),
      type: "pie-chart",
      title: "Lead Sources (Last Touch)",
      config: {
        groupBy: "latest_source",
        metricKey: "contacts_count",
        reportType: "contactsByLatestSource",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * Expand the Attribution Trend preset into 1 line-chart widget (Story 25.5, AC #8).
 * Daily new contacts by top 5 sources over time.
 */
export function expandHubSpotAttributionTrendPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Attribution Trend",
      config: {
        metrics: [{ key: "contacts_count", label: "New Contacts" }],
        xAxis: "date",
        groupBy: "source",
        reportType: "dailyContactsBySource",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

// ============================================================================
// HubSpot Email Presets (Story 25.6)
// ============================================================================

/**
 * HubSpot Email Overview — 5 KPI widgets.
 * Metrics: email_sent, email_delivered, email_open_rate, email_click_rate, email_unsubscribed_rate.
 */
export function expandHubSpotEmailOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "email_sent", title: "Emails Sent" },
    { metricKey: "email_delivered", title: "Delivered" },
    { metricKey: "email_open_rate", title: "Open Rate" },
    { metricKey: "email_click_rate", title: "Click Rate" },
    { metricKey: "email_unsubscribed_rate", title: "Unsubscribe Rate" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * HubSpot Email Campaign Table — per-email stats data table sorted by sent count.
 */
export function expandHubSpotEmailCampaignTablePreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "Email Campaigns",
      config: {
        metricKeys: ["email_campaign_sent", "email_campaign_open_rate", "email_campaign_click_rate"],
        dimensionKey: "email_name",
        reportType: "emailCampaigns",
        sortBy: "email_campaign_sent",
        sortDirection: "desc",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
      autoSize: true,
    },
  ];
}

/**
 * HubSpot Email Device Breakdown — pie chart of opens by device type.
 */
export function expandHubSpotEmailDeviceBreakdownPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "pie-chart",
      title: "Email Opens by Device",
      config: {
        groupBy: "device_type",
        metricKey: "open_count",
        reportType: "emailDeviceBreakdown",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * HubSpot Email Funnel — Sent → Delivered → Opened → Clicked.
 */
export function expandHubSpotEmailFunnelPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "funnel",
      title: "Email Engagement Funnel",
      config: {
        metrics: [
          { key: "email_sent", label: "Sent" },
          { key: "email_delivered", label: "Delivered" },
          { key: "email_open", label: "Opened" },
          { key: "email_click", label: "Clicked" },
        ],
        comparisonEnabled: true,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

// ============================================================================
// HubSpot Traffic Analytics Presets (Story 25.7)
// ============================================================================

/**
 * HubSpot Traffic Overview — 5 KPI widgets (AC #7).
 * Metrics: traffic_sessions, traffic_visitors, traffic_pageviews, traffic_bounce_rate, traffic_avg_session_duration.
 */
export function expandHubSpotTrafficOverviewPreset(platform: string, integrationId: string): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "traffic_sessions", title: "Sessions" },
    { metricKey: "traffic_visitors", title: "Visitors" },
    { metricKey: "traffic_pageviews", title: "Page Views" },
    { metricKey: "traffic_bounce_rate", title: "Bounce Rate" },
    { metricKey: "traffic_avg_session_duration", title: "Avg Session Duration" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

/**
 * HubSpot Traffic Sources — pie chart of sessions by source type (AC #7).
 * Uses trafficBySource reportType grouped by traffic_source_type.
 */
export function expandHubSpotTrafficSourcesPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "pie-chart",
      title: "Traffic Sources",
      config: {
        metricKey: "sessions",
        groupBy: "traffic_source_type",
        reportType: "trafficBySource",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 3,
    },
  ];
}

/**
 * HubSpot Traffic Trend — line chart of daily sessions + visitors (AC #7).
 * R2-H1: Uses string[] metric keys (transformer + LineChartWidget contract),
 * not {key, label} objects.
 */
export function expandHubSpotTrafficTrendPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "line-chart",
      title: "Traffic Trend",
      config: {
        metrics: ["sessions", "visitors"],
        xAxis: "date",
        reportType: "dailyTraffic",
        comparisonEnabled: true,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 2,
    },
  ];
}

// ============================================================================
// HubSpot Marketing Campaign Presets (Story 25.8)
// ============================================================================

/**
 * HubSpot Campaign ROI — data-table showing per-campaign sessions, contacts,
 * deals, and revenue, sorted by revenue descending (AC #6).
 *
 * Uses campaignPerformance reportType with campaign_name dimension.
 */
export function expandHubSpotCampaignRoiPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "HubSpot Campaign ROI",
      config: {
        metricKeys: [
          "campaign_sessions",
          "campaign_influenced_contacts",
          "campaign_deals_number",
          "campaign_revenue_amount",
        ],
        groupBy: "campaign_name",
        reportType: "campaignPerformance",
        sortBy: "campaign_revenue_amount",
        sortDirection: "desc",
        limit: 20,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * HubSpot Campaign Attribution — bar-chart comparing first-touch vs last-touch
 * new contacts per campaign (AC #6). Two series grouped by campaign_name.
 */
export function expandHubSpotCampaignAttributionPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "HubSpot Campaign Attribution",
      config: {
        metrics: [
          "campaign_new_contacts_first_touch",
          "campaign_new_contacts_last_touch",
        ],
        groupBy: "campaign_name",
        reportType: "campaignPerformance",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

// ============================================================================
// Story 25.9: HubSpot Companies, Tickets & Engagements Presets
// ============================================================================

/**
 * HubSpot Company Overview — 3 KPI widgets (AC #6).
 * Shows total companies, new companies in period, and the top industry's share.
 *
 * Note: AC #6 calls for a "top industry" KPI. KPI widgets bind to scalar metrics;
 * we surface the count of companies in the top industry via `companies_by_industry`
 * from `companiesByIndustry` records (sorted descending — first record = top).
 * The widget shows the count; the industry name is available on the same record
 * if the renderer wants to show "N companies in <industry>" labels.
 */
export function expandHubSpotCompanyOverviewPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "kpi",
      title: "Total Companies",
      config: {
        metricKey: "companies_total",
        comparisonEnabled: true,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 4,
      rowSpan: 1,
    },
    {
      id: crypto.randomUUID(),
      type: "kpi",
      title: "New Companies",
      config: {
        metricKey: "companies_new",
        comparisonEnabled: true,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 4,
      rowSpan: 1,
    },
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "Companies by Industry",
      config: {
        metricKey: "companies_by_industry",
        groupBy: "industry",
        reportType: "companiesByIndustry",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 4,
      rowSpan: 2,
    },
  ];
}

/**
 * HubSpot Ticket Summary — 4 KPI widgets (AC #6).
 * Total tickets, open/closed split via ticketsByStatus pie, avg response and
 * close times. `avg_time_to_first_response` and `avg_time_to_close` are stored
 * as milliseconds — the KPI formatter handles duration display.
 */
export function expandHubSpotTicketSummaryPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "tickets_total", title: "Total Tickets" },
    { metricKey: "avg_time_to_first_response", title: "Avg Response Time" },
    { metricKey: "avg_time_to_close", title: "Avg Close Time" },
  ];

  const widgets: PresetWidget[] = kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 1,
  }));

  widgets.push({
    id: crypto.randomUUID(),
    type: "pie-chart",
    title: "Tickets by Status",
    config: {
      metricKey: "tickets_by_status",
      groupBy: "ticket_status",
      reportType: "ticketsByStatus",
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 3,
    rowSpan: 2,
  });

  return widgets;
}

/**
 * HubSpot Sales Activity — 5 KPI widgets (AC #6).
 * Calls, meetings, CRM-tracked emails, notes, completed tasks for the period.
 */
export function expandHubSpotSalesActivityPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  const kpis: { metricKey: string; title: string }[] = [
    { metricKey: "total_calls", title: "Calls" },
    { metricKey: "total_meetings", title: "Meetings" },
    { metricKey: "total_emails_crm", title: "Emails (CRM)" },
    { metricKey: "total_notes", title: "Notes" },
    { metricKey: "total_tasks_completed", title: "Completed Tasks" },
  ];

  return kpis.map((kpi) => ({
    id: crypto.randomUUID(),
    type: "kpi",
    title: kpi.title,
    config: {
      metricKey: kpi.metricKey,
      comparisonEnabled: true,
      integrationPlatform: platform,
      integrationId,
    },
    colSpan: 2,
    rowSpan: 1,
  }));
}

// ============================================================================
// Story 25.10: HubSpot Forms & Marketing Events Presets
// ============================================================================

/**
 * HubSpot Form Performance — data-table sorted descending by submissions.
 * Shows form name, submissions count, and conversion rate per form
 * (conversion rate renders as "—" because form views aren't exposed by the API).
 *
 * Uses formSubmissions reportType with form_name dimension.
 */
export function expandHubSpotFormPerformancePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "HubSpot Form Performance",
      config: {
        // Use the registry-canonical keys (form_submissions_count matches
        // HUBSPOT_METRICS; form_conversion_rate registered as metric #114).
        metricKeys: ["form_submissions_count", "form_conversion_rate"],
        groupBy: "form_name",
        reportType: "formSubmissions",
        sortBy: "form_submissions_count",
        sortDirection: "desc",
        limit: 20,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * HubSpot Event Attendance — bar-chart comparing registrations vs attended
 * per marketing event. Two series grouped by event_name.
 *
 * Uses eventPerformance reportType.
 */
export function expandHubSpotEventAttendancePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "HubSpot Event Attendance",
      config: {
        // `registrations` and `attended` are per-record aliases emitted by
        // `processMarketingEvents` on each `eventPerformance` record. The
        // corresponding summary-level registry keys are `total_event_registrations`
        // and `total_event_attendees`; those aren't used here because this widget
        // groups by event (per-record), not summary.
        metrics: ["registrations", "attended"],
        groupBy: "event_name",
        reportType: "eventPerformance",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

// ============================================================================
// Story 25.11: HubSpot Workflows + Deal Forecast + Geography Presets
// ============================================================================

/**
 * HubSpot Workflow Performance — data-table sorted descending by enrollments.
 * Shows workflow name, total enrollments, and goal completion rate per flow.
 *
 * Uses workflowPerformance reportType (Story 25.11, AC #7).
 *
 * **Data window:** HubSpot Automation v4 BETA exposes only 6 months of
 * enrollment history — older enrollments are lost. Widget description notes
 * this limitation so agencies know the reported numbers cap at 6 months.
 */
export function expandHubSpotWorkflowPerformancePreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "HubSpot Workflow Performance",
      config: {
        // Emit both registry-canonical keys and short record aliases so the
        // data-table can bind to either form. `enrollments` / `conversion_rate`
        // match the per-record fields; registry keys match HUBSPOT_METRICS.
        metricKeys: ["total_workflow_enrollments", "workflow_goal_conversion_rate"],
        groupBy: "workflow_name",
        reportType: "workflowPerformance",
        sortBy: "total_workflow_enrollments",
        sortDirection: "desc",
        limit: 20,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

/**
 * HubSpot Contact Geography — bar-chart showing contacts grouped by country.
 * Uses contactsByCountry reportType (Story 25.11, AC #7).
 */
export function expandHubSpotContactGeographyPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "HubSpot Contact Geography",
      config: {
        // `contacts_count` is the per-record field on `contactsByCountry`; the
        // `contacts_by_country` alias matches HUBSPOT_METRICS registry key for
        // widgets that prefer the registry-canonical form.
        metricKey: "contacts_by_country",
        groupBy: "country",
        reportType: "contactsByCountry",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

/**
 * HubSpot Deal Forecast — data-table showing each deal's stage, forecast
 * probability, and amount. Sorted descending by amount.
 * Uses dealForecast reportType (Story 25.11, AC #7).
 */
export function expandHubSpotDealForecastPreset(
  platform: string,
  integrationId: string,
): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "HubSpot Deal Forecast",
      config: {
        // Per-record aliases on `dealForecast`: deal_stage + forecast_probability
        // + amount. The registry key `deal_forecast_probability` is the same
        // value re-emitted so widgets bound by canonical key still resolve.
        metricKeys: ["deal_forecast_probability", "amount"],
        groupBy: "deal_stage",
        reportType: "dealForecast",
        sortBy: "amount",
        sortDirection: "desc",
        limit: 20,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 4,
    },
  ];
}

// ============================================================================
// IMC-20: Google Business Profile Presets
// ============================================================================

/**
 * GBP Review Breakdown — bar-chart showing reviews by star rating (1-5 stars).
 * Uses reviewsByRating reportType with star_rating dimension.
 */
export function expandGBPReviewBreakdownPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "bar-chart",
      title: "GBP Review Breakdown",
      config: {
        metricKey: "review_count",
        groupBy: "star_rating",
        reportType: "reviewsByRating",
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 6,
      rowSpan: 2,
    },
  ];
}

/**
 * GBP Search Keywords — data-table showing top 20 keywords by impressions.
 * Uses searchKeywords reportType with search_keyword dimension.
 */
export function expandGBPSearchKeywordsPreset(platform: string, integrationId: string): PresetWidget[] {
  return [
    {
      id: crypto.randomUUID(),
      type: "data-table",
      title: "GBP Search Keywords",
      config: {
        metricKeys: ["keywordImpressions"],
        groupBy: "search_keyword",
        reportType: "searchKeywords",
        sortBy: "keywordImpressions",
        sortDirection: "desc",
        limit: 20,
        integrationPlatform: platform,
        integrationId,
      },
      colSpan: 12,
      rowSpan: 3,
    },
  ];
}

export const PRESET_LABELS: Record<PresetType, string> = {
  "email-overview-kpi": "Email Overview",
  "email-campaign-table": "Campaign Performance",
  "email-subscriber-growth": "Subscriber Growth",
  "email-top-campaigns": "Top Campaigns",
  "ecommerce-sales-overview-kpi": "Sales Overview",
  "ecommerce-revenue-trend": "Revenue Trend",
  "ecommerce-top-products": "Top Products",
  "ecommerce-orders-conversion": "Orders & Conversion",
  "ecommerce-product-performance": "Product Performance",
  "ecommerce-profit": "Profit Metrics",
  "ecommerce-customer": "Customer Insights",
  "ecommerce-cart-abandonment": "Cart Abandonment",
  "ecommerce-geo": "Sales by Geography",
  "instagram-overview-kpi": "Instagram Overview",
  "instagram-reels-table": "Reels Performance",
  "instagram-demographics": "Follower Demographics",
  "facebook-pages-overview-kpi": "Facebook Overview",
  "facebook-pages-video": "Video Performance",
  "facebook-pages-reactions": "Reactions Breakdown",
  "tiktok-ads-overview-kpi": "TikTok Ads Overview",
  "tiktok-ads-video-funnel": "Video View Funnel",
  "tiktok-ads-campaign-table": "Campaign Performance",
  "tiktok-organic-overview-kpi": "TikTok Organic Overview",
  "tiktok-organic-video-table": "Video Performance",
  "google-ads-overview-kpi": "Google Ads Overview",
  "google-ads-impression-share": "Impression Share",
  "google-ads-keyword-table": "Keyword Performance",
  "google-ads-video-funnel": "Video Funnel",
  "google-ads-shopping-table": "Shopping Performance",
  "pagespeed-scorecard-kpi": "PageSpeed Scorecard",
  "pagespeed-core-web-vitals": "Core Web Vitals",
  "pagespeed-crux-history": "CrUX History Trend",
  "ga4-traffic-overview-kpi": "GA4 Traffic Overview",
  "ga4-ecommerce-funnel": "E-commerce Funnel",
  "ga4-revenue": "Revenue Dashboard",
  "ga4-acquisition": "Acquisition Report",
  "youtube-overview-kpi": "YouTube Overview",
  "youtube-video-table": "Top Videos",
  "youtube-audience": "Audience Demographics",
  "linkedin-ads-overview-kpi": "LinkedIn Ads Overview",
  "linkedin-ads-video-funnel": "Video Funnel",
  "linkedin-ads-demographics": "Professional Demographics",
  "linkedin-ads-lead-gen": "Lead Generation",
  "meta-ads-overview-kpi": "Meta Ads Overview",
  "meta-ads-video-funnel": "Video Funnel",
  "meta-ads-conversion-table": "Conversion Performance",
  "meta-ads-quality-table": "Quality Rankings",
  // Story 19.3: Microsoft Ads presets
  "microsoft-ads-overview-kpi": "Microsoft Ads Overview",
  "microsoft-ads-impression-share": "Impression Share",
  "microsoft-ads-keyword-quality-table": "Quality Score Keywords",
  "microsoft-ads-campaign-performance": "Campaign Performance",
  "microsoft-ads-pmax-final-url-table": "PMax Final URL",
  // Story 19.6: Pinterest Ads presets
  "pinterest-ads-overview-kpi": "Pinterest Ads Overview",
  "pinterest-ads-paid-vs-earned": "Paid vs Earned Impressions",
  "pinterest-ads-saves-performance": "Saves Performance",
  "pinterest-ads-video-funnel": "Pinterest Video Funnel",
  "pinterest-ads-conversion-roas-table": "Conversion ROAS Table",
  "pinterest-ads-cross-device-attribution-table": "Cross-Device Attribution",
  // Story 19.9: Snapchat Ads presets
  "snapchat-ads-overview-kpi": "Snapchat Ads Overview",
  "snapchat-ads-swipe-up-funnel": "Snap Swipe-Up Funnel",
  "snapchat-ads-video-quartile-funnel": "Snap Video Quartile Funnel",
  "snapchat-ads-app-vs-web-conversions": "App vs Web Conversions",
  "snapchat-ads-demographic-breakdown": "Snap Demographic Breakdown",
  "snapchat-ads-conversion-roas-table": "Snap Conversion ROAS Table",
  // Story 19.12: Reddit Ads presets
  "reddit-ads-overview-kpi": "Reddit Ads Overview",
  "reddit-ads-subreddit-performance": "Subreddit Performance",
  "reddit-ads-conversion-attribution-funnel": "Conversion Funnel (Click)",
  "reddit-ads-video-completion-funnel": "Reddit Video Completion",
  "reddit-ads-geo-country-region": "Geo by Country",
  "reddit-ads-click-vs-view-attribution": "Click vs View Attribution",
  // Story 19.17: Amazon Ads presets (closes Epic 19)
  "amazon-ads-overview-kpi": "Amazon Ads Overview",
  "amazon-ads-advertised-product-performance": "Advertised Product Performance",
  "amazon-ads-sb-ntb-funnel": "SB New-to-Brand Funnel",
  "amazon-ads-sd-click-vs-view": "SD Click vs View Attribution",
  "amazon-ads-dsp-audience-performance": "DSP Audience Performance",
  "amazon-ads-dsp-brand-store-engagement-funnel": "DSP Brand Store Engagement",
  "amazon-ads-acos-roas-trend": "ACOS / ROAS Trend",
  "amazon-ads-cross-surface-comparison": "Cross-Surface Comparison",
  // Story 20.3: Ahrefs presets (closes Epic 20 Ahrefs slice)
  "ahrefs-domain-rating-gauge": "Domain Rating Gauge",
  "ahrefs-backlink-profile-overview": "Backlink Profile Overview",
  "ahrefs-organic-traffic-value-trend": "Organic Traffic Value Trend",
  "ahrefs-keyword-position-distribution": "Keyword Position Distribution",
  "ahrefs-site-audit-health-score": "Site Audit Health Score",
  "ahrefs-ai-visibility-sov": "AI Visibility — Share of Voice",
  "ahrefs-web-analytics-overview": "Web Analytics Overview",
  "ahrefs-cross-endpoint-kpi-bundle": "Cross-Endpoint KPI Bundle",
  // Story 20.6: Semrush presets (closes Epic 20 Semrush slice)
  "semrush-authority-score-gauge": "Authority Score Gauge",
  "semrush-backlinks-profile-overview": "Backlinks Profile Overview",
  "semrush-organic-traffic-cost-trend": "Organic Traffic & Cost Trend",
  "semrush-position-changes-distribution": "Position Changes Distribution",
  "semrush-top-organic-keywords-table": "Top Organic Keywords",
  "semrush-top-organic-competitors-table": "Top Organic Competitors",
  "semrush-ai-visibility-mixed": "AI Visibility — Mixed Surface",
  "semrush-cross-endpoint-kpi-bundle": "Cross-Endpoint KPI Bundle",
  // Story 20.8: Moz presets (closes Epic 20 Moz slice)
  "moz-domain-authority-gauge": "Domain Authority Gauge",
  "moz-page-authority-gauge": "Page Authority Gauge",
  "moz-brand-authority-gauge": "Brand Authority Gauge",
  "moz-backlink-profile-overview": "Backlink Profile Overview",
  "moz-top-pages-table": "Top Pages",
  "moz-top-linking-root-domains-table": "Top Linking Root Domains",
  "moz-da-trend": "Domain Authority Trend",
  "moz-cross-endpoint-scorecard": "Cross-Endpoint Scorecard",
  // Story 22.2 — CallRail presets (closes Epic 22 CallRail slice)
  "callrail-call-volume-kpi": "Call Volume KPI",
  "callrail-answer-rate-gauge": "Answer Rate Gauge",
  "callrail-calls-over-time": "Calls Over Time",
  "callrail-calls-by-source-bar": "Calls by Source",
  "callrail-calls-by-source-answer-rate-stacked": "Calls × Source × Answer Rate",
  "callrail-calls-list-table": "Calls List",
  "callrail-conversation-intelligence-sentiment-pie": "Sentiment Distribution",
  "callrail-cross-endpoint-scorecard": "Cross-Endpoint Scorecard",
  // Story 22.4 — CTM presets (closes Epic 22 in full). The "CTM " prefix
  // disambiguates from CallRail labels when both adapters are connected
  // (Round 1 UX persona invariant — Summit Legal renders both side-by-side).
  "ctm-call-volume-kpi": "CTM Call Volume KPI",
  "ctm-answer-rate-gauge": "CTM Answer Rate Gauge",
  "ctm-calls-over-time": "CTM Calls Over Time",
  "ctm-calls-by-source-bar": "CTM Calls by Source",
  "ctm-calls-by-source-answer-rate-stacked": "CTM Calls × Source × Answer Rate",
  "ctm-calls-list-table": "CTM Calls List",
  "ctm-conversation-intelligence-likelihood-quality-pie": "CTM AI Likelihood Quality",
  "ctm-cross-endpoint-scorecard": "CTM Cross-Endpoint Scorecard",
  "ctm-omnichannel-touch-funnel": "CTM Omnichannel Touch Funnel",
  // Story 23.2 — Google Reviews presets (closes Epic 23 Google Reviews slice)
  "google-reviews-overview-kpi": "Reviews Overview",
  "google-reviews-rating-distribution-bar": "Rating Distribution",
  "google-reviews-sentiment-pie": "Sentiment Distribution",
  "google-reviews-reply-rate-gauge": "Reply Rate Gauge",
  "google-reviews-reviews-over-time": "Reviews Over Time",
  "google-reviews-reviews-list-table": "Reviews List",
  "google-reviews-reply-moderation-state-pie": "Reply Moderation State",
  "google-reviews-reputation-scorecard": "Reputation Scorecard",
  // Story 23.4 — Trustpilot presets (closes Epic 23 Trustpilot slice).
  // Round 2 review fix — every label prefixed with "Trustpilot" to
  // disambiguate from same-named Google Reviews entries in the SAME
  // PRESET_LABELS Record.
  "trustpilot-overview-kpi": "Trustpilot Overview",
  "trustpilot-rating-distribution-bar": "Trustpilot Rating Distribution",
  "trustpilot-sentiment-pie": "Trustpilot Sentiment Distribution",
  "trustpilot-reply-rate-gauge": "Trustpilot Reply Rate Gauge",
  "trustpilot-reviews-over-time": "Trustpilot Reviews Over Time",
  "trustpilot-reviews-list-table": "Trustpilot Reviews List",
  "trustpilot-content-integrity-flag-pie": "Trustpilot Content Integrity Flags",
  "trustpilot-reputation-scorecard": "Trustpilot Reputation Scorecard",
  // Story 23.6 — Yelp presets (closes Epic 23 Yelp slice; closes Epic 23 in full).
  // Build-to-sell category-defining widget = preset #5
  // (`yelp-rating-trend-over-time` — exploits Story 23.5's D1
  // `yelp_rating_snapshots` workaround which makes a Rating × time
  // line chart possible despite Yelp Fusion having no historical API.
  // No competitor reporting tool surfaces this widget per
  // agencyanalytics.com/integrations/yelp + trustindex.io/yelp-reviews-widget
  // + databox + whatagraph + dashthis + swydo May 2026 verified 2026-05-18).
  // Every label prefixed with "Yelp" to disambiguate from same-named
  // Google Reviews + Trustpilot entries in the SAME PRESET_LABELS Record.
  "yelp-overview-kpi": "Yelp Overview",
  "yelp-rating-distribution-bar": "Yelp Rating Distribution",
  "yelp-sentiment-pie": "Yelp Sentiment Distribution",
  "yelp-review-count-growth-line": "Yelp Review Count Growth",
  "yelp-rating-trend-over-time": "Yelp Rating Trend",
  "yelp-tier-coverage-annotation": "Yelp Tier Coverage",
  "yelp-review-excerpts-list-table": "Yelp Review Excerpts",
  "yelp-reputation-scorecard": "Yelp Reputation Scorecard",
  // Story 20.9 — SEO Cross-Tool presets (Epic 20 cherry-on-top)
  "seo-cross-tool-authority-scoreboard": "SEO Authority Scoreboard",
  "seo-cross-tool-authority-bar-comparison": "SEO Authority Bar Comparison",
  "seo-cross-tool-authority-trend": "SEO Authority 6-Month Trend",
  // Story 23.7 — Reviews Cross-Platform presets (Epic 23 cherry-on-top).
  // Labels prefixed "Cross-Platform " to disambiguate from per-platform
  // reviews entries in the same PRESET_LABELS Record.
  "reviews-cross-platform-unified-scoreboard": "Cross-Platform Reputation Scoreboard",
  "reviews-cross-platform-rating-bar-comparison": "Cross-Platform Rating Bar Comparison",
  "reviews-cross-platform-review-velocity-trend": "Cross-Platform Review Velocity Trend",
  "reviews-cross-platform-sentiment-stack": "Cross-Platform Sentiment Stack",
  // Story 23.8 — Reviews Cross-Platform Response Tracking presets (closes
  // Epic 23). 2-platform scope — Yelp excluded (no reply API surface).
  "reviews-cross-platform-reply-rate-scoreboard": "Cross-Platform Reply Rate Scoreboard",
  "reviews-cross-platform-avg-response-time-bar": "Cross-Platform Avg Response Time Bar",
  "reviews-cross-platform-reply-rate-trend": "Cross-Platform Reply Rate Trend (6mo)",
  "reviews-cross-platform-awaiting-response-queue": "Cross-Platform Awaiting Response Queue",
  "linkedin-pages-overview-kpi": "LinkedIn Pages Overview",
  "linkedin-pages-demographics": "Follower Demographics",
  // IMC-18: Mailchimp expanded presets
  "mailchimp-revenue": "E-commerce Revenue",
  "mailchimp-benchmark": "Industry Benchmarks",
  "mailchimp-click-map": "URL Click Map",
  "mailchimp-automation": "Automation Performance",
  // Story 12.8: Klaviyo-namespaced presets
  "klaviyo-overview-kpi": "Klaviyo Overview",
  "klaviyo-campaign-table": "Klaviyo Campaign Performance",
  "klaviyo-top-campaigns": "Klaviyo Top Campaigns",
  "klaviyo-flow-performance": "Klaviyo Flow Performance",
  "klaviyo-form-conversion": "Klaviyo Form Conversion",
  "klaviyo-segment-growth": "Klaviyo Segment Growth",
  // Story 25.4: HubSpot CRM presets
  "hubspot-crm-pipeline-overview-kpi": "Pipeline Overview",
  "hubspot-crm-deal-funnel": "Deal Stage Funnel",
  "hubspot-crm-lifecycle-funnel": "Lifecycle Funnel",
  "hubspot-crm-lead-source": "Lead Source Breakdown",
  "hubspot-crm-deals-by-owner": "Deals by Owner",
  "hubspot-crm-revenue-trend": "Revenue Trend",
  "hubspot-crm-deals-table": "Deals by Stage",
  // Story 25.5: Attribution presets
  "hubspot-crm-lead-source-detail": "Lead Source Detail",
  "hubspot-crm-first-vs-last-touch": "First vs Last Touch",
  "hubspot-crm-attribution-trend": "Attribution Trend",
  // Story 25.6: HubSpot email presets
  "hubspot-email-overview": "Email Overview",
  "hubspot-email-campaign-table": "Email Campaigns",
  "hubspot-email-device-breakdown": "Email Device Breakdown",
  "hubspot-email-funnel": "Email Engagement Funnel",
  // Story 25.7: HubSpot traffic analytics presets
  "hubspot-traffic-overview": "HubSpot Traffic Overview",
  "hubspot-traffic-sources": "HubSpot Traffic Sources",
  "hubspot-traffic-trend": "HubSpot Traffic Trend",
  // Story 25.8: HubSpot marketing campaign presets
  "hubspot-campaign-roi": "HubSpot Campaign ROI",
  "hubspot-campaign-attribution": "HubSpot Campaign Attribution",
  // Story 25.9: HubSpot companies/tickets/engagements presets
  "hubspot-company-overview": "HubSpot Company Overview",
  "hubspot-ticket-summary": "HubSpot Ticket Summary",
  "hubspot-sales-activity": "HubSpot Sales Activity",
  // Story 25.10: HubSpot forms + marketing events presets
  "hubspot-form-performance": "HubSpot Form Performance",
  "hubspot-event-attendance": "HubSpot Event Attendance",
  // Story 25.11: HubSpot workflows + deal forecast + geography presets
  "hubspot-workflow-performance": "HubSpot Workflow Performance",
  "hubspot-contact-geography": "HubSpot Contact Geography",
  "hubspot-deal-forecast": "HubSpot Deal Forecast",
  // IMC-20: GBP presets
  "gbp-review-breakdown": "Review Breakdown",
  "gbp-search-keywords": "Search Keywords",
};

/**
 * Dispatcher: expand any preset type into its widget configs.
 */
export function expandPreset(presetType: PresetType, platform: string, integrationId: string): PresetWidget[] {
  switch (presetType) {
    case "email-overview-kpi":
      return expandEmailOverviewPreset(platform, integrationId);
    case "email-campaign-table":
      return expandCampaignTablePreset(platform, integrationId);
    case "email-subscriber-growth":
      return expandSubscriberGrowthPreset(platform, integrationId);
    case "email-top-campaigns":
      return expandTopCampaignsPreset(platform, integrationId);
    case "ecommerce-sales-overview-kpi":
      return expandEcommerceSalesOverviewPreset(platform, integrationId);
    case "ecommerce-revenue-trend":
      return expandEcommerceRevenueTrendPreset(platform, integrationId);
    case "ecommerce-top-products":
      return expandEcommerceTopProductsPreset(platform, integrationId);
    case "ecommerce-orders-conversion":
      return expandEcommerceOrdersConversionPreset(platform, integrationId);
    case "ecommerce-product-performance":
      return expandProductPerformancePreset(platform, integrationId);
    case "ecommerce-profit":
      return expandShopifyProfitPreset(platform, integrationId);
    case "ecommerce-customer":
      return expandShopifyCustomerPreset(platform, integrationId);
    case "ecommerce-cart-abandonment":
      return expandShopifyCartAbandonmentPreset(platform, integrationId);
    case "ecommerce-geo":
      return expandShopifyGeoPreset(platform, integrationId);
    case "instagram-overview-kpi":
      return expandInstagramOverviewPreset(platform, integrationId);
    case "instagram-reels-table":
      return expandInstagramReelsPreset(platform, integrationId);
    case "instagram-demographics":
      return expandInstagramDemographicsPreset(platform, integrationId);
    case "facebook-pages-overview-kpi":
      return expandFacebookPagesOverviewPreset(platform, integrationId);
    case "facebook-pages-video":
      return expandFacebookPagesVideoPreset(platform, integrationId);
    case "facebook-pages-reactions":
      return expandFacebookPagesReactionsPreset(platform, integrationId);
    case "tiktok-ads-overview-kpi":
      return expandTikTokAdsOverviewPreset(platform, integrationId);
    case "tiktok-ads-video-funnel":
      return expandTikTokAdsVideoFunnelPreset(platform, integrationId);
    case "tiktok-ads-campaign-table":
      return expandTikTokAdsCampaignTablePreset(platform, integrationId);
    case "tiktok-organic-overview-kpi":
      return expandTikTokOrganicOverviewPreset(platform, integrationId);
    case "tiktok-organic-video-table":
      return expandTikTokVideoTablePreset(platform, integrationId);
    case "google-ads-overview-kpi":
      return expandGoogleAdsOverviewPreset(platform, integrationId);
    case "google-ads-impression-share":
      return expandGoogleAdsImpressionSharePreset(platform, integrationId);
    case "google-ads-keyword-table":
      return expandGoogleAdsKeywordTablePreset(platform, integrationId);
    case "google-ads-video-funnel":
      return expandGoogleAdsVideoFunnelPreset(platform, integrationId);
    case "google-ads-shopping-table":
      return expandGoogleAdsShoppingTablePreset(platform, integrationId);
    case "pagespeed-scorecard-kpi":
      return expandPageSpeedScorecardPreset(platform, integrationId);
    case "pagespeed-core-web-vitals":
      return expandCoreWebVitalsPreset(platform, integrationId);
    case "pagespeed-crux-history":
      return expandCruxHistoryPreset(platform, integrationId);
    case "ga4-traffic-overview-kpi":
      return expandGA4TrafficOverviewPreset(platform, integrationId);
    case "ga4-ecommerce-funnel":
      return expandGA4EcommerceFunnelPreset(platform, integrationId);
    case "ga4-revenue":
      return expandGA4RevenuePreset(platform, integrationId);
    case "ga4-acquisition":
      return expandGA4AcquisitionPreset(platform, integrationId);
    case "youtube-overview-kpi":
      return expandYouTubeOverviewPreset(platform, integrationId);
    case "youtube-video-table":
      return expandYouTubeVideoTablePreset(platform, integrationId);
    case "youtube-audience":
      return expandYouTubeAudiencePreset(platform, integrationId);
    case "linkedin-ads-overview-kpi":
      return expandLinkedInAdsOverviewPreset(platform, integrationId);
    case "linkedin-ads-video-funnel":
      return expandLinkedInAdsVideoPreset(platform, integrationId);
    case "linkedin-ads-demographics":
      return expandLinkedInAdsDemographicsPreset(platform, integrationId);
    case "linkedin-ads-lead-gen":
      return expandLinkedInAdsLeadGenPreset(platform, integrationId);
    case "meta-ads-overview-kpi":
      return expandMetaAdsOverviewPreset(platform, integrationId);
    case "meta-ads-video-funnel":
      return expandMetaAdsVideoFunnelPreset(platform, integrationId);
    case "meta-ads-conversion-table":
      return expandMetaAdsConversionTablePreset(platform, integrationId);
    case "meta-ads-quality-table":
      return expandMetaAdsQualityTablePreset(platform, integrationId);
    // Story 19.3: Microsoft Ads presets
    case "microsoft-ads-overview-kpi":
      return expandMicrosoftAdsOverviewPreset(platform, integrationId);
    case "microsoft-ads-impression-share":
      return expandMicrosoftAdsImpressionSharePreset(platform, integrationId);
    case "microsoft-ads-keyword-quality-table":
      return expandMicrosoftAdsKeywordQualityPreset(platform, integrationId);
    case "microsoft-ads-campaign-performance":
      return expandMicrosoftAdsCampaignPerformancePreset(platform, integrationId);
    case "microsoft-ads-pmax-final-url-table":
      return expandMicrosoftAdsPmaxFinalUrlPreset(platform, integrationId);
    // Story 19.6: Pinterest Ads presets
    case "pinterest-ads-overview-kpi":
      return expandPinterestAdsOverviewPreset(platform, integrationId);
    case "pinterest-ads-paid-vs-earned":
      return expandPinterestAdsPaidVsEarnedPreset(platform, integrationId);
    case "pinterest-ads-saves-performance":
      return expandPinterestAdsSavesPerformancePreset(platform, integrationId);
    case "pinterest-ads-video-funnel":
      return expandPinterestAdsVideoFunnelPreset(platform, integrationId);
    case "pinterest-ads-conversion-roas-table":
      return expandPinterestAdsConversionRoasTablePreset(platform, integrationId);
    case "pinterest-ads-cross-device-attribution-table":
      return expandPinterestAdsCrossDeviceAttributionTablePreset(platform, integrationId);
    // Story 19.9: Snapchat Ads presets
    case "snapchat-ads-overview-kpi":
      return expandSnapchatAdsOverviewPreset(platform, integrationId);
    case "snapchat-ads-swipe-up-funnel":
      return expandSnapchatAdsSwipeUpFunnelPreset(platform, integrationId);
    case "snapchat-ads-video-quartile-funnel":
      return expandSnapchatAdsVideoQuartileFunnelPreset(platform, integrationId);
    case "snapchat-ads-app-vs-web-conversions":
      return expandSnapchatAdsAppVsWebConversionsPreset(platform, integrationId);
    case "snapchat-ads-demographic-breakdown":
      return expandSnapchatAdsDemographicBreakdownPreset(platform, integrationId);
    case "snapchat-ads-conversion-roas-table":
      return expandSnapchatAdsConversionRoasTablePreset(platform, integrationId);
    // Story 19.12: Reddit Ads presets
    case "reddit-ads-overview-kpi":
      return expandRedditAdsOverviewPreset(platform, integrationId);
    case "reddit-ads-subreddit-performance":
      return expandRedditAdsSubredditPerformancePreset(platform, integrationId);
    case "reddit-ads-conversion-attribution-funnel":
      return expandRedditAdsConversionAttributionFunnelPreset(platform, integrationId);
    case "reddit-ads-video-completion-funnel":
      return expandRedditAdsVideoCompletionFunnelPreset(platform, integrationId);
    case "reddit-ads-geo-country-region":
      return expandRedditAdsGeoCountryRegionPreset(platform, integrationId);
    case "reddit-ads-click-vs-view-attribution":
      return expandRedditAdsClickVsViewAttributionPreset(platform, integrationId);
    // Story 19.17: Amazon Ads presets (closes Epic 19)
    case "amazon-ads-overview-kpi":
      return expandAmazonAdsOverviewPreset(platform, integrationId);
    case "amazon-ads-advertised-product-performance":
      return expandAmazonAdsAdvertisedProductPerformancePreset(platform, integrationId);
    case "amazon-ads-sb-ntb-funnel":
      return expandAmazonAdsSbNtbFunnelPreset(platform, integrationId);
    case "amazon-ads-sd-click-vs-view":
      return expandAmazonAdsSdClickVsViewPreset(platform, integrationId);
    case "amazon-ads-dsp-audience-performance":
      return expandAmazonAdsDspAudiencePerformancePreset(platform, integrationId);
    case "amazon-ads-dsp-brand-store-engagement-funnel":
      return expandAmazonAdsDspBrandStoreEngagementFunnelPreset(platform, integrationId);
    case "amazon-ads-acos-roas-trend":
      return expandAmazonAdsAcosRoasTrendPreset(platform, integrationId);
    case "amazon-ads-cross-surface-comparison":
      return expandAmazonAdsCrossSurfaceComparisonPreset(platform, integrationId);
    // Story 20.3: Ahrefs presets (closes Epic 20 Ahrefs slice)
    case "ahrefs-domain-rating-gauge":
      return expandAhrefsDomainRatingGaugePreset(platform, integrationId);
    case "ahrefs-backlink-profile-overview":
      return expandAhrefsBacklinkProfileOverviewPreset(platform, integrationId);
    case "ahrefs-organic-traffic-value-trend":
      return expandAhrefsOrganicTrafficValueTrendPreset(platform, integrationId);
    case "ahrefs-keyword-position-distribution":
      return expandAhrefsKeywordPositionDistributionPreset(platform, integrationId);
    case "ahrefs-site-audit-health-score":
      return expandAhrefsSiteAuditHealthScorePreset(platform, integrationId);
    case "ahrefs-ai-visibility-sov":
      return expandAhrefsAiVisibilitySovPreset(platform, integrationId);
    case "ahrefs-web-analytics-overview":
      return expandAhrefsWebAnalyticsOverviewPreset(platform, integrationId);
    case "ahrefs-cross-endpoint-kpi-bundle":
      return expandAhrefsCrossEndpointKpiBundlePreset(platform, integrationId);
    // Story 20.6: Semrush presets (closes Epic 20 Semrush slice)
    case "semrush-authority-score-gauge":
      return expandSemrushAuthorityScoreGaugePreset(platform, integrationId);
    case "semrush-backlinks-profile-overview":
      return expandSemrushBacklinksProfileOverviewPreset(platform, integrationId);
    case "semrush-organic-traffic-cost-trend":
      return expandSemrushOrganicTrafficCostTrendPreset(platform, integrationId);
    case "semrush-position-changes-distribution":
      return expandSemrushPositionChangesDistributionPreset(platform, integrationId);
    case "semrush-top-organic-keywords-table":
      return expandSemrushTopOrganicKeywordsTablePreset(platform, integrationId);
    case "semrush-top-organic-competitors-table":
      return expandSemrushTopOrganicCompetitorsTablePreset(platform, integrationId);
    case "semrush-ai-visibility-mixed":
      return expandSemrushAiVisibilityMixedPreset(platform, integrationId);
    case "semrush-cross-endpoint-kpi-bundle":
      return expandSemrushCrossEndpointKpiBundlePreset(platform, integrationId);
    // Story 20.8: Moz presets (closes Epic 20 Moz slice)
    case "moz-domain-authority-gauge":
      return expandMozDomainAuthorityGaugePreset(platform, integrationId);
    case "moz-page-authority-gauge":
      return expandMozPageAuthorityGaugePreset(platform, integrationId);
    case "moz-brand-authority-gauge":
      return expandMozBrandAuthorityGaugePreset(platform, integrationId);
    case "moz-backlink-profile-overview":
      return expandMozBacklinkProfileOverviewPreset(platform, integrationId);
    case "moz-top-pages-table":
      return expandMozTopPagesTablePreset(platform, integrationId);
    case "moz-top-linking-root-domains-table":
      return expandMozTopLinkingRootDomainsTablePreset(platform, integrationId);
    case "moz-da-trend":
      return expandMozDaTrendPreset(platform, integrationId);
    case "moz-cross-endpoint-scorecard":
      return expandMozCrossEndpointScorecardPreset(platform, integrationId);
    // Story 22.2 — CallRail presets (closes Epic 22 CallRail slice)
    case "callrail-call-volume-kpi":
      return expandCallRailCallVolumeKpiPreset(platform, integrationId);
    case "callrail-answer-rate-gauge":
      return expandCallRailAnswerRateGaugePreset(platform, integrationId);
    case "callrail-calls-over-time":
      return expandCallRailCallsOverTimePreset(platform, integrationId);
    case "callrail-calls-by-source-bar":
      return expandCallRailCallsBySourceBarPreset(platform, integrationId);
    case "callrail-calls-by-source-answer-rate-stacked":
      return expandCallRailCallsBySourceAnswerRateStackedPreset(
        platform,
        integrationId,
      );
    case "callrail-calls-list-table":
      return expandCallRailCallsListTablePreset(platform, integrationId);
    case "callrail-conversation-intelligence-sentiment-pie":
      return expandCallRailConversationIntelligenceSentimentPiePreset(
        platform,
        integrationId,
      );
    case "callrail-cross-endpoint-scorecard":
      return expandCallRailCrossEndpointScorecardPreset(
        platform,
        integrationId,
      );
    // Story 22.4 — CTM presets dispatch (closes Epic 22 in full)
    case "ctm-call-volume-kpi":
      return expandCtmCallVolumeKpiPreset(platform, integrationId);
    case "ctm-answer-rate-gauge":
      return expandCtmAnswerRateGaugePreset(platform, integrationId);
    case "ctm-calls-over-time":
      return expandCtmCallsOverTimePreset(platform, integrationId);
    case "ctm-calls-by-source-bar":
      return expandCtmCallsBySourceBarPreset(platform, integrationId);
    case "ctm-calls-by-source-answer-rate-stacked":
      return expandCtmCallsBySourceAnswerRateStackedPreset(
        platform,
        integrationId,
      );
    case "ctm-calls-list-table":
      return expandCtmCallsListTablePreset(platform, integrationId);
    case "ctm-conversation-intelligence-likelihood-quality-pie":
      return expandCtmConversationIntelligenceLikelihoodQualityPiePreset(
        platform,
        integrationId,
      );
    case "ctm-cross-endpoint-scorecard":
      return expandCtmCrossEndpointScorecardPreset(platform, integrationId);
    case "ctm-omnichannel-touch-funnel":
      return expandCtmOmnichannelTouchFunnelPreset(platform, integrationId);
    // Story 23.2 — Google Reviews dispatch (closes Epic 23 GR slice)
    case "google-reviews-overview-kpi":
      return expandGoogleReviewsOverviewKpiPreset(platform, integrationId);
    case "google-reviews-rating-distribution-bar":
      return expandGoogleReviewsRatingDistributionBarPreset(platform, integrationId);
    case "google-reviews-sentiment-pie":
      return expandGoogleReviewsSentimentPiePreset(platform, integrationId);
    case "google-reviews-reply-rate-gauge":
      return expandGoogleReviewsReplyRateGaugePreset(platform, integrationId);
    case "google-reviews-reviews-over-time":
      return expandGoogleReviewsReviewsOverTimePreset(platform, integrationId);
    case "google-reviews-reviews-list-table":
      return expandGoogleReviewsReviewsListTablePreset(platform, integrationId);
    case "google-reviews-reply-moderation-state-pie":
      return expandGoogleReviewsReplyModerationStatePiePreset(platform, integrationId);
    case "google-reviews-reputation-scorecard":
      return expandGoogleReviewsReputationScorecardPreset(platform, integrationId);
    // Story 23.4 — Trustpilot preset case arms (closes Epic 23 Trustpilot slice)
    case "trustpilot-overview-kpi":
      return expandTrustpilotOverviewKpiPreset(platform, integrationId);
    case "trustpilot-rating-distribution-bar":
      return expandTrustpilotRatingDistributionBarPreset(platform, integrationId);
    case "trustpilot-sentiment-pie":
      return expandTrustpilotSentimentPiePreset(platform, integrationId);
    case "trustpilot-reply-rate-gauge":
      return expandTrustpilotReplyRateGaugePreset(platform, integrationId);
    case "trustpilot-reviews-over-time":
      return expandTrustpilotReviewsOverTimePreset(platform, integrationId);
    case "trustpilot-reviews-list-table":
      return expandTrustpilotReviewsListTablePreset(platform, integrationId);
    case "trustpilot-content-integrity-flag-pie":
      return expandTrustpilotContentIntegrityFlagPiePreset(platform, integrationId);
    case "trustpilot-reputation-scorecard":
      return expandTrustpilotReputationScorecardPreset(platform, integrationId);
    // Story 23.6 — Yelp preset case arms (closes Epic 23 Yelp slice + closes Epic 23 in full)
    case "yelp-overview-kpi":
      return expandYelpOverviewKpiPreset(platform, integrationId);
    case "yelp-rating-distribution-bar":
      return expandYelpRatingDistributionBarPreset(platform, integrationId);
    case "yelp-sentiment-pie":
      return expandYelpSentimentPiePreset(platform, integrationId);
    case "yelp-review-count-growth-line":
      return expandYelpReviewCountGrowthLinePreset(platform, integrationId);
    case "yelp-rating-trend-over-time":
      return expandYelpRatingTrendOverTimePreset(platform, integrationId);
    case "yelp-tier-coverage-annotation":
      return expandYelpTierCoverageAnnotationPreset(platform, integrationId);
    case "yelp-review-excerpts-list-table":
      return expandYelpReviewExcerptsListTablePreset(platform, integrationId);
    case "yelp-reputation-scorecard":
      return expandYelpReputationScorecardPreset(platform, integrationId);
    // Story 20.9 — SEO Cross-Tool dispatch. `integrationId` is treated as
    // the Ahrefs integration ID; Semrush + Moz integrationIds are resolved
    // by the renderer via the `resolve:<platform>` sentinel pattern (see
    // expandSeoCrossToolAuthorityScoreboardPreset docstring at the top of
    // the cross-tool section).
    case "seo-cross-tool-authority-scoreboard":
      return expandSeoCrossToolAuthorityScoreboardPreset(platform, integrationId);
    case "seo-cross-tool-authority-bar-comparison":
      return expandSeoCrossToolAuthorityBarComparisonPreset(platform, integrationId);
    case "seo-cross-tool-authority-trend":
      return expandSeoCrossToolAuthorityTrendPreset(platform, integrationId);
    // Story 23.7 — Reviews Cross-Platform dispatch. `integrationId` is
    // treated as the Google Reviews integration ID; Trustpilot + Yelp
    // integrationIds are resolved by the renderer via the
    // `resolve:<platform>` sentinel pattern.
    case "reviews-cross-platform-unified-scoreboard":
      return expandReviewsCrossPlatformUnifiedScoreboardPreset(platform, integrationId);
    case "reviews-cross-platform-rating-bar-comparison":
      return expandReviewsCrossPlatformRatingBarComparisonPreset(platform, integrationId);
    case "reviews-cross-platform-review-velocity-trend":
      return expandReviewsCrossPlatformReviewVelocityTrendPreset(platform, integrationId);
    case "reviews-cross-platform-sentiment-stack":
      return expandReviewsCrossPlatformSentimentStackPreset(platform, integrationId);
    // Story 23.8 — Reviews Cross-Platform Response Tracking presets (closes
    // Epic 23). 2-platform scope (GR + TP only); `integrationId` is treated
    // as the Google Reviews integration ID, same as the Story 23.7 presets.
    case "reviews-cross-platform-reply-rate-scoreboard":
      return expandReviewsCrossPlatformReplyRateScoreboardPreset(platform, integrationId);
    case "reviews-cross-platform-avg-response-time-bar":
      return expandReviewsCrossPlatformAvgResponseTimeBarPreset(platform, integrationId);
    case "reviews-cross-platform-reply-rate-trend":
      return expandReviewsCrossPlatformReplyRateTrendPreset(platform, integrationId);
    case "reviews-cross-platform-awaiting-response-queue":
      return expandReviewsCrossPlatformAwaitingResponseQueuePreset(platform, integrationId);
    case "linkedin-pages-overview-kpi":
      return expandLinkedInPagesOverviewPreset(platform, integrationId);
    case "linkedin-pages-demographics":
      return expandLinkedInPagesDemographicsPreset(platform, integrationId);
    // IMC-18: Mailchimp expanded presets
    case "mailchimp-revenue":
      return expandMailchimpRevenuePreset(platform, integrationId);
    case "mailchimp-benchmark":
      return expandMailchimpBenchmarkPreset(platform, integrationId);
    case "mailchimp-click-map":
      return expandMailchimpClickMapPreset(platform, integrationId);
    case "mailchimp-automation":
      return expandMailchimpAutomationPreset(platform, integrationId);
    // Story 12.8: Klaviyo-namespaced presets
    case "klaviyo-overview-kpi":
      return expandKlaviyoOverviewPreset(platform, integrationId);
    case "klaviyo-campaign-table":
      return expandKlaviyoCampaignTablePreset(platform, integrationId);
    case "klaviyo-top-campaigns":
      return expandKlaviyoTopCampaignsPreset(platform, integrationId);
    case "klaviyo-flow-performance":
      return expandKlaviyoFlowPerformancePreset(platform, integrationId);
    case "klaviyo-form-conversion":
      return expandKlaviyoFormConversionPreset(platform, integrationId);
    case "klaviyo-segment-growth":
      return expandKlaviyoSegmentGrowthPreset(platform, integrationId);
    // Story 25.4: HubSpot CRM presets
    case "hubspot-crm-pipeline-overview-kpi":
      return expandHubSpotPipelineOverviewPreset(platform, integrationId);
    case "hubspot-crm-deal-funnel":
      return expandHubSpotDealFunnelPreset(platform, integrationId);
    case "hubspot-crm-lifecycle-funnel":
      return expandHubSpotLifecycleFunnelPreset(platform, integrationId);
    case "hubspot-crm-lead-source":
      return expandHubSpotLeadSourcePreset(platform, integrationId);
    case "hubspot-crm-deals-by-owner":
      return expandHubSpotDealsByOwnerPreset(platform, integrationId);
    case "hubspot-crm-revenue-trend":
      return expandHubSpotRevenueTrendPreset(platform, integrationId);
    case "hubspot-crm-deals-table":
      return expandHubSpotDealsTablePreset(platform, integrationId);
    // Story 25.5: Attribution presets
    case "hubspot-crm-lead-source-detail":
      return expandHubSpotLeadSourceDetailPreset(platform, integrationId);
    case "hubspot-crm-first-vs-last-touch":
      return expandHubSpotFirstVsLastTouchPreset(platform, integrationId);
    case "hubspot-crm-attribution-trend":
      return expandHubSpotAttributionTrendPreset(platform, integrationId);
    // Story 25.6: HubSpot email presets
    case "hubspot-email-overview":
      return expandHubSpotEmailOverviewPreset(platform, integrationId);
    case "hubspot-email-campaign-table":
      return expandHubSpotEmailCampaignTablePreset(platform, integrationId);
    case "hubspot-email-device-breakdown":
      return expandHubSpotEmailDeviceBreakdownPreset(platform, integrationId);
    case "hubspot-email-funnel":
      return expandHubSpotEmailFunnelPreset(platform, integrationId);
    // Story 25.7: HubSpot traffic analytics presets
    case "hubspot-traffic-overview":
      return expandHubSpotTrafficOverviewPreset(platform, integrationId);
    case "hubspot-traffic-sources":
      return expandHubSpotTrafficSourcesPreset(platform, integrationId);
    case "hubspot-traffic-trend":
      return expandHubSpotTrafficTrendPreset(platform, integrationId);
    // Story 25.8: HubSpot marketing campaign presets
    case "hubspot-campaign-roi":
      return expandHubSpotCampaignRoiPreset(platform, integrationId);
    case "hubspot-campaign-attribution":
      return expandHubSpotCampaignAttributionPreset(platform, integrationId);
    // Story 25.9: HubSpot companies/tickets/engagements presets
    case "hubspot-company-overview":
      return expandHubSpotCompanyOverviewPreset(platform, integrationId);
    case "hubspot-ticket-summary":
      return expandHubSpotTicketSummaryPreset(platform, integrationId);
    case "hubspot-sales-activity":
      return expandHubSpotSalesActivityPreset(platform, integrationId);
    // Story 25.10: HubSpot forms + marketing events presets
    case "hubspot-form-performance":
      return expandHubSpotFormPerformancePreset(platform, integrationId);
    case "hubspot-event-attendance":
      return expandHubSpotEventAttendancePreset(platform, integrationId);
    // Story 25.11: HubSpot workflows + deal forecast + geography presets
    case "hubspot-workflow-performance":
      return expandHubSpotWorkflowPerformancePreset(platform, integrationId);
    case "hubspot-contact-geography":
      return expandHubSpotContactGeographyPreset(platform, integrationId);
    case "hubspot-deal-forecast":
      return expandHubSpotDealForecastPreset(platform, integrationId);
    // IMC-20: GBP presets
    case "gbp-review-breakdown":
      return expandGBPReviewBreakdownPreset(platform, integrationId);
    case "gbp-search-keywords":
      return expandGBPSearchKeywordsPreset(platform, integrationId);
    default: {
      const _exhaustive: never = presetType;
      throw new Error(`Unknown preset type: ${_exhaustive}`);
    }
  }
}
